/* =====================================================================
 * ExcelRenderer — 엑셀 파일 렌더링 및 편집 컴포넌트
 * ===================================================================== */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import styled, { css, keyframes } from 'styled-components';
import XLSX from 'xlsx-js-style';
import JSZip from 'jszip';
import {
  Bold,
  Italic,
  Underline,
  Merge,
  Plus,
  Minus,
  Undo2,
  Redo2,
  Download,
  FilePlus2,
  FileX2,
  Pencil,
  Copy,
  X,
  ChevronDown,
  Clipboard,
  Trash2,
  PaintBucket,
  Pipette,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Percent,
  DecimalsArrowLeft,
  DecimalsArrowRight,
} from 'lucide-react';
import { Tooltip } from '@auw/workspace-ui';
import { useChatStore } from '@auw/stores';

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

interface CellPosition {
  row: number;
  col: number;
}

interface CellRange {
  start: CellPosition;
  end: CellPosition;
}

interface ParsedCellStyle {
  backgroundColor?: string;
  color?: string;
  fontWeight?: string;
  fontStyle?: string;
  textDecoration?: string;
  fontSize?: string;
  fontFamily?: string;
  textAlign?: 'left' | 'center' | 'right';
  verticalAlign?: 'top' | 'middle' | 'bottom';
  borderTop?: string;
  borderBottom?: string;
  borderLeft?: string;
  borderRight?: string;
  /** 사용자가 명시적으로 테두리를 적용했는지 여부 (디폴트 테두리 제거용) */
  _hasExplicitBorder?: boolean;
  /** 숫자 서식: 'percent' = 퍼센트 형식, undefined = 일반 */
  _numberFormat?: 'percent';
  /** 소수점 이하 자릿수 (undefined = 원래 값 그대로) */
  _decimalPlaces?: number;
}

interface MergeInfo {
  /** 이 셀이 병합의 시작(좌상단) 셀인가? */
  isOrigin: boolean;
  /** 숨겨진 셀인가? (병합에 의해 가려짐) */
  hidden: boolean;
  colSpan: number;
  rowSpan: number;
}

interface SheetImage {
  /** base64 data URI */
  src: string;
  /** 셀 좌표 (from) */
  fromRow: number;
  fromCol: number;
  /** 셀 좌표 (to) — 없으면 1x1 */
  toRow?: number;
  toCol?: number;
  width?: number;
  height?: number;
}

/** 내부 클립보드 — 앱 내 복사/붙여넣기 시 이미지·스타일·병합·수식 보존 */
interface InternalClipboardCell {
  value: string;
  style?: ParsedCellStyle;
  image?: SheetImage;
  formula?: string;
  merge?: MergeInfo;
}
interface InternalClipboard {
  /** 복사 시점의 시스템 클립보드 텍스트 (외부 붙여넣기 감지용) */
  plainText: string;
  /** rows × cols 2D 배열 */
  cells: InternalClipboardCell[][];
  /** 복사 영역의 행/열 수 */
  rowCount: number;
  colCount: number;
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

/* ── HSV ↔ RGB 변환 유틸 ── */
function hsvToRgb(h: number, s: number, v: number): [number, number, number] {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let r1 = 0, g1 = 0, b1 = 0;
  if (h < 60) { r1 = c; g1 = x; }
  else if (h < 120) { r1 = x; g1 = c; }
  else if (h < 180) { g1 = c; b1 = x; }
  else if (h < 240) { g1 = x; b1 = c; }
  else if (h < 300) { r1 = x; b1 = c; }
  else { r1 = c; b1 = x; }
  return [Math.round((r1 + m) * 255), Math.round((g1 + m) * 255), Math.round((b1 + m) * 255)];
}

function rgbToHsv(r: number, g: number, b: number): [number, number, number] {
  const r1 = r / 255, g1 = g / 255, b1 = b / 255;
  const max = Math.max(r1, g1, b1), min = Math.min(r1, g1, b1);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === r1) h = ((g1 - b1) / d + 6) % 6 * 60;
    else if (max === g1) h = ((b1 - r1) / d + 2) * 60;
    else h = ((r1 - g1) / d + 4) * 60;
  }
  const s = max === 0 ? 0 : d / max;
  return [h, s, max];
}

function hexToRgb(hex: string): [number, number, number] {
  const c = hex.replace('#', '');
  return [parseInt(c.substring(0, 2), 16) || 0, parseInt(c.substring(2, 4), 16) || 0, parseInt(c.substring(4, 6), 16) || 0];
}

function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map(v => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0')).join('').toUpperCase();
}

/** 0-based 열 인덱스 → 엑셀 열 알파벳 (A, B, ..., Z, AA, ...) */
const colIndexToLetter = (idx: number): string => {
  let result = '';
  let n = idx;
  while (n >= 0) {
    result = String.fromCharCode((n % 26) + 65) + result;
    n = Math.floor(n / 26) - 1;
  }
  return result;
};

/* ── 수식 엔진 ── */

/** 열 문자(A, B, ..., AA, AB, ...) → 0-based 열 인덱스 */
const letterToColIndex = (letters: string): number => {
  let idx = 0;
  const upper = letters.toUpperCase();
  for (let i = 0; i < upper.length; i++) {
    idx = idx * 26 + (upper.charCodeAt(i) - 64);
  }
  return idx - 1;
};

/** "A1" → { row: 0, col: 0 } */
const parseCellRef = (ref: string): { row: number; col: number } | null => {
  const m = ref.match(/^([A-Za-z]+)(\d+)$/);
  if (!m) return null;
  return { row: parseInt(m[2], 10) - 1, col: letterToColIndex(m[1]) };
};

/** "A1:B3" → [{ row, col }, ...] 범위 내 모든 셀 좌표 */
const parseRange = (rangeStr: string): { row: number; col: number }[] | null => {
  const parts = rangeStr.split(':');
  if (parts.length !== 2) return null;
  const s = parseCellRef(parts[0].trim());
  const e = parseCellRef(parts[1].trim());
  if (!s || !e) return null;
  const cells: { row: number; col: number }[] = [];
  const minR = Math.min(s.row, e.row), maxR = Math.max(s.row, e.row);
  const minC = Math.min(s.col, e.col), maxC = Math.max(s.col, e.col);
  for (let r = minR; r <= maxR; r++) {
    for (let c = minC; c <= maxC; c++) {
      cells.push({ row: r, col: c });
    }
  }
  return cells;
};

/** 셀 값을 숫자로 가져오기 (숫자가 아니면 NaN) */
const numVal = (grid: string[][], row: number, col: number): number => {
  const v = grid[row]?.[col];
  if (v == null || v === '') return NaN;
  return Number(v);
};

/** 범위 또는 단일 셀의 숫자 배열 추출 */
const resolveNumbers = (token: string, grid: string[][]): number[] => {
  if (token.includes(':')) {
    const cells = parseRange(token);
    if (!cells) return [];
    return cells.map(c => numVal(grid, c.row, c.col)).filter(n => !isNaN(n));
  }
  const ref = parseCellRef(token);
  if (ref) {
    const n = numVal(grid, ref.row, ref.col);
    return isNaN(n) ? [] : [n];
  }
  const n = Number(token);
  return isNaN(n) ? [] : [n];
};

/** 괄호 안 인수 분리 (쉼표 기준, 중첩 괄호 고려) */
const splitArgs = (argsStr: string): string[] => {
  const result: string[] = [];
  let depth = 0;
  let current = '';
  for (const ch of argsStr) {
    if (ch === '(') depth++;
    else if (ch === ')') depth--;
    if (ch === ',' && depth === 0) {
      result.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  if (current.trim()) result.push(current.trim());
  return result;
};

/**
 * 수식 평가 (재귀적). = 접두사 없이 전달.
 * 지원 함수: SUM, AVERAGE, COUNT, COUNTA, MAX, MIN, IF, ROUND, ABS, INT, MOD, POWER, SQRT, LEN, UPPER, LOWER, TRIM, CONCATENATE, LEFT, RIGHT, MID
 * 지원 연산자: +, -, *, /, ^, >, <, >=, <=, =, <>, &
 */
const evaluateFormula = (formula: string, grid: string[][], visited: Set<string> = new Set()): string => {
  const expr = formula.trim();

  // 숫자 리터럴
  if (/^-?\d+(\.\d+)?$/.test(expr)) return expr;

  // 문자열 리터럴
  if (expr.startsWith('"') && expr.endsWith('"')) return expr.slice(1, -1);

  // 함수 호출 감지: NAME(...)
  const fnMatch = expr.match(/^([A-Za-z_]\w*)\((.*)$/);
  if (fnMatch) {
    const fnName = fnMatch[1].toUpperCase();
    // 인수 영역 파싱: 마지막 닫는 괄호 찾기
    let depth = 1;
    let i = 0;
    const inner = fnMatch[2];
    for (; i < inner.length; i++) {
      if (inner[i] === '(') depth++;
      else if (inner[i] === ')') { depth--; if (depth === 0) break; }
    }
    const argsStr = inner.slice(0, i);
    const remainder = inner.slice(i + 1).trim(); // 함수 뒤의 나머지 연산자

    let fnResult: string;
    const args = splitArgs(argsStr);

    switch (fnName) {
      case 'SUM': {
        let total = 0;
        for (const a of args) total += resolveNumbers(evaluateFormula(a, grid, visited), grid)
          .concat(isNaN(Number(evaluateFormula(a, grid, visited))) ? [] : [Number(evaluateFormula(a, grid, visited))])
          .length ? 0 : 0;
        // 더 정확한 구현
        let sum = 0;
        for (const a of args) {
          if (a.includes(':')) {
            resolveNumbers(a, grid).forEach(n => sum += n);
          } else {
            const ev = evaluateFormula(a, grid, visited);
            const n = Number(ev);
            if (!isNaN(n)) sum += n;
          }
        }
        fnResult = String(sum);
        break;
      }
      case 'AVERAGE': {
        const nums: number[] = [];
        for (const a of args) {
          if (a.includes(':')) {
            resolveNumbers(a, grid).forEach(n => nums.push(n));
          } else {
            const ev = evaluateFormula(a, grid, visited);
            const n = Number(ev);
            if (!isNaN(n)) nums.push(n);
          }
        }
        fnResult = nums.length ? String(nums.reduce((s, n) => s + n, 0) / nums.length) : '#DIV/0!';
        break;
      }
      case 'COUNT': {
        let cnt = 0;
        for (const a of args) {
          if (a.includes(':')) {
            cnt += resolveNumbers(a, grid).length;
          } else {
            const ev = evaluateFormula(a, grid, visited);
            if (!isNaN(Number(ev))) cnt++;
          }
        }
        fnResult = String(cnt);
        break;
      }
      case 'COUNTA': {
        let cnt = 0;
        for (const a of args) {
          if (a.includes(':')) {
            const cells = parseRange(a);
            if (cells) cnt += cells.filter(c => (grid[c.row]?.[c.col] ?? '') !== '').length;
          } else {
            const ev = evaluateFormula(a, grid, visited);
            if (ev !== '') cnt++;
          }
        }
        fnResult = String(cnt);
        break;
      }
      case 'MAX': {
        const nums: number[] = [];
        for (const a of args) {
          if (a.includes(':')) {
            resolveNumbers(a, grid).forEach(n => nums.push(n));
          } else {
            const ev = evaluateFormula(a, grid, visited);
            const n = Number(ev);
            if (!isNaN(n)) nums.push(n);
          }
        }
        fnResult = nums.length ? String(Math.max(...nums)) : '0';
        break;
      }
      case 'MIN': {
        const nums: number[] = [];
        for (const a of args) {
          if (a.includes(':')) {
            resolveNumbers(a, grid).forEach(n => nums.push(n));
          } else {
            const ev = evaluateFormula(a, grid, visited);
            const n = Number(ev);
            if (!isNaN(n)) nums.push(n);
          }
        }
        fnResult = nums.length ? String(Math.min(...nums)) : '0';
        break;
      }
      case 'IF': {
        if (args.length < 2) { fnResult = '#VALUE!'; break; }
        const cond = evaluateFormula(args[0], grid, visited);
        const isTrue = cond !== '0' && cond !== '' && cond.toUpperCase() !== 'FALSE';
        fnResult = isTrue
          ? evaluateFormula(args[1], grid, visited)
          : (args[2] ? evaluateFormula(args[2], grid, visited) : 'FALSE');
        break;
      }
      case 'ROUND': {
        const val = Number(evaluateFormula(args[0], grid, visited));
        const digits = args[1] ? Number(evaluateFormula(args[1], grid, visited)) : 0;
        fnResult = isNaN(val) ? '#VALUE!' : val.toFixed(digits);
        break;
      }
      case 'ABS': {
        const val = Number(evaluateFormula(args[0], grid, visited));
        fnResult = isNaN(val) ? '#VALUE!' : String(Math.abs(val));
        break;
      }
      case 'INT': {
        const val = Number(evaluateFormula(args[0], grid, visited));
        fnResult = isNaN(val) ? '#VALUE!' : String(Math.floor(val));
        break;
      }
      case 'MOD': {
        const a = Number(evaluateFormula(args[0], grid, visited));
        const b = Number(evaluateFormula(args[1], grid, visited));
        fnResult = (isNaN(a) || isNaN(b) || b === 0) ? '#VALUE!' : String(a % b);
        break;
      }
      case 'POWER': {
        const base = Number(evaluateFormula(args[0], grid, visited));
        const exp = Number(evaluateFormula(args[1], grid, visited));
        fnResult = (isNaN(base) || isNaN(exp)) ? '#VALUE!' : String(Math.pow(base, exp));
        break;
      }
      case 'SQRT': {
        const val = Number(evaluateFormula(args[0], grid, visited));
        fnResult = (isNaN(val) || val < 0) ? '#VALUE!' : String(Math.sqrt(val));
        break;
      }
      case 'LEN': {
        fnResult = String(evaluateFormula(args[0], grid, visited).length);
        break;
      }
      case 'UPPER': {
        fnResult = evaluateFormula(args[0], grid, visited).toUpperCase();
        break;
      }
      case 'LOWER': {
        fnResult = evaluateFormula(args[0], grid, visited).toLowerCase();
        break;
      }
      case 'TRIM': {
        fnResult = evaluateFormula(args[0], grid, visited).trim();
        break;
      }
      case 'CONCATENATE': {
        fnResult = args.map(a => evaluateFormula(a, grid, visited)).join('');
        break;
      }
      case 'LEFT': {
        const str = evaluateFormula(args[0], grid, visited);
        const cnt = args[1] ? Number(evaluateFormula(args[1], grid, visited)) : 1;
        fnResult = str.slice(0, cnt);
        break;
      }
      case 'RIGHT': {
        const str = evaluateFormula(args[0], grid, visited);
        const cnt = args[1] ? Number(evaluateFormula(args[1], grid, visited)) : 1;
        fnResult = str.slice(-cnt);
        break;
      }
      case 'MID': {
        const str = evaluateFormula(args[0], grid, visited);
        const start = Number(evaluateFormula(args[1], grid, visited));
        const len = Number(evaluateFormula(args[2], grid, visited));
        fnResult = str.slice(start - 1, start - 1 + len);
        break;
      }
      default:
        fnResult = '#NAME?';
    }

    // 함수 뒤 연산자 있으면 이어서 처리
    if (remainder) {
      return evaluateFormula(`${fnResult}${remainder}`, grid, visited);
    }
    return fnResult;
  }

  // 단일 셀 참조
  const cellRef = parseCellRef(expr);
  if (cellRef) {
    const key = `${cellRef.row}:${cellRef.col}`;
    if (visited.has(key)) return '#REF!'; // 순환 참조 방지
    const rawVal = grid[cellRef.row]?.[cellRef.col] ?? '';
    if (rawVal.startsWith('=')) {
      visited.add(key);
      return evaluateFormula(rawVal.slice(1), grid, visited);
    }
    return rawVal;
  }

  // 산술/비교 연산 처리 (괄호 포함)
  try {
    return evalExpression(expr, grid, visited);
  } catch {
    return '#ERROR!';
  }
};

/** 산술 표현식 파서 (연산자 우선순위: 괄호 > 단항 > ^ > *,/ > +,- > 비교 > & ) */
const evalExpression = (expr: string, grid: string[][], visited: Set<string>): string => {
  const tokens = tokenize(expr);
  const result = parseComparison(tokens, grid, visited);
  return String(result);
};

interface TokenStream {
  tokens: string[];
  pos: number;
}

/** 토큰 분리 */
const tokenize = (expr: string): TokenStream => {
  const tokens: string[] = [];
  let i = 0;
  const s = expr.trim();
  while (i < s.length) {
    // 공백 건너뛰기
    if (s[i] === ' ') { i++; continue; }
    // 문자열 리터럴
    if (s[i] === '"') {
      let j = i + 1;
      while (j < s.length && s[j] !== '"') j++;
      tokens.push(s.slice(i, j + 1));
      i = j + 1;
      continue;
    }
    // 연산자
    if ('<>=!'.includes(s[i])) {
      if (i + 1 < s.length && s[i + 1] === '>' && s[i] === '<') { tokens.push('<>'); i += 2; continue; }
      if (i + 1 < s.length && s[i + 1] === '=') { tokens.push(s.slice(i, i + 2)); i += 2; continue; }
      tokens.push(s[i]); i++; continue;
    }
    if ('+-*/^()&,'.includes(s[i])) { tokens.push(s[i]); i++; continue; }
    // 숫자 또는 셀참조/함수명/범위
    let j = i;
    while (j < s.length && !' +-*/^()&,<>=!'.includes(s[j]) && s[j] !== '"') j++;
    if (j > i) tokens.push(s.slice(i, j));
    i = j;
  }
  return { tokens, pos: 0 };
};

const peek = (ts: TokenStream): string | undefined => ts.tokens[ts.pos];
const advance = (ts: TokenStream): string => ts.tokens[ts.pos++];

/** & (문자열 연결) */
const parseComparison = (ts: TokenStream, grid: string[][], visited: Set<string>): string | number => {
  let left = parseConcat(ts, grid, visited);
  while (peek(ts) && ['<', '>', '<=', '>=', '=', '<>'].includes(peek(ts)!)) {
    const op = advance(ts);
    const right = parseConcat(ts, grid, visited);
    const l = Number(left), r = Number(right);
    const nl = isNaN(l) ? left : l;
    const nr = isNaN(r) ? right : r;
    switch (op) {
      case '<': left = nl < nr ? 'TRUE' : 'FALSE'; break;
      case '>': left = nl > nr ? 'TRUE' : 'FALSE'; break;
      case '<=': left = nl <= nr ? 'TRUE' : 'FALSE'; break;
      case '>=': left = nl >= nr ? 'TRUE' : 'FALSE'; break;
      case '=': left = nl === nr ? 'TRUE' : 'FALSE'; break;
      case '<>': left = nl !== nr ? 'TRUE' : 'FALSE'; break;
    }
  }
  return left;
};

const parseConcat = (ts: TokenStream, grid: string[][], visited: Set<string>): string | number => {
  let left = parseAddSub(ts, grid, visited);
  while (peek(ts) === '&') {
    advance(ts);
    const right = parseAddSub(ts, grid, visited);
    left = `${left}${right}`;
  }
  return left;
};

const parseAddSub = (ts: TokenStream, grid: string[][], visited: Set<string>): string | number => {
  let left = parseMulDiv(ts, grid, visited);
  while (peek(ts) === '+' || peek(ts) === '-') {
    const op = advance(ts);
    const right = parseMulDiv(ts, grid, visited);
    const l = Number(left), r = Number(right);
    if (isNaN(l) || isNaN(r)) return '#VALUE!';
    left = op === '+' ? l + r : l - r;
  }
  return left;
};

const parseMulDiv = (ts: TokenStream, grid: string[][], visited: Set<string>): string | number => {
  let left = parsePower(ts, grid, visited);
  while (peek(ts) === '*' || peek(ts) === '/') {
    const op = advance(ts);
    const right = parsePower(ts, grid, visited);
    const l = Number(left), r = Number(right);
    if (isNaN(l) || isNaN(r)) return '#VALUE!';
    if (op === '/' && r === 0) return '#DIV/0!';
    left = op === '*' ? l * r : l / r;
  }
  return left;
};

const parsePower = (ts: TokenStream, grid: string[][], visited: Set<string>): string | number => {
  let left = parseUnary(ts, grid, visited);
  while (peek(ts) === '^') {
    advance(ts);
    const right = parseUnary(ts, grid, visited);
    const l = Number(left), r = Number(right);
    if (isNaN(l) || isNaN(r)) return '#VALUE!';
    left = Math.pow(l, r);
  }
  return left;
};

const parseUnary = (ts: TokenStream, grid: string[][], visited: Set<string>): string | number => {
  if (peek(ts) === '-') {
    advance(ts);
    const val = parseAtom(ts, grid, visited);
    const n = Number(val);
    return isNaN(n) ? '#VALUE!' : -n;
  }
  if (peek(ts) === '+') {
    advance(ts);
    return parseAtom(ts, grid, visited);
  }
  return parseAtom(ts, grid, visited);
};

const parseAtom = (ts: TokenStream, grid: string[][], visited: Set<string>): string | number => {
  const t = peek(ts);
  if (!t) return 0;

  // 괄호
  if (t === '(') {
    advance(ts);
    const val = parseComparison(ts, grid, visited);
    if (peek(ts) === ')') advance(ts);
    return val;
  }

  // 문자열 리터럴
  if (t.startsWith('"') && t.endsWith('"')) {
    advance(ts);
    return t.slice(1, -1);
  }

  advance(ts);

  // 함수 호출: 토큰 다음이 "(" 이면 함수
  if (peek(ts) === '(' && /^[A-Za-z_]\w*$/.test(t)) {
    // 함수 호출을 evaluateFormula 로 위임
    // ( 부터 닫는 ) 까지 수집
    advance(ts); // skip (
    let depth = 1;
    let argStr = '';
    while (ts.pos < ts.tokens.length && depth > 0) {
      const tk = advance(ts);
      if (tk === '(') depth++;
      else if (tk === ')') { depth--; if (depth === 0) break; }
      if (depth > 0) argStr += tk;
    }
    return evaluateFormula(`${t}(${argStr})`, grid, visited);
  }

  // 숫자
  if (/^-?\d+(\.\d+)?$/.test(t)) return Number(t);

  // % 접미사
  if (/^-?\d+(\.\d+)?%$/.test(t)) return Number(t.replace('%', '')) / 100;

  // 셀 참조
  const ref = parseCellRef(t);
  if (ref) {
    const key = `${ref.row}:${ref.col}`;
    if (visited.has(key)) return '#REF!';
    const rawVal = grid[ref.row]?.[ref.col] ?? '';
    if (rawVal.startsWith('=')) {
      const newVisited = new Set(visited);
      newVisited.add(key);
      const result = evaluateFormula(rawVal.slice(1), grid, newVisited);
      return isNaN(Number(result)) ? result : Number(result);
    }
    const n = Number(rawVal);
    return isNaN(n) ? rawVal : n;
  }

  // TRUE / FALSE
  if (t.toUpperCase() === 'TRUE') return 'TRUE';
  if (t.toUpperCase() === 'FALSE') return 'FALSE';

  return t;
};

/** CellRange 정규화 (start가 항상 좌상단) */
const normalizeRange = (range: CellRange): CellRange => ({
  start: {
    row: Math.min(range.start.row, range.end.row),
    col: Math.min(range.start.col, range.end.col),
  },
  end: {
    row: Math.max(range.start.row, range.end.row),
    col: Math.max(range.start.col, range.end.col),
  },
});

/** 셀이 범위 안에 있는지 확인 */
const isCellInRange = (row: number, col: number, range: CellRange | null): boolean => {
  if (!range) return false;
  const n = normalizeRange(range);
  return row >= n.start.row && row <= n.end.row && col >= n.start.col && col <= n.end.col;
};

/* ── 스타일 변환 헬퍼 ── */

/** xlsx color 객체 → CSS 색상 문자열 */
const xlsxColorToCSS = (color: any): string | undefined => {
  if (!color) return undefined;
  if (color.rgb) {
    // ARGB → RGB (앞 2글자가 alpha인 경우 제거)
    let rgb = color.rgb;
    if (rgb.length === 8) rgb = rgb.slice(2);
    // "00" 접두어 제거 (e.g. "00E0F2FE" → "E0F2FE")
    if (rgb.length === 6 && rgb !== '000000') return `#${rgb}`;
    if (rgb === '000000') return '#000000';
    return `#${rgb}`;
  }
  if (color.theme !== undefined) {
    const themeColors: Record<number, string> = {
      0: '#000000', 1: '#FFFFFF', 2: '#44546A', 3: '#4472C4',
      4: '#ED7D31', 5: '#A5A5A5', 6: '#FFC000', 7: '#5B9BD5',
      8: '#70AD47', 9: '#264478',
    };
    return themeColors[color.theme] || undefined;
  }
  if (color.indexed !== undefined) {
    // Excel 색상 인덱스 표 (확장)
    const indexedColors: Record<number, string> = {
      0: '#000000', 1: '#FFFFFF', 2: '#FF0000', 3: '#00FF00',
      4: '#0000FF', 5: '#FFFF00', 6: '#FF00FF', 7: '#00FFFF',
      8: '#000000', 9: '#FFFFFF', 10: '#FF0000', 11: '#00FF00',
      12: '#0000FF', 13: '#FFFF00', 14: '#FF00FF', 15: '#00FFFF',
      16: '#800000', 17: '#008000', 18: '#000080', 19: '#808000',
      20: '#800080', 21: '#008080', 22: '#C0C0C0', 23: '#808080',
      64: '#000000',
    };
    return indexedColors[color.indexed] || undefined;
  }
  return undefined;
};

/** xlsx border side → CSS border 문자열 (XML 파싱 결과용) */
const xlsxBorderSideToCSS = (side: any): string | undefined => {
  if (!side) return undefined;
  const borderStyle = typeof side === 'object' ? side.style : side;
  if (!borderStyle) return undefined;
  const borderColor = typeof side === 'object' && side.color
    ? xlsxColorToCSS(side.color) || '#000000'
    : '#000000';
  const styleMap: Record<string, string> = {
    thin: '1px solid',
    medium: '2px solid',
    thick: '3px solid',
    dotted: '1px dotted',
    dashed: '1px dashed',
    double: '3px double',
    hair: '1px solid',
    mediumDashed: '2px dashed',
    dashDot: '1px dashed',
    mediumDashDot: '2px dashed',
    dashDotDot: '1px dashed',
    mediumDashDotDot: '2px dashed',
    slantDashDot: '1px dashed',
  };
  const cssStyle = styleMap[borderStyle] || '1px solid';
  return `${cssStyle} ${borderColor}`;
};

/* resolveFullCellStyle 삭제됨 — resolveStyleHybrid로 대체 */

/** cell.s가 fill 객체(xlsx-js-style 직접 파싱 결과)인 경우 폴백 */
const parseCellStyleDirect = (s: any): ParsedCellStyle => {
  const style: ParsedCellStyle = {};
  if (!s) return style;

  // 배경색 (cell.s가 fill 패턴 객체인 경우)
  if (s.patternType && s.patternType !== 'none' && s.patternType !== 'gray125') {
    const fg = xlsxColorToCSS(s.fgColor);
    if (fg && fg !== '#000000') {
      style.backgroundColor = fg;
    } else {
      const bg = xlsxColorToCSS(s.bgColor);
      if (bg && bg !== '#000000' && bg !== '#FFFFFF') {
        style.backgroundColor = bg;
      }
    }
  }

  // 전체 스타일 객체 형태 (font, fill, border 포함)
  if (s.fill && s.fill.fgColor) {
    const fg = xlsxColorToCSS(s.fill.fgColor);
    if (fg && fg !== '#000000') style.backgroundColor = fg;
  }
  if (s.font) {
    if (s.font.bold) style.fontWeight = 'bold';
    if (s.font.italic) style.fontStyle = 'italic';
    if (s.font.underline) style.textDecoration = 'underline';
    if (s.font.sz) style.fontSize = `${s.font.sz}pt`;
    if (s.font.name) style.fontFamily = s.font.name;
    if (s.font.color) {
      const c = xlsxColorToCSS(s.font.color);
      if (c) style.color = c;
    }
  }
  if (s.alignment) {
    const hMap: Record<string, 'left' | 'center' | 'right'> = {
      left: 'left', center: 'center', right: 'right',
    };
    if (s.alignment.horizontal) style.textAlign = hMap[s.alignment.horizontal] || 'left';
    const vMap: Record<string, 'top' | 'middle' | 'bottom'> = {
      top: 'top', center: 'middle', bottom: 'bottom',
    };
    if (s.alignment.vertical) style.verticalAlign = vMap[s.alignment.vertical] || 'bottom';
  }
  if (s.border) {
    style.borderTop = xlsxBorderSideToCSS(s.border.top);
    style.borderBottom = xlsxBorderSideToCSS(s.border.bottom);
    style.borderLeft = xlsxBorderSideToCSS(s.border.left);
    style.borderRight = xlsxBorderSideToCSS(s.border.right);
  }

  return style;
};

/**
 * xlsx ZIP에서 borders만 regex로 파싱 (DOMParser 네임스페이스 문제 회피)
 * wb.Styles.Borders가 비어있는 문제를 보완
 */
const parseBordersFromZip = async (
  arrayBuffer: ArrayBuffer,
): Promise<any[]> => {
  const borders: any[] = [];
  try {
    const zip = await JSZip.loadAsync(arrayBuffer);
    const stylesFile = zip.file('xl/styles.xml');
    if (!stylesFile) return borders;

    const xml = await stylesFile.async('text');

    // <borders ...>...</borders> 블록 추출
    const bordersMatch = xml.match(/<borders[^>]*>([\s\S]*?)<\/borders>/);
    if (!bordersMatch) return borders;
    const bordersXml = bordersMatch[1];

    // 개별 <border>...</border> 또는 <border .../> 추출
    // [^>]*? non-greedy: <border /> self-closing에서 /를 소비하지 않도록
    const borderRe = /<border\b[^>]*?(?:\/\s*>|>([\s\S]*?)<\/border\s*>)/g;
    let bm;
    while ((bm = borderRe.exec(bordersXml)) !== null) {
      const border: any = {};
      const content = bm[1] || bm[0]; // self-closing이면 전체 태그 사용

      for (const side of ['left', 'right', 'top', 'bottom'] as const) {
        // <left style="medium">...<color rgb="00000000" />...</left> 또는 <left style="medium" />
        const sideRe = new RegExp(
          `<${side}\\b([^>]*)(?:\\/>|>([\\s\\S]*?)<\\/${side}>)`,
        );
        const sm = content.match(sideRe);
        if (sm) {
          const attrs = sm[1] || '';
          const inner = sm[2] || '';

          // style 속성 추출
          const styleMatch = attrs.match(/style="([^"]+)"/);
          if (styleMatch) {
            const sideObj: any = { style: styleMatch[1] };

            // color 추출 (속성 또는 내부 요소)
            const colorMatch = inner.match(/<color\b[^>]*/);
            if (colorMatch) {
              sideObj.color = {};
              const rgbMatch = colorMatch[0].match(/rgb="([^"]+)"/);
              if (rgbMatch) sideObj.color.rgb = rgbMatch[1];
              const themeMatch = colorMatch[0].match(/theme="([^"]+)"/);
              if (themeMatch) sideObj.color.theme = parseInt(themeMatch[1], 10);
              const indexedMatch = colorMatch[0].match(/indexed="([^"]+)"/);
              if (indexedMatch) sideObj.color.indexed = parseInt(indexedMatch[1], 10);
            }

            border[side] = sideObj;
          }
        }
      }

      borders.push(border);
    }
  } catch (err) {
    console.warn('[ExcelRenderer] Border parsing from ZIP failed:', err);
  }

  return borders;
};

/**
 * xlsx ZIP에서 시트 파일 경로 목록을 가져옴 (workbook.xml.rels 기반)
 */
const getSheetFilePaths = async (zip: any): Promise<string[]> => {
  const paths: string[] = [];
  try {
    // workbook.xml에서 시트 → rId 매핑
    const wbFile = zip.file('xl/workbook.xml');
    if (!wbFile) return paths;
    const wbXml = await wbFile.async('text');

    // rId 순서대로 시트 수집
    const sheetRe = /<sheet\s[^>]*name="[^"]*"[^>]*r:id="(rId\d+)"[^>]*/g;
    const rIds: string[] = [];
    let sm;
    while ((sm = sheetRe.exec(wbXml)) !== null) {
      rIds.push(sm[1]);
    }
    // name 앞에 r:id가 올 수도 있음
    const sheetRe2 = /<sheet\s[^>]*r:id="(rId\d+)"[^>]*name="[^"]*"[^>]*/g;
    while ((sm = sheetRe2.exec(wbXml)) !== null) {
      if (!rIds.includes(sm[1])) rIds.push(sm[1]);
    }

    // workbook.xml.rels에서 rId → 파일 경로 매핑
    const relsFile = zip.file('xl/_rels/workbook.xml.rels');
    if (!relsFile) return paths;
    const relsXml = await relsFile.async('text');

    const relMap: Record<string, string> = {};
    const relRe = /<Relationship[^>]*Id="([^"]+)"[^>]*Target="([^"]+)"[^>]*/g;
    while ((sm = relRe.exec(relsXml)) !== null) {
      relMap[sm[1]] = sm[2];
    }

    for (const rId of rIds) {
      const target = relMap[rId];
      if (target) {
        // 절대 경로(/xl/...) 또는 상대 경로(worksheets/...) 처리
        const fullPath = target.startsWith('/') ? target.slice(1) : `xl/${target}`;
        paths.push(fullPath);
      }
    }
  } catch {
    // 폴백: sheet1.xml, sheet2.xml, ... 순서
  }
  return paths;
};

/**
 * xlsx ZIP에서 시트 XML을 파싱하여 셀별 스타일 인덱스 맵 반환
 */
const parseCellStyleIndices = async (
  arrayBuffer: ArrayBuffer,
  sheetIndex: number,
): Promise<Map<string, number>> => {
  const map = new Map<string, number>();
  try {
    const zip = await JSZip.loadAsync(arrayBuffer);

    // 정확한 시트 파일 경로 결정
    const sheetPaths = await getSheetFilePaths(zip);
    let sheetFilePath: string;
    if (sheetIndex < sheetPaths.length) {
      sheetFilePath = sheetPaths[sheetIndex];
    } else {
      sheetFilePath = `xl/worksheets/sheet${sheetIndex + 1}.xml`;
    }

    const sheetFile = zip.file(sheetFilePath);
    if (!sheetFile) return map;

    const xml = await sheetFile.async('text');
    // 정규식으로 <c r="A1" s="2" ...> 파싱 (성능 위해 DOM 파싱 대신)
    const cellRe = /<c\s[^>]*?r="([A-Z]+\d+)"[^>]*?s="(\d+)"[^>]*/g;
    let match;
    while ((match = cellRe.exec(xml)) !== null) {
      const cellRef = match[1];
      const styleIdx = parseInt(match[2], 10);
      const decoded = XLSX.utils.decode_cell(cellRef);
      map.set(`${decoded.r}:${decoded.c}`, styleIdx);
    }

    // s 속성이 r 속성 앞에 오는 경우도 처리
    const cellRe2 = /<c\s[^>]*?s="(\d+)"[^>]*?r="([A-Z]+\d+)"[^>]*/g;
    while ((match = cellRe2.exec(xml)) !== null) {
      const styleIdx = parseInt(match[1], 10);
      const cellRef = match[2];
      const decoded = XLSX.utils.decode_cell(cellRef);
      const key = `${decoded.r}:${decoded.c}`;
      if (!map.has(key)) {
        map.set(key, styleIdx);
      }
    }
  } catch (err) {
    console.warn('[ExcelRenderer] Cell style index parsing failed:', err);
  }
  return map;
};

/**
 * 스타일 인덱스 → ParsedCellStyle (하이브리드: wb.Styles + ZIP borders)
 * wb.Styles.Fonts, wb.Styles.Fills, wb.Styles.CellXf → 정확
 * wb.Styles.Borders → 비어있으므로 ZIP에서 파싱한 borders 사용
 */
const resolveStyleHybrid = (
  styleIdx: number,
  wbStyles: any,
  zipBorders: any[],
): ParsedCellStyle => {
  const result: ParsedCellStyle = {};
  const cellXfs: any[] = wbStyles?.CellXf || [];
  const fonts: any[] = wbStyles?.Fonts || [];
  const fills: any[] = wbStyles?.Fills || [];

  if (styleIdx < 0 || styleIdx >= cellXfs.length) return result;

  const xf = cellXfs[styleIdx];
  if (!xf) return result;

  // 1. 폰트 (wb.Styles.Fonts에서)
  const fontId = xf.fontId ?? xf.fontid;
  if (fontId !== undefined && fontId < fonts.length) {
    const font = fonts[fontId];
    if (font) {
      if (font.bold || font.b) result.fontWeight = 'bold';
      if (font.italic || font.i) result.fontStyle = 'italic';
      if (font.underline || font.u) result.textDecoration = 'underline';
      if (font.sz) result.fontSize = `${font.sz}pt`;
      if (font.name) result.fontFamily = font.name;
      if (font.color) {
        const c = xlsxColorToCSS(font.color);
        if (c) result.color = c;
      }
    }
  }

  // 2. Fill (wb.Styles.Fills에서)
  const fillId = xf.fillId ?? xf.fillid;
  if (fillId !== undefined && fillId < fills.length) {
    const fill = fills[fillId];
    if (fill && fill.patternType && fill.patternType !== 'none' && fill.patternType !== 'gray125' && fill.patternType !== 'lightGray') {
      const fg = xlsxColorToCSS(fill.fgColor);
      if (fg) {
        result.backgroundColor = fg;
      } else {
        const bg = xlsxColorToCSS(fill.bgColor);
        if (bg && bg !== '#000000') result.backgroundColor = bg;
      }
    }
  }

  // 3. Border (ZIP에서 파싱한 borders 사용)
  const borderId = xf.borderId ?? xf.borderid;
  if (borderId !== undefined && borderId < zipBorders.length) {
    const border = zipBorders[borderId];
    if (border) {
      if (border.top) result.borderTop = xlsxBorderSideToCSS(border.top);
      if (border.bottom) result.borderBottom = xlsxBorderSideToCSS(border.bottom);
      if (border.left) result.borderLeft = xlsxBorderSideToCSS(border.left);
      if (border.right) result.borderRight = xlsxBorderSideToCSS(border.right);
    }
  }

  // 4. Alignment (CellXf에서)
  if (xf.alignment) {
    if (xf.alignment.horizontal) {
      const hMap: Record<string, 'left' | 'center' | 'right'> = {
        left: 'left', center: 'center', right: 'right',
        general: 'left', justify: 'left', fill: 'left',
      };
      result.textAlign = hMap[xf.alignment.horizontal] || 'left';
    }
    if (xf.alignment.vertical) {
      const vMap: Record<string, 'top' | 'middle' | 'bottom'> = {
        top: 'top', center: 'middle', bottom: 'bottom',
      };
      result.verticalAlign = vMap[xf.alignment.vertical] || 'bottom';
    }
  }

  return result;
};

/**
 * XLSX.write 출력 버퍼에 원본 파일의 이미지/드로잉/미디어를 병합합니다.
 * xlsx-js-style이 이미지를 지원하지 않으므로, 원본 ZIP에서 미디어 관련 엔트리를
 * 출력 ZIP에 복사하여 이미지가 보존되도록 합니다.
 */
const mergeMediaFromOriginal = async (
  outputBuffer: ArrayBuffer,
  originalBuffer: ArrayBuffer,
  sheetOrigIndexMap: Map<string, number>,
  sheetNames: string[],
): Promise<Blob> => {
  const origZip = await JSZip.loadAsync(originalBuffer);
  const outZip = await JSZip.loadAsync(outputBuffer);

  // 1. 미디어 파일 복사 (xl/media/*)
  const mediaEntries: { path: string; data: Promise<ArrayBuffer> }[] = [];
  origZip.folder('xl/media')?.forEach((relativePath, file) => {
    if (!file.dir) {
      mediaEntries.push({ path: `xl/media/${relativePath}`, data: file.async('arraybuffer') });
    }
  });
  if (mediaEntries.length === 0) {
    // 이미지가 없으면 병합할 필요 없음 — 출력 그대로 반환
    return outZip.generateAsync({
      type: 'blob',
      mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
  }
  for (const entry of mediaEntries) {
    outZip.file(entry.path, await entry.data);
  }

  // 2. 드로잉 파일 복사 (xl/drawings/*)
  origZip.folder('xl/drawings')?.forEach((relativePath, file) => {
    if (!file.dir) {
      // _rels 폴더 포함
      outZip.file(`xl/drawings/${relativePath}`, file.async('arraybuffer'));
    }
  });

  // 3. 시트별 드로잉 관계 복사
  for (let outIdx = 0; outIdx < sheetNames.length; outIdx++) {
    const sheetName = sheetNames[outIdx];
    const origIdx = sheetOrigIndexMap.get(sheetName);
    if (origIdx === undefined) continue; // 새로 추가된 시트 — 원본에 없음

    const origSheetNum = origIdx + 1;
    const outSheetNum = outIdx + 1;

    // 원본 시트 rels에서 드로잉 관계 찾기
    const origRelsPath = `xl/worksheets/_rels/sheet${origSheetNum}.xml.rels`;
    const origRelsFile = origZip.file(origRelsPath);
    if (!origRelsFile) continue;

    const origRelsXml = await origRelsFile.async('text');

    // 드로잉 관련 Relationship 추출
    const drawingRelRegex = /<Relationship[^>]*Type="[^"]*\/drawing"[^>]*\/?\s*>/gi;
    const drawingRels = origRelsXml.match(drawingRelRegex);
    if (!drawingRels || drawingRels.length === 0) continue;

    // vmlDrawing 관계도 추출 (레거시 이미지/코멘트용)
    const vmlRelRegex = /<Relationship[^>]*Type="[^"]*\/vmlDrawing"[^>]*\/?\s*>/gi;
    const vmlRels = origRelsXml.match(vmlRelRegex) || [];

    // 출력 시트 rels에 병합
    const outRelsPath = `xl/worksheets/_rels/sheet${outSheetNum}.xml.rels`;
    const outRelsFile = outZip.file(outRelsPath);

    if (outRelsFile) {
      let outRelsXml = await outRelsFile.async('text');
      // 기존 rId 최대값 찾기 (충돌 방지)
      const existingIds = [...outRelsXml.matchAll(/Id="rId(\d+)"/g)].map(m => parseInt(m[1], 10));
      let nextId = existingIds.length > 0 ? Math.max(...existingIds) + 1 : 100;

      for (const rel of [...drawingRels, ...vmlRels]) {
        // rId를 새 값으로 교체
        const updatedRel = rel.replace(/Id="rId\d+"/, `Id="rId${nextId}"`);
        if (!outRelsXml.includes('</Relationships>')) {
          outRelsXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${updatedRel}</Relationships>`;
        } else {
          outRelsXml = outRelsXml.replace('</Relationships>', `${updatedRel}</Relationships>`);
        }

        // 시트 XML에 <drawing> 참조 추가
        const outSheetPath = `xl/worksheets/sheet${outSheetNum}.xml`;
        const outSheetFile = outZip.file(outSheetPath);
        if (outSheetFile && rel.includes('/drawing')) {
          let outSheetXml = await outSheetFile.async('text');
          if (!outSheetXml.includes('<drawing ')) {
            // </worksheet> 앞에 삽입
            outSheetXml = outSheetXml.replace(
              '</worksheet>',
              `<drawing r:id="rId${nextId}"/></worksheet>`,
            );
            outZip.file(outSheetPath, outSheetXml);
          }
        }
        nextId++;
      }
      outZip.file(outRelsPath, outRelsXml);
    } else {
      // 출력에 rels 파일 없음 — 원본 것을 복사 (시트 번호 변환)
      if (origSheetNum === outSheetNum) {
        outZip.file(outRelsPath, origRelsXml);
      } else {
        // rId는 그대로 사용 가능 (충돌 없음)
        outZip.file(outRelsPath, origRelsXml);
      }
      // 시트 XML에 <drawing> 참조 추가
      const outSheetPath = `xl/worksheets/sheet${outSheetNum}.xml`;
      const outSheetFile = outZip.file(outSheetPath);
      if (outSheetFile) {
        let outSheetXml = await outSheetFile.async('text');
        if (!outSheetXml.includes('<drawing ')) {
          // 원본에서 drawing rId 추출
          const rIdMatch = drawingRels[0]?.match(/Id="(rId\d+)"/);
          if (rIdMatch) {
            outSheetXml = outSheetXml.replace(
              '</worksheet>',
              `<drawing r:id="${rIdMatch[1]}"/></worksheet>`,
            );
            outZip.file(outSheetPath, outSheetXml);
          }
        }
      }
    }
  }

  // 4. [Content_Types].xml 업데이트
  const origCtFile = origZip.file('[Content_Types].xml');
  const outCtFile = outZip.file('[Content_Types].xml');
  if (origCtFile && outCtFile) {
    const origCtXml = await origCtFile.async('text');
    let outCtXml = await outCtFile.async('text');

    // 이미지 확장자 Default 엔트리 복사
    const defaultRegex = /<Default\s+[^>]*Extension="(png|jpg|jpeg|gif|bmp|tiff|tif|emf|wmf|svg)"[^>]*\/?\s*>/gi;
    let match;
    while ((match = defaultRegex.exec(origCtXml)) !== null) {
      const ext = match[1].toLowerCase();
      if (!outCtXml.toLowerCase().includes(`extension="${ext}"`)) {
        outCtXml = outCtXml.replace('</Types>', `${match[0]}</Types>`);
      }
    }

    // 드로잉 Override 엔트리 복사
    const overrideRegex = /<Override\s+[^>]*PartName="\/xl\/drawings\/[^"]*"[^>]*\/?\s*>/gi;
    while ((match = overrideRegex.exec(origCtXml)) !== null) {
      if (!outCtXml.includes(match[0])) {
        outCtXml = outCtXml.replace('</Types>', `${match[0]}</Types>`);
      }
    }

    // vmlDrawing Override 엔트리 복사
    const vmlOverrideRegex = /<Override\s+[^>]*PartName="\/xl\/drawings\/[^"]*vml[^"]*"[^>]*\/?\s*>/gi;
    while ((match = vmlOverrideRegex.exec(origCtXml)) !== null) {
      if (!outCtXml.includes(match[0])) {
        outCtXml = outCtXml.replace('</Types>', `${match[0]}</Types>`);
      }
    }

    // vml Default 엔트리
    if (origCtXml.includes('Extension="vml"') && !outCtXml.includes('Extension="vml"')) {
      const vmlDefault = origCtXml.match(/<Default\s+[^>]*Extension="vml"[^>]*\/?\s*>/i);
      if (vmlDefault) {
        outCtXml = outCtXml.replace('</Types>', `${vmlDefault[0]}</Types>`);
      }
    }

    outZip.file('[Content_Types].xml', outCtXml);
  }

  return outZip.generateAsync({
    type: 'blob',
    mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
};

/**
 * 다운로드 전 모든 셀에 완전한 xlsx-js-style 호환 cell.s 스타일 객체를 재구축합니다.
 * xlsx-js-style이 읽기 시 cell.s를 불완전하게 파싱하는 문제를 보완합니다.
 */
const rebuildAllCellStyles = async (
  wb: XLSX.WorkBook,
  buffer: ArrayBuffer,
  wbStyles: any,
  borders: any[],
  sheetOrigIndexMap: Map<string, number>,
): Promise<void> => {
  const cellXfs: any[] = wbStyles?.CellXf || [];
  const fonts: any[] = wbStyles?.Fonts || [];
  const fills: any[] = wbStyles?.Fills || [];

  const zip = await JSZip.loadAsync(buffer);
  const sheetPaths = await getSheetFilePaths(zip);

  for (const sheetName of wb.SheetNames) {
    const sheet = wb.Sheets[sheetName];
    if (!sheet) continue;

    // 원본 ZIP에서의 시트 인덱스 결정
    const origIdx = sheetOrigIndexMap.get(sheetName);
    if (origIdx === undefined || origIdx >= sheetPaths.length) continue;

    const sheetFilePath = sheetPaths[origIdx];
    const sheetFile = zip.file(sheetFilePath);
    if (!sheetFile) continue;

    const xml = await sheetFile.async('text');

    // 셀별 스타일 인덱스 파싱
    const indexMap = new Map<string, number>();
    const cellRe = /<c\s[^>]*?r="([A-Z]+\d+)"[^>]*?s="(\d+)"[^>]*/g;
    let match;
    while ((match = cellRe.exec(xml)) !== null) {
      const decoded = XLSX.utils.decode_cell(match[1]);
      indexMap.set(`${decoded.r}:${decoded.c}`, parseInt(match[2], 10));
    }
    const cellRe2 = /<c\s[^>]*?s="(\d+)"[^>]*?r="([A-Z]+\d+)"[^>]*/g;
    while ((match = cellRe2.exec(xml)) !== null) {
      const decoded = XLSX.utils.decode_cell(match[2]);
      const key = `${decoded.r}:${decoded.c}`;
      if (!indexMap.has(key)) {
        indexMap.set(key, parseInt(match[1], 10));
      }
    }

    // 각 셀에 cell.s 재구축
    indexMap.forEach((styleIdx, key) => {
      const [r, c] = key.split(':').map(Number);
      const addr = XLSX.utils.encode_cell({ r, c });
      const cell = sheet[addr];
      if (!cell) return;
      if (styleIdx < 0 || styleIdx >= cellXfs.length) return;

      const xf = cellXfs[styleIdx];
      if (!xf) return;

      const s: any = {};

      // 1. 폰트
      const fontId = xf.fontId ?? xf.fontid;
      if (fontId !== undefined && fontId < fonts.length) {
        const font = fonts[fontId];
        if (font) {
          const f: any = {};
          if (font.bold || font.b) f.bold = true;
          if (font.italic || font.i) f.italic = true;
          if (font.underline || font.u) f.underline = true;
          if (font.strike) f.strike = true;
          if (font.sz) f.sz = font.sz;
          if (font.name) f.name = font.name;
          if (font.color) {
            const fc: any = {};
            if (font.color.rgb) fc.rgb = font.color.rgb;
            if (font.color.theme !== undefined) fc.theme = font.color.theme;
            if (font.color.indexed !== undefined) fc.indexed = font.color.indexed;
            if (font.color.tint !== undefined) fc.tint = font.color.tint;
            if (font.color.auto !== undefined) fc.auto = font.color.auto;
            if (Object.keys(fc).length > 0) f.color = fc;
          }
          if (Object.keys(f).length > 0) s.font = f;
        }
      }

      // 2. 채우기
      const fillId = xf.fillId ?? xf.fillid;
      if (fillId !== undefined && fillId < fills.length) {
        const fill = fills[fillId];
        if (fill && fill.patternType) {
          const fl: any = { patternType: fill.patternType };
          if (fill.fgColor) {
            const fc: any = {};
            if (fill.fgColor.rgb) fc.rgb = fill.fgColor.rgb;
            if (fill.fgColor.theme !== undefined) fc.theme = fill.fgColor.theme;
            if (fill.fgColor.indexed !== undefined) fc.indexed = fill.fgColor.indexed;
            if (fill.fgColor.tint !== undefined) fc.tint = fill.fgColor.tint;
            if (Object.keys(fc).length > 0) fl.fgColor = fc;
          }
          if (fill.bgColor) {
            const bc: any = {};
            if (fill.bgColor.rgb) bc.rgb = fill.bgColor.rgb;
            if (fill.bgColor.theme !== undefined) bc.theme = fill.bgColor.theme;
            if (fill.bgColor.indexed !== undefined) bc.indexed = fill.bgColor.indexed;
            if (fill.bgColor.tint !== undefined) bc.tint = fill.bgColor.tint;
            if (Object.keys(bc).length > 0) fl.bgColor = bc;
          }
          s.fill = fl;
        }
      }

      // 3. 테두리 (ZIP에서 파싱한 borders)
      const borderId = xf.borderId ?? xf.borderid;
      if (borderId !== undefined && borderId < borders.length) {
        const border = borders[borderId];
        if (border) {
          const b: any = {};
          for (const side of ['top', 'bottom', 'left', 'right'] as const) {
            if (border[side] && border[side].style) {
              b[side] = { style: border[side].style };
              if (border[side].color) {
                const cc: any = {};
                if (border[side].color.rgb) cc.rgb = border[side].color.rgb;
                if (border[side].color.theme !== undefined) cc.theme = border[side].color.theme;
                if (border[side].color.indexed !== undefined) cc.indexed = border[side].color.indexed;
                if (border[side].color.auto !== undefined) cc.auto = border[side].color.auto;
                if (Object.keys(cc).length > 0) b[side].color = cc;
              }
            }
          }
          if (Object.keys(b).length > 0) s.border = b;
        }
      }

      // 4. 정렬
      if (xf.alignment) {
        const a: any = {};
        if (xf.alignment.horizontal) a.horizontal = xf.alignment.horizontal;
        if (xf.alignment.vertical) a.vertical = xf.alignment.vertical;
        if (xf.alignment.wrapText) a.wrapText = true;
        if (xf.alignment.textRotation !== undefined) a.textRotation = xf.alignment.textRotation;
        if (xf.alignment.indent !== undefined) a.indent = xf.alignment.indent;
        if (xf.alignment.shrinkToFit) a.shrinkToFit = true;
        if (Object.keys(a).length > 0) s.alignment = a;
      }

      // 5. 숫자 형식 (내장 + 사용자 정의)
      if (xf.numFmtId !== undefined && xf.numFmtId !== 0) {
        // 내장 numFmt 매핑 (Excel 표준)
        const builtinNumFmts: Record<number, string> = {
          1: '0', 2: '0.00', 3: '#,##0', 4: '#,##0.00',
          9: '0%', 10: '0.00%', 11: '0.00E+00', 12: '# ?/?', 13: '# ??/??',
          14: 'm/d/yy', 15: 'd-mmm-yy', 16: 'd-mmm', 17: 'mmm-yy',
          18: 'h:mm AM/PM', 19: 'h:mm:ss AM/PM', 20: 'h:mm', 21: 'h:mm:ss',
          22: 'm/d/yy h:mm', 37: '#,##0 ;(#,##0)', 38: '#,##0 ;[Red](#,##0)',
          39: '#,##0.00;(#,##0.00)', 40: '#,##0.00;[Red](#,##0.00)',
          41: '_(* #,##0_);_(* (#,##0);_(* "-"_);_(@_)',
          42: '_("$"* #,##0_);_("$"* (#,##0);_("$"* "-"_);_(@_)',
          43: '_(* #,##0.00_);_(* (#,##0.00);_(* "-"??_);_(@_)',
          44: '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "-"??_);_(@_)',
          45: 'mm:ss', 46: '[h]:mm:ss', 47: 'mmss.0', 48: '##0.0E+0', 49: '@',
        };
        let found = false;
        // 사용자 정의 numFmt 먼저 검색
        const numFmts = wbStyles?.NumberFmt || [];
        for (const nf of numFmts) {
          if ((nf.numFmtId ?? nf.id) === xf.numFmtId) {
            const code = nf.formatCode ?? nf.fmt;
            if (code) { s.numFmt = code; found = true; }
            break;
          }
        }
        // 내장 numFmt 폴백
        if (!found && builtinNumFmts[xf.numFmtId]) {
          s.numFmt = builtinNumFmts[xf.numFmtId];
        }
      }

      if (Object.keys(s).length > 0) {
        cell.s = s;
      }
    });
  }
};

/* ── 이미지 추출 헬퍼 ── */
const extractImagesFromXlsx = async (
  arrayBuffer: ArrayBuffer,
  sheetIndex: number,
): Promise<SheetImage[]> => {
  const images: SheetImage[] = [];
  try {
    const zip = await JSZip.loadAsync(arrayBuffer);

    // drawing 관계 파일 찾기
    const sheetRelsPath = `xl/worksheets/_rels/sheet${sheetIndex + 1}.xml.rels`;
    const relsFile = zip.file(sheetRelsPath);
    if (!relsFile) return images;

    const relsXml = await relsFile.async('text');
    const parser = new DOMParser();
    const relsDoc = parser.parseFromString(relsXml, 'text/xml');

    // drawing 관계 찾기
    const drawingRel = Array.from(relsDoc.getElementsByTagName('Relationship')).find(
      (r) => r.getAttribute('Type')?.includes('/drawing'),
    );
    if (!drawingRel) return images;

    const drawingTarget = drawingRel.getAttribute('Target') || '';
    const drawingPath = drawingTarget.startsWith('/')
      ? drawingTarget.slice(1)
      : `xl/worksheets/${drawingTarget}`.replace(/\/\.\.\//, '/');
    const normalizedDrawingPath = drawingPath.replace(/xl\/worksheets\/\.\.\//, 'xl/');

    const drawingFile = zip.file(normalizedDrawingPath);
    if (!drawingFile) return images;

    const drawingXml = await drawingFile.async('text');
    const drawingDoc = parser.parseFromString(drawingXml, 'text/xml');

    // drawing rels 파일
    const drawingName = normalizedDrawingPath.split('/').pop() || '';
    const drawingDir = normalizedDrawingPath.substring(0, normalizedDrawingPath.lastIndexOf('/'));
    const drawingRelsPath = `${drawingDir}/_rels/${drawingName}.rels`;
    const drawingRelsFile = zip.file(drawingRelsPath);

    let imageRelMap: Record<string, string> = {};
    if (drawingRelsFile) {
      const drRelsXml = await drawingRelsFile.async('text');
      const drRelsDoc = parser.parseFromString(drRelsXml, 'text/xml');
      const rels = drRelsDoc.getElementsByTagName('Relationship');
      for (let i = 0; i < rels.length; i++) {
        const rel = rels[i];
        const rId = rel.getAttribute('Id') || '';
        const target = rel.getAttribute('Target') || '';
        imageRelMap[rId] = target;
      }
    }

    // twoCellAnchor / oneCellAnchor 파싱
    const anchors = [
      ...Array.from(drawingDoc.getElementsByTagNameNS('*', 'twoCellAnchor')),
      ...Array.from(drawingDoc.getElementsByTagNameNS('*', 'oneCellAnchor')),
    ];

    for (const anchor of anchors) {
      const fromEl = anchor.getElementsByTagNameNS('*', 'from')[0];
      const toEl = anchor.getElementsByTagNameNS('*', 'to')[0];
      const blipEl = anchor.getElementsByTagNameNS('*', 'blip')[0];

      if (!fromEl || !blipEl) continue;

      const fromCol = parseInt(fromEl.getElementsByTagNameNS('*', 'col')[0]?.textContent || '0', 10);
      const fromRow = parseInt(fromEl.getElementsByTagNameNS('*', 'row')[0]?.textContent || '0', 10);
      let toCol: number | undefined;
      let toRow: number | undefined;
      if (toEl) {
        toCol = parseInt(toEl.getElementsByTagNameNS('*', 'col')[0]?.textContent || '0', 10);
        toRow = parseInt(toEl.getElementsByTagNameNS('*', 'row')[0]?.textContent || '0', 10);
      }

      // 이미지 embed ID
      const embedId =
        blipEl.getAttributeNS('http://schemas.openxmlformats.org/officeDocument/2006/relationships', 'embed') ||
        blipEl.getAttribute('r:embed') || '';
      if (!embedId || !imageRelMap[embedId]) continue;

      const imgTarget = imageRelMap[embedId];
      const imgPath = imgTarget.startsWith('/')
        ? imgTarget.slice(1)
        : `${drawingDir}/${imgTarget}`.replace(/\/\.\.\//, '/').replace(/xl\/drawings\/\.\.\//, 'xl/');

      const imgFile = zip.file(imgPath);
      if (!imgFile) continue;

      const imgData = await imgFile.async('base64');
      const ext = imgPath.split('.').pop()?.toLowerCase() || 'png';
      const mime = ext === 'jpg' || ext === 'jpeg' ? 'image/jpeg'
        : ext === 'gif' ? 'image/gif'
        : ext === 'svg' ? 'image/svg+xml'
        : 'image/png';

      // extent 크기 (EMU → px, 1 EMU = 1/914400 inch, 96 DPI)
      const extEl = anchor.getElementsByTagNameNS('*', 'ext')[0];
      let width: number | undefined;
      let height: number | undefined;
      if (extEl) {
        const cx = parseInt(extEl.getAttribute('cx') || '0', 10);
        const cy = parseInt(extEl.getAttribute('cy') || '0', 10);
        if (cx > 0) width = Math.round(cx / 9525);
        if (cy > 0) height = Math.round(cy / 9525);
      }

      images.push({
        src: `data:${mime};base64,${imgData}`,
        fromRow,
        fromCol,
        toRow,
        toCol,
        width,
        height,
      });
    }
  } catch (err) {
    console.warn('[ExcelRenderer] Image extraction failed:', err);
  }
  return images;
};

/* ── 병합 맵 생성 ── */
const buildMergeMap = (
  merges: XLSX.Range[] | undefined,
): Map<string, MergeInfo> => {
  const map = new Map<string, MergeInfo>();
  if (!merges) return map;

  for (const merge of merges) {
    const sr = merge.s.r;
    const sc = merge.s.c;
    const er = merge.e.r;
    const ec = merge.e.c;
    const rowSpan = er - sr + 1;
    const colSpan = ec - sc + 1;

    for (let r = sr; r <= er; r++) {
      for (let c = sc; c <= ec; c++) {
        const key = `${r}:${c}`;
        if (r === sr && c === sc) {
          map.set(key, { isOrigin: true, hidden: false, colSpan, rowSpan });
        } else {
          map.set(key, { isOrigin: false, hidden: true, colSpan: 1, rowSpan: 1 });
        }
      }
    }
  }
  return map;
};

/* ── 기본 열 너비(px) 및 행 높이(px) ── */
const DEFAULT_COL_WIDTH = 72;
const MIN_COL_WIDTH = 30;
const MAX_COL_WIDTH = 300;
const DEFAULT_ROW_HEIGHT = 24;
const MIN_ROW_HEIGHT = 16;
const HEADER_COL_WIDTH = 46;

/** Virtual Scrolling: 뷰포트 밖 추가 렌더링 행 수 (위/아래 각각) — 초기 ~50행 표시 */
const OVERSCAN_ROWS = 50;

/** Virtual Scrolling: rowPrefixSums에서 value 이상인 첫 인덱스 반환 (이진 탐색) */
const binarySearchRow = (prefixSums: number[], value: number): number => {
  let lo = 0;
  let hi = prefixSums.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (prefixSums[mid] < value) lo = mid + 1;
    else hi = mid;
  }
  return lo;
};

/* ------------------------------------------------------------------ */
/* Excel 선택 색상 (Excel-like green theme)                            */
/* ------------------------------------------------------------------ */
const EXCEL_GREEN        = '#217346';  // 헤더 선택 bg, 활성 셀 border, 시트 탭 강조
const EXCEL_GREEN_DARK   = '#185C37';  // 헤더 hover (선택 상태)
const EXCEL_GREEN_LIGHT  = '#E2EFDA';  // 선택 범위 배경 (반투명)
const EXCEL_SELECT_BG    = 'rgba(33, 115, 70, 0.12)'; // 셀 선택 오버레이
const ENABLE_CELL_CONTEXT_MENU = false;
const ENABLE_HEADER_CONTEXT_MENU = false;

/* ── 컬러 팔레트 (Excel 기본) ── */
const COLOR_PALETTE = [
  ['#000000', '#434343', '#666666', '#999999', '#B7B7B7', '#CCCCCC', '#D9D9D9', '#EFEFEF', '#F3F3F3', '#FFFFFF'],
  ['#980000', '#FF0000', '#FF9900', '#FFFF00', '#00FF00', '#00FFFF', '#4A86E8', '#0000FF', '#9900FF', '#FF00FF'],
  ['#E6B8AF', '#F4CCCC', '#FCE5CD', '#FFF2CC', '#D9EAD3', '#D0E0E3', '#C9DAF8', '#CFE2F3', '#D9D2E9', '#EAD1DC'],
  ['#DD7E6B', '#EA9999', '#F9CB9C', '#FFE599', '#B6D7A8', '#A2C4C9', '#A4C2F4', '#9FC5E8', '#B4A7D6', '#D5A6BD'],
  ['#CC4125', '#E06666', '#F6B26B', '#FFD966', '#93C47D', '#76A5AF', '#6D9EEB', '#6FA8DC', '#8E7CC3', '#C27BA0'],
  ['#A61C00', '#CC0000', '#E69138', '#F1C232', '#6AA84F', '#45818E', '#3C78D8', '#3D85C6', '#674EA7', '#A64D79'],
  ['#85200C', '#990000', '#B45F06', '#BF9000', '#38761D', '#134F5C', '#1155CC', '#0B5394', '#351C75', '#741B47'],
  ['#5B0F00', '#660000', '#783F04', '#7F6000', '#274E13', '#0C343D', '#1C4587', '#073763', '#20124D', '#4C1130'],
];

/* ── 테두리 옵션 타입 ── */
type BorderOption =
  | 'all' | 'outer' | 'thick-outer' | 'bottom'
  | 'left' | 'right' | 'none' | 'top-bottom'
  | 'top' | 'inner-h' | 'inner-v' | 'inner-all';

interface BorderOptionDef { key: BorderOption; label: string }
const BORDER_OPTIONS: BorderOptionDef[][] = [
  [
    { key: 'all', label: '모든 테두리' },
    { key: 'outer', label: '바깥쪽 테두리' },
    { key: 'thick-outer', label: '굵은 바깥쪽 테두리' },
    { key: 'bottom', label: '아래쪽 테두리' },
  ],
  [
    { key: 'left', label: '왼쪽 테두리' },
    { key: 'right', label: '오른쪽 테두리' },
    { key: 'none', label: '테두리 없음' },
    { key: 'top-bottom', label: '위/아래 테두리' },
  ],
  [
    { key: 'top', label: '위쪽 테두리' },
    { key: 'inner-h', label: '가로 안쪽 테두리' },
    { key: 'inner-v', label: '세로 안쪽 테두리' },
    { key: 'inner-all', label: '안쪽 테두리' },
  ],
];

/** 테두리 옵션 SVG 아이콘 */
const BorderIcon: React.FC<{ type: BorderOption }> = ({ type }) => {
  const s = 20;
  const thin = { stroke: '#333', strokeWidth: 1.5 };
  const thick = { stroke: '#333', strokeWidth: 3 };
  const light = { stroke: '#ccc', strokeWidth: 0.8, strokeDasharray: '2 2' };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _none = { stroke: 'none' };

  switch (type) {
    case 'all':
      return (
        <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
          <rect x="1" y="1" width={s - 2} height={s - 2} fill="none" {...thin} />
          <line x1={s / 2} y1="1" x2={s / 2} y2={s - 1} {...thin} />
          <line x1="1" y1={s / 2} x2={s - 1} y2={s / 2} {...thin} />
        </svg>
      );
    case 'outer':
      return (
        <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
          <rect x="1" y="1" width={s - 2} height={s - 2} fill="none" {...thin} />
        </svg>
      );
    case 'thick-outer':
      return (
        <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
          <rect x="2" y="2" width={s - 4} height={s - 4} fill="none" {...thick} />
        </svg>
      );
    case 'none':
      return (
        <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
          <rect x="1" y="1" width={s - 2} height={s - 2} fill="none" {...light} />
          <line x1="3" y1="3" x2={s - 3} y2={s - 3} stroke="#cc0000" strokeWidth="1.5" />
          <line x1={s - 3} y1="3" x2="3" y2={s - 3} stroke="#cc0000" strokeWidth="1.5" />
        </svg>
      );
    case 'top':
      return (
        <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
          <rect x="1" y="1" width={s - 2} height={s - 2} fill="none" {...light} />
          <line x1="1" y1="1" x2={s - 1} y2="1" {...thin} />
        </svg>
      );
    case 'bottom':
      return (
        <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
          <rect x="1" y="1" width={s - 2} height={s - 2} fill="none" {...light} />
          <line x1="1" y1={s - 1} x2={s - 1} y2={s - 1} {...thin} />
        </svg>
      );
    case 'left':
      return (
        <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
          <rect x="1" y="1" width={s - 2} height={s - 2} fill="none" {...light} />
          <line x1="1" y1="1" x2="1" y2={s - 1} {...thin} />
        </svg>
      );
    case 'right':
      return (
        <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
          <rect x="1" y="1" width={s - 2} height={s - 2} fill="none" {...light} />
          <line x1={s - 1} y1="1" x2={s - 1} y2={s - 1} {...thin} />
        </svg>
      );
    case 'top-bottom':
      return (
        <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
          <rect x="1" y="1" width={s - 2} height={s - 2} fill="none" {...light} />
          <line x1="1" y1="1" x2={s - 1} y2="1" {...thin} />
          <line x1="1" y1={s - 1} x2={s - 1} y2={s - 1} {...thin} />
        </svg>
      );
    case 'inner-h':
      return (
        <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
          <rect x="1" y="1" width={s - 2} height={s - 2} fill="none" {...light} />
          <line x1="1" y1={s / 2} x2={s - 1} y2={s / 2} {...thin} />
        </svg>
      );
    case 'inner-v':
      return (
        <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
          <rect x="1" y="1" width={s - 2} height={s - 2} fill="none" {...light} />
          <line x1={s / 2} y1="1" x2={s / 2} y2={s - 1} {...thin} />
        </svg>
      );
    case 'inner-all':
      return (
        <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
          <rect x="1" y="1" width={s - 2} height={s - 2} fill="none" {...light} />
          <line x1={s / 2} y1="1" x2={s / 2} y2={s - 1} {...thin} />
          <line x1="1" y1={s / 2} x2={s - 1} y2={s / 2} {...thin} />
        </svg>
      );
    default:
      return null;
  }
};

/* ── Delta 기반 Undo/Redo (메모리 최적화) ── */

/** 셀 단위 변경 기록 */
interface CellDelta {
  key: string;               // "row:col"
  oldVal?: string;
  newVal?: string;
  oldStyle?: ParsedCellStyle;
  newStyle?: ParsedCellStyle;
}
/** 병합 맵 변경 기록 */
interface MergeDelta {
  key: string;               // "row:col"
  oldInfo?: MergeInfo;
  newInfo?: MergeInfo;
}
/** 구조적 변경 (행/열 삽입삭제 등): 전체 data/rows 수 변경됨 → 압축 스냅샷 사용 */
interface StructuralSnapshot {
  data: string[][];
  cellStyles: Map<string, ParsedCellStyle>;
  mergeMap: Map<string, MergeInfo>;
  colCount: number;
}
/** Delta 히스토리 엔트리 */
interface DeltaHistoryEntry {
  type: 'delta';
  cellDeltas: CellDelta[];
  mergeDeltas: MergeDelta[];
}
/** Structural 히스토리 엔트리 (행/열 삽입삭제 시 사용) */
interface StructuralHistoryEntry {
  type: 'structural';
  before: StructuralSnapshot;
  after: StructuralSnapshot;
}
type HistoryEntry = DeltaHistoryEntry | StructuralHistoryEntry;

const MAX_HISTORY = 50;

/* ------------------------------------------------------------------ */
/* Styled Components                                                   */
/* ------------------------------------------------------------------ */

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  font-family: ${({ theme }) => theme.fonts.sans};
`;

/* ── Toolbar ── */
const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 6px 8px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  flex-shrink: 0;
  flex-wrap: wrap;
  min-height: 40px;
`;

const ToolbarGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1px;
  padding: 0 4px;

  &:not(:last-child) {
    border-right: 1px solid ${({ theme }) => theme.colors.borderLight};
    padding-right: 6px;
    margin-right: 2px;
  }
`;

const ToolbarBtn = styled.button<{ $active?: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textPrimary};
  transition: all ${({ theme }) => theme.transitions.fast};
  flex-shrink: 0;

  ${({ $active, theme }) =>
    $active &&
    css`
      background: ${theme.colors.accentLight};
      color: ${theme.colors.textPrimary};
    `}

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  svg {
    width: 15px;
    height: 15px;
    stroke-width: 2.2;
  }
`;

const FontSizeInput = styled.input`
  width: 36px;
  height: 26px;
  text-align: center;
  font-size: 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  color: ${({ theme }) => theme.colors.textPrimary};
  outline: none;
  padding: 0 2px;
  flex-shrink: 0;

  &:focus {
    border-color: #217346;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

/* ── 수식 입력 바 ── */
const FormulaBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  flex-shrink: 0;
  min-height: 32px;
`;

const CellRefLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  min-width: 48px;
  text-align: center;
  padding: 2px 6px;
  background: ${({ theme }) => theme.colors.bgSecondary};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 4px;
  user-select: none;
`;

const FormulaInput = styled.input`
  flex: 1;
  font-size: 0.75rem;
  font-family: ${({ theme }) => theme.fonts.mono};
  color: ${({ theme }) => theme.colors.textPrimary};
  background: ${({ theme }) => theme.colors.bgSecondary};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 4px;
  padding: 2px 8px;
  outline: none;
  height: 24px;

  &:focus {
    border-color: ${EXCEL_GREEN};
  }
`;

/* ── 시트 탭 ── */
const SheetTabs = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  padding: 0 8px;
  background: ${({ theme }) => theme.colors.bgSecondary};
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
  flex-shrink: 0;
  overflow-x: auto;
  min-height: 32px;

  &::-webkit-scrollbar {
    height: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.scrollbar};
    border-radius: 2px;
  }
`;

const SheetTab = styled.button<{ $active?: boolean; $dragOver?: 'left' | 'right' | null; $dragging?: boolean }>`
  padding: 6px 14px;
  font-size: 0.6875rem;
  font-weight: 500;
  color: ${({ $active, theme }) =>
    $active ? theme.colors.textPrimary : theme.colors.textMuted};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.bgPrimary : 'transparent'};
  border-bottom: 2px solid ${({ $active }) =>
    $active ? EXCEL_GREEN : 'transparent'};
  border-top: none;
  border-left: none;
  border-right: 1px solid ${({ theme }) => theme.colors.borderLight};
  white-space: nowrap;
  transition: background ${({ theme }) => theme.transitions.fast},
              color ${({ theme }) => theme.transitions.fast},
              opacity 0.15s ease;
  cursor: grab;
  position: relative;

  ${({ $dragging }) =>
    $dragging &&
    css`
      opacity: 0.4;
      cursor: grabbing;
    `}

  ${({ $dragOver }) =>
    $dragOver === 'left' &&
    css`
      box-shadow: inset 3px 0 0 0 ${EXCEL_GREEN};
    `}

  ${({ $dragOver }) =>
    $dragOver === 'right' &&
    css`
      box-shadow: inset -3px 0 0 0 ${EXCEL_GREEN};
    `}

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
    background: ${({ theme }) => theme.colors.bgHover};
  }

  &:active {
    cursor: grabbing;
  }
`;

/* ── 테이블 영역 ── */
const TableArea = styled.div`
  flex: 1;
  overflow: auto;
  position: relative;

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.bgSecondary};
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.scrollbar};
    border-radius: 4px;
    &:hover {
      background: ${({ theme }) => theme.colors.scrollbarHover};
    }
  }
  &::-webkit-scrollbar-corner {
    background: ${({ theme }) => theme.colors.bgSecondary};
  }
`;

const Table = styled.table`
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
  user-select: none;

  input {
    user-select: text;
  }
`;

const Thead = styled.thead`
  position: sticky;
  top: 0;
  z-index: 3;
`;

/* ── CornerCell: 행번호/열 알파벳 교차 셀 ── */
const CornerCell = styled.th<{ $selected?: boolean }>`
  width: ${HEADER_COL_WIDTH}px;
  min-width: ${HEADER_COL_WIDTH}px;
  height: 26px;
  background: ${({ $selected, theme }) =>
    $selected ? EXCEL_GREEN : theme.colors.bgSecondary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  position: sticky;
  left: 0;
  top: 0;
  z-index: 5;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;

  &:hover {
    background: ${({ $selected, theme }) =>
      $selected ? EXCEL_GREEN_DARK : theme.colors.bgHover};
  }
`;

/* ── 열 헤더 (알파벳) ── */
const ColHeader = styled.th<{ $selected?: boolean }>`
  height: 26px;
  padding: 0 4px;
  text-align: center;
  font-size: 0.6875rem;
  font-weight: 600;
  color: ${({ $selected, theme }) =>
    $selected ? '#ffffff' : theme.colors.textSecondary};
  background: ${({ $selected, theme }) =>
    $selected ? EXCEL_GREEN : theme.colors.bgSecondary};
  border-bottom: 1px solid ${({ $selected, theme }) =>
    $selected ? EXCEL_GREEN : theme.colors.border};
  border-right: 1px solid ${({ $selected, theme }) =>
    $selected ? EXCEL_GREEN_DARK : theme.colors.borderLight};
  position: sticky;
  top: 0;
  z-index: 3;
  user-select: none;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;

  &:hover {
    background: ${({ $selected, theme }) =>
      $selected ? EXCEL_GREEN_DARK : theme.colors.bgHover};
  }

  &:last-child {
    border-right: 1px solid ${({ $selected, theme }) =>
      $selected ? EXCEL_GREEN : theme.colors.border};
  }
`;

/** 열 리사이즈 핸들 */
const ColResizeHandle = styled.div`
  position: absolute;
  top: 0;
  right: -2px;
  width: 5px;
  height: 100%;
  cursor: col-resize;
  z-index: 4;

  &:hover,
  &:active {
    background: ${EXCEL_GREEN};
    opacity: 0.5;
  }
`;

/* ── 행 헤더 (숫자) ── */
const RowHeader = styled.th<{ $selected?: boolean }>`
  width: ${HEADER_COL_WIDTH}px;
  min-width: ${HEADER_COL_WIDTH}px;
  padding: 0 4px;
  text-align: center;
  font-size: 0.6875rem;
  font-weight: 500;
  color: ${({ $selected, theme }) =>
    $selected ? '#ffffff' : theme.colors.textSecondary};
  background: ${({ $selected, theme }) =>
    $selected ? EXCEL_GREEN : theme.colors.bgSecondary};
  border-bottom: 1px solid ${({ $selected, theme }) =>
    $selected ? EXCEL_GREEN_DARK : theme.colors.borderLight};
  border-right: 1px solid ${({ $selected, theme }) =>
    $selected ? EXCEL_GREEN : theme.colors.border};
  position: sticky;
  left: 0;
  z-index: 2;
  user-select: none;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;

  &:hover {
    background: ${({ $selected, theme }) =>
      $selected ? EXCEL_GREEN_DARK : theme.colors.bgHover};
  }
`;

/** 행 리사이즈 핸들 */
const RowResizeHandle = styled.div`
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 100%;
  height: 5px;
  cursor: row-resize;
  z-index: 4;

  &:hover,
  &:active {
    background: ${EXCEL_GREEN};
    opacity: 0.5;
  }
`;

/* ── 일반 셀 ── */
const Cell = styled.td<{ $selected?: boolean; $active?: boolean; $editing?: boolean }>`
  height: ${DEFAULT_ROW_HEIGHT}px;
  padding: 0;
  position: relative;
  background: #ffffff;
  border-right: 1px solid #e2e2e2;
  border-bottom: 1px solid #e2e2e2;

  /* 선택 범위 — 연녹색 오버레이 */
  ${({ $selected, $active }) =>
    $selected && !$active &&
    css`
      background: ${EXCEL_GREEN_LIGHT} !important;
    `}

  /* 활성 셀 (앵커) — 두꺼운 녹색 테두리, 배경 유지 */
  ${({ $active }) =>
    $active &&
    css`
      box-shadow: inset 0 0 0 2px ${EXCEL_GREEN};
      z-index: 1;
    `}
`;

const CellContent = styled.div`
  padding: 2px 4px;
  font-size: 0.75rem;
  color: #000000;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  height: 100%;
  display: flex;
  align-items: center;
  cursor: default;
  line-height: 1.3;
  position: relative;
`;

const CellImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  pointer-events: none;
`;

const CellInput = styled.input`
  width: 100%;
  height: 100%;
  padding: 2px 4px;
  font-size: 0.75rem;
  font-family: ${({ theme }) => theme.fonts.sans};
  color: #000000;
  background: #ffffff;
  border: 2px solid ${EXCEL_GREEN};
  outline: none;
  box-sizing: border-box;
`;

/* ── 채우기 핸들 ── */
const FillHandle = styled.div`
  position: absolute;
  width: 8px;
  height: 8px;
  background: ${EXCEL_GREEN};
  border: 1px solid #fff;
  cursor: crosshair;
  z-index: 10;
  pointer-events: auto;
  box-sizing: border-box;
`;

/** 채우기 드래그 중 확장 범위 오버레이 */
const FillOverlay = styled.div`
  position: absolute;
  border: 2px dashed ${EXCEL_GREEN};
  background: rgba(33, 115, 70, 0.06);
  pointer-events: none;
  z-index: 9;
  box-sizing: border-box;
`;

/* ── 상태 바 ── */
const StatusBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 3px 12px;
  background: ${({ theme }) => theme.colors.bgSecondary};
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
  font-size: 0.625rem;
  color: ${({ theme }) => theme.colors.textMuted};
  flex-shrink: 0;
  min-height: 22px;
  gap: 16px;
`;

const StatusGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

/* ── 빈 상태 ── */
const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.875rem;
`;

/* ── 시트 추가 버튼 ── */
const AddSheetBtn = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textMuted};
  transition: all ${({ theme }) => theme.transitions.fast};
  flex-shrink: 0;
  margin-left: 4px;

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

/* ── 시트 컨텍스트 메뉴 ── */
const SheetContextOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 99;
`;

const SHEET_CTX_MENU_HEIGHT = 200;
const SHEET_CTX_MENU_WIDTH = 160;

const sheetCtxUp = keyframes`
  from { opacity: 0; transform: scale(0.96) translateY(4px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
`;

const sheetCtxDown = keyframes`
  from { opacity: 0; transform: scale(0.96) translateY(-4px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
`;

const SheetContextMenu = styled.div<{ $x: number; $y: number; $openUp: boolean }>`
  position: fixed;
  ${({ $openUp, $y }) =>
    $openUp ? `bottom: ${window.innerHeight - $y}px;` : `top: ${$y}px;`}
  left: ${({ $x }) => $x}px;
  z-index: 100;
  min-width: ${SHEET_CTX_MENU_WIDTH}px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  padding: 4px;
  animation: ${({ $openUp }) => ($openUp ? sheetCtxUp : sheetCtxDown)} 0.12s ease-out;
  transform-origin: ${({ $openUp }) => ($openUp ? 'bottom left' : 'top left')};
`;

const SheetCtxItem = styled.button<{ $danger?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  background: none;
  border: none;
  border-radius: 6px;
  font-size: 0.75rem;
  color: ${({ $danger, theme }) =>
    $danger ? theme.colors.error : theme.colors.textPrimary};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  text-align: left;

  svg {
    width: 14px;
    height: 14px;
    color: ${({ $danger, theme }) =>
      $danger ? theme.colors.error : theme.colors.textMuted};
    flex-shrink: 0;
  }

  &:hover {
    background: ${({ $danger, theme }) =>
      $danger ? `${theme.colors.error}10` : theme.colors.bgHover};

    svg {
      color: ${({ $danger, theme }) =>
        $danger ? theme.colors.error : theme.colors.textPrimary};
    }
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const SheetCtxDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.borderLight};
  margin: 4px 6px;
`;

/* ── 시트 이름 변경 모달 ── */
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${({ theme }) => theme.colors.bgOverlay};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
  animation: modalOverlayIn 0.15s ease;

  @keyframes modalOverlayIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
`;

const ModalBox = styled.div`
  background: ${({ theme }) => theme.colors.bgPrimary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 12px;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  width: 320px;
  max-width: 90vw;
  animation: modalBoxIn 0.2s ease;

  @keyframes modalBoxIn {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(8px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 10px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const ModalTitle = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const ModalCloseBtn = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textMuted};
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const ModalBody = styled.div`
  padding: 16px;
`;

const ModalLabel = styled.label`
  display: block;
  font-size: 0.75rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 6px;
`;

const ModalInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  background: ${({ theme }) => theme.colors.bgSecondary};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 8px;
  outline: none;
  box-sizing: border-box;
  transition: border-color ${({ theme }) => theme.transitions.fast};

  &:focus {
    border-color: ${EXCEL_GREEN};
  }
`;

const ModalError = styled.span`
  display: block;
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.error};
  margin-top: 4px;
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 10px 16px 14px;
`;

const ModalBtn = styled.button<{ $primary?: boolean }>`
  padding: 6px 16px;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 500;
  transition: all ${({ theme }) => theme.transitions.fast};

  ${({ $primary, theme }) =>
    $primary
      ? css`
          background: ${EXCEL_GREEN};
          color: #ffffff;
          &:hover:not(:disabled) {
            background: ${EXCEL_GREEN_DARK};
          }
        `
      : css`
          background: ${theme.colors.bgSecondary};
          color: ${theme.colors.textSecondary};
          border: 1px solid ${theme.colors.borderLight};
          &:hover:not(:disabled) {
            background: ${theme.colors.bgHover};
            color: ${theme.colors.textPrimary};
          }
        `}

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

/* ── 툴바 드롭다운 (색상/테두리) 공용 ── */
const ToolbarDropdownWrap = styled.div`
  position: relative;
  display: inline-flex;
`;

const ToolbarSplitBtn = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.textMuted};
  transition: all 0.12s ease;
  flex-shrink: 0;
  padding: 0;
  gap: 0;

  ${({ $active }) =>
    $active &&
    css`
      background: rgba(33,115,70,0.1);
      color: ${EXCEL_GREEN};
    `}

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
  &:disabled { opacity: 0.3; cursor: not-allowed; }
`;

const SplitBtnIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 28px;
  svg { width: 14px; height: 14px; }
`;

const SplitBtnArrow = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 28px;
  svg { width: 10px; height: 10px; }
`;

/** 색상 인디케이터 바 (아이콘 아래에 표시) */
const ColorIndicator = styled.span<{ $color: string }>`
  position: absolute;
  bottom: 2px;
  left: 4px;
  right: 4px;
  height: 3px;
  border-radius: 1px;
  background: ${({ $color }) => $color};
`;

/** 드롭다운 패널 공용 */
const DropdownPanel = styled.div<{ $width?: number }>`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 100;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.15);
  padding: 10px 12px;
  ${({ $width }) => $width && css`width: ${$width}px;`}
`;

/** 컬러 그리드 */
const ColorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  gap: 2px;
`;

const ColorSwatch = styled.button<{ $color: string; $active?: boolean }>`
  width: 18px;
  height: 18px;
  border-radius: 2px;
  background: ${({ $color }) => $color};
  border: 1px solid ${({ $color }) =>
    $color === '#FFFFFF' ? '#d0d0d0' : 'transparent'};
  cursor: pointer;
  transition: transform 0.1s;

  ${({ $active }) => $active && css`
    box-shadow: 0 0 0 2px ${EXCEL_GREEN};
    transform: scale(1.15);
  `}

  &:hover {
    transform: scale(1.2);
    box-shadow: 0 0 0 1px ${EXCEL_GREEN};
  }
`;

/** 커스텀 색상 선택 (input[type="color"]) */
const CustomColorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding-top: 6px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const CustomColorBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  padding: 3px 6px;
  border-radius: 4px;
  transition: background 0.12s;

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const CustomColorPreview = styled.span<{ $color: string }>`
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 1.5px solid ${({ theme }) => theme.colors.border};
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

/* ── 커스텀 컬러피커 모달 ── */
const CpSatValArea = styled.div<{ $hue: number }>`
  position: relative;
  width: 100%;
  height: 180px;
  border-radius: 8px;
  cursor: crosshair;
  user-select: none;
  background:
    linear-gradient(to bottom, transparent 0%, #000 100%),
    linear-gradient(to right, #fff 0%, hsl(${({ $hue }) => $hue}, 100%, 50%) 100%);
`;

const CpCursor = styled.div<{ $x: number; $y: number }>`
  position: absolute;
  left: ${({ $x }) => $x}%;
  top: ${({ $y }) => $y}%;
  width: 16px;
  height: 16px;
  border: 2px solid #fff;
  border-radius: 50%;
  box-shadow: 0 0 0 1px rgba(0,0,0,0.25), inset 0 0 0 1px rgba(0,0,0,0.15);
  pointer-events: none;
  transform: translate(-50%, -50%);
`;

/** 미리보기 스와치 + 스포이드 | 작은 원 + Hue 바 */
const CpMiddleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
`;

const CpPreviewSwatch = styled.div<{ $color: string }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const CpEyedropperBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  background: ${({ theme }) => theme.colors.bgSecondary};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.12s;
  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
  svg { width: 16px; height: 16px; }
`;

const CpHueSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const CpHueSmallSwatch = styled.div<{ $color: string }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const CpHueBar = styled.div`
  position: relative;
  flex: 1;
  height: 14px;
  border-radius: 7px;
  cursor: pointer;
  user-select: none;
  background: linear-gradient(to right,
    hsl(0,100%,50%),hsl(60,100%,50%),hsl(120,100%,50%),
    hsl(180,100%,50%),hsl(240,100%,50%),hsl(300,100%,50%),hsl(360,100%,50%)
  );
`;

const CpHueThumb = styled.div<{ $pos: number }>`
  position: absolute;
  top: -1px;
  left: ${({ $pos }) => $pos}%;
  width: 16px;
  height: 16px;
  border: 2px solid #fff;
  border-radius: 50%;
  box-shadow: 0 0 3px rgba(0,0,0,0.35);
  transform: translateX(-50%);
  pointer-events: none;
  background: transparent;
`;

/** 16진수 + R + G + B 가로 입력 행 */
const CpInputRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 14px;
`;

const CpInputField = styled.div<{ $flex?: number }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: ${({ $flex }) => $flex ?? 1};
  min-width: 0;
`;

const CpInputLabel = styled.span`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: 4px;
`;

const CpRgbInput = styled.input`
  width: 100%;
  text-align: center;
  font-size: 0.8125rem;
  padding: 6px 4px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.bgSecondary};
  color: ${({ theme }) => theme.colors.textPrimary};
  outline: none;
  box-sizing: border-box;
  &:focus { border-color: ${EXCEL_GREEN}; }
  /* hide spinners */
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const CpHexInputWrap = styled.div`
  width: 100%;
`;

const CpHexInput = styled.input`
  width: 100%;
  text-align: center;
  font-size: 0.8125rem;
  padding: 6px 4px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.bgSecondary};
  color: ${({ theme }) => theme.colors.textPrimary};
  outline: none;
  box-sizing: border-box;
  &:focus { border-color: ${EXCEL_GREEN}; }
`;

/** 테두리 옵션 그리드 */
const BorderGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2px;
`;

const BorderOptionBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.1s;

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

/* ── 셀 우클릭 컨텍스트 메뉴 ── */
const CELL_CTX_MENU_WIDTH = 200;
const CELL_CTX_MENU_ITEM_H = 32;

const CellContextOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 998;
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
`;

const CellContextMenuBox = styled.div<{ $x: number; $y: number; $openUp: boolean }>`
  position: fixed;
  left: ${({ $x }) => $x}px;
  top: ${({ $y, $openUp }) => $openUp ? 'auto' : `${$y}px`};
  bottom: ${({ $y, $openUp }) => $openUp ? `${window.innerHeight - $y}px` : 'auto'};
  z-index: 999;
  width: ${CELL_CTX_MENU_WIDTH}px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.12);
  padding: 4px 0;
  animation: ${fadeIn} 0.12s ease;
`;

const CellCtxItem = styled.button<{ $danger?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 14px;
  font-size: 0.75rem;
  color: ${({ $danger, theme }) => $danger ? theme.colors.error : theme.colors.textPrimary};
  transition: background 0.1s;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.bgHover};
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  svg { width: 14px; height: 14px; flex-shrink: 0; }
`;

const CellCtxDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.borderLight};
  margin: 4px 0;
`;

/* ── 드롭다운 오버레이 (클릭 시 닫기) ── */
const PickerOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 99;
`;

/* ------------------------------------------------------------------ */
/* ExcelRenderer Component                                             */
/* ------------------------------------------------------------------ */

interface ExcelRendererProps {
  file: File;
  onLoadComplete?: () => void;
  onLoadStart?: () => void;
  onError?: (error: string) => void;
}

/* ── 메모이즈드 셀 컴포넌트 (리렌더링 최적화) ── */
interface MemoCellProps {
  ri: number;
  ci: number;
  cellValue: string;
  rh: number;
  isSelected: boolean;
  isActiveCell: boolean;
  isEditing: boolean;
  cellStyle?: ParsedCellStyle;
  image?: SheetImage;
  merge?: MergeInfo;
  colWidth: number;
  editValue: string;
  inputRef: React.RefObject<HTMLInputElement>;
  onEditValueChange: (val: string) => void;
  onFinishEditing: () => void;
  onMouseDown: (ri: number, ci: number, e: React.MouseEvent) => void;
  onMouseEnter: (ri: number, ci: number) => void;
  onDoubleClick: (ri: number, ci: number) => void;
  onContextMenu: (e: React.MouseEvent, ri: number, ci: number) => void;
}

const MemoCell = React.memo<MemoCellProps>(({
  ri, ci, cellValue, rh, isSelected, isActiveCell, isEditing,
  cellStyle, image, merge, colWidth,
  editValue, inputRef, onEditValueChange, onFinishEditing,
  onMouseDown, onMouseEnter, onDoubleClick, onContextMenu,
}) => {
  const tdStyle: React.CSSProperties = { height: rh };
  const contentStyle: React.CSSProperties = {};

  if (cellStyle) {
    if (cellStyle.backgroundColor) {
      // 활성 셀이면 원래 배경색 유지, 선택 범위면 오버레이로 덮임
      tdStyle.background = isActiveCell ? cellStyle.backgroundColor
        : isSelected ? undefined
        : cellStyle.backgroundColor;
    }
    // 명시적 테두리 적용된 셀: 디폴트 회색 테두리 제거 후 적용된 것만 표시
    if (cellStyle._hasExplicitBorder) {
      tdStyle.borderTop = cellStyle.borderTop || 'none';
      tdStyle.borderBottom = cellStyle.borderBottom || 'none';
      tdStyle.borderLeft = cellStyle.borderLeft || 'none';
      tdStyle.borderRight = cellStyle.borderRight || 'none';
    } else {
      if (cellStyle.borderTop) tdStyle.borderTop = cellStyle.borderTop;
      if (cellStyle.borderBottom) tdStyle.borderBottom = cellStyle.borderBottom;
      if (cellStyle.borderLeft) tdStyle.borderLeft = cellStyle.borderLeft;
      if (cellStyle.borderRight) tdStyle.borderRight = cellStyle.borderRight;
    }

    if (cellStyle.color) contentStyle.color = cellStyle.color;
    if (cellStyle.fontWeight) contentStyle.fontWeight = cellStyle.fontWeight;
    if (cellStyle.fontStyle) contentStyle.fontStyle = cellStyle.fontStyle;
    if (cellStyle.textDecoration) contentStyle.textDecoration = cellStyle.textDecoration;
    if (cellStyle.fontSize) contentStyle.fontSize = cellStyle.fontSize;
    if (cellStyle.fontFamily) contentStyle.fontFamily = cellStyle.fontFamily;
    if (cellStyle.textAlign) contentStyle.justifyContent =
      cellStyle.textAlign === 'center' ? 'center'
      : cellStyle.textAlign === 'right' ? 'flex-end'
      : 'flex-start';
    if (cellStyle.verticalAlign) contentStyle.alignItems =
      cellStyle.verticalAlign === 'top' ? 'flex-start'
      : cellStyle.verticalAlign === 'middle' ? 'center'
      : 'flex-end';
  }

  const colSpan = merge?.isOrigin ? merge.colSpan : undefined;
  const rowSpan = merge?.isOrigin ? merge.rowSpan : undefined;

  return (
    <Cell
      $selected={isSelected}
      $active={isActiveCell}
      $editing={isEditing}
      colSpan={colSpan}
      rowSpan={rowSpan}
      style={tdStyle}
      onMouseDown={(e) => onMouseDown(ri, ci, e)}
      onMouseEnter={() => onMouseEnter(ri, ci)}
      onDoubleClick={() => onDoubleClick(ri, ci)}
      onContextMenu={(e) => onContextMenu(e, ri, ci)}
    >
      {isEditing ? (
        <CellInput
          ref={inputRef}
          value={editValue}
          onChange={(e) => onEditValueChange(e.target.value)}
          onBlur={onFinishEditing}
        />
      ) : (
        <CellContent style={contentStyle}>
          {image && (
            <CellImage
              src={image.src}
              style={{
                width: image.width ? Math.min(image.width, colWidth * (colSpan || 1)) : undefined,
                height: image.height ? Math.min(image.height, rh * (rowSpan || 1)) : undefined,
              }}
              alt=""
            />
          )}
          {(() => {
            const num = Number(cellValue);
            if (cellValue !== '' && !isNaN(num) && cellStyle) {
              const isPercent = cellStyle._numberFormat === 'percent';
              const dp = cellStyle._decimalPlaces;
              let v = isPercent ? num * 100 : num;
              if (dp !== undefined) v = parseFloat(v.toFixed(dp));
              return dp !== undefined ? v.toFixed(dp) + (isPercent ? '%' : '') : (isPercent ? v + '%' : cellValue);
            }
            return cellValue;
          })()}
        </CellContent>
      )}
    </Cell>
  );
});
MemoCell.displayName = 'MemoCell';

export const ExcelRenderer: React.FC<ExcelRendererProps> = ({
  file,
  onLoadComplete,
  onLoadStart,
  onError,
}) => {
  /* ── Toast ── */
  const addToast = useChatStore((s) => s.addToast);

  /* ── 클립보드 헬퍼 (navigator.clipboard 실패 시 execCommand fallback) ── */
  /** 내부 copy 작업 중 플래그 — 글로벌 copy 리스너가 이를 외부 복사로 오인하지 않도록 */
  const isSelfCopyRef = useRef(false);

  const writeClipboard = useCallback(async (text: string): Promise<boolean> => {
    // 1) navigator.clipboard API
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch { /* fallback */ }
    }
    // 2) execCommand fallback
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      ta.style.top = '-9999px';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      isSelfCopyRef.current = true;
      const ok = document.execCommand('copy');
      isSelfCopyRef.current = false;
      document.body.removeChild(ta);
      return ok;
    } catch {
      isSelfCopyRef.current = false;
      return false;
    }
  }, []);

  const readClipboard = useCallback(async (): Promise<string | null> => {
    // 1) navigator.clipboard API
    if (navigator.clipboard?.readText) {
      try {
        return await navigator.clipboard.readText();
      } catch { /* fallback */ }
    }
    // 브라우저에서 execCommand('paste')는 보안상 차단됨 → null 반환
    return null;
  }, []);

  /* ── 상태 ── */
  const [workbook, setWorkbook] = useState<XLSX.WorkBook | null>(null);
  const [activeSheet, setActiveSheet] = useState<string>('');
  const [data, setData] = useState<string[][]>([]);
  const [colCount, setColCount] = useState(0);

  /** 셀 스타일 맵: key = "row:col", value = ParsedCellStyle */
  const [cellStyles, setCellStyles] = useState<Map<string, ParsedCellStyle>>(new Map());
  /** 병합 맵 */
  const [mergeMap, setMergeMap] = useState<Map<string, MergeInfo>>(new Map());
  /** 시트 이미지 */
  const [sheetImages, setSheetImages] = useState<SheetImage[]>([]);
  /** 원본 ArrayBuffer (이미지 추출, 다운로드용) */
  const [fileBuffer, setFileBuffer] = useState<ArrayBuffer | null>(null);
  /** 수식 맵: key = "row:col", value = formula string */
  const [formulaMap, setFormulaMap] = useState<Map<string, string>>(new Map());
  /** ZIP에서 파싱한 border 정의 배열 (wb.Styles.Borders가 비어있는 문제 보완) */
  const [zipBorders, setZipBorders] = useState<any[] | null>(null);

  /** 열 너비 배열 (px) */
  const [colWidths, setColWidths] = useState<number[]>([]);
  /** 행 높이 배열 (px) */
  const [rowHeights, setRowHeights] = useState<number[]>([]);

  const [selection, setSelection] = useState<CellRange | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);
  /** 헤더 드래그 모드: 'row' | 'col' | null */
  const [headerDragMode, setHeaderDragMode] = useState<'row' | 'col' | null>(null);
  const headerDragStartRef = useRef<number>(-1);
  /** Shift 범위 선택 앵커 */
  const lastRowAnchorRef = useRef<number>(-1);
  const lastColAnchorRef = useRef<number>(-1);
  const [editingCell, setEditingCell] = useState<CellPosition | null>(null);
  const [editValue, setEditValue] = useState('');
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [selectedCols, setSelectedCols] = useState<Set<number>>(new Set());

  /* 시트 관리 상태 */
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [sheetContextMenu, setSheetContextMenu] = useState<{
    x: number;
    y: number;
    sheetName: string;
    openUp: boolean;
  } | null>(null);
  const [renameModal, setRenameModal] = useState<{
    open: boolean;
    sheetName: string;
    value: string;
    error: string;
  }>({ open: false, sheetName: '', value: '', error: '' });

  /* 시트 드래그 상태 */
  const [dragSheetName, setDragSheetName] = useState<string | null>(null);
  const [dragOverSheet, setDragOverSheet] = useState<{ name: string; side: 'left' | 'right' } | null>(null);

  /* 열/행 리사이즈 상태 */
  const [resizing, setResizing] = useState<{
    type: 'col' | 'row';
    index: number;
    startPos: number;
    startSize: number;
  } | null>(null);

  /* ── Virtual Scrolling 상태 ── */
  const [scrollTop, setScrollTop] = useState(0);
  const scrollTopRef = useRef(0);
  const rafIdRef = useRef(0);
  const [viewportHeight, setViewportHeight] = useState(600);

  const containerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  /* ── 채우기 핸들 (Fill Handle) ── */
  const [isFillDragging, setIsFillDragging] = useState(false);
  /** 채우기 드래그 중 마우스가 가리키는 끝 행 */
  const [fillEndRow, setFillEndRow] = useState<number | null>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);

  /** 편집 여부 추적 (다운로드 시 원본/재구축 분기용) */
  const isDirtyRef = useRef(false);
  /** 시트 이름 → 원본 ZIP 내 시트 인덱스 매핑 (리네임/리오더 대응) */
  const sheetOrigIndexRef = useRef<Map<string, number>>(new Map());

  /* ── Undo / Redo 히스토리 ── */
  const undoStackRef = useRef<HistoryEntry[]>([]);
  const redoStackRef = useRef<HistoryEntry[]>([]);
  const [undoLen, setUndoLen] = useState(0); // 리렌더 트리거용
  const [redoLen, setRedoLen] = useState(0);

  /* ── 셀 우클릭 컨텍스트 메뉴 ── */
  const [cellContextMenu, setCellContextMenu] = useState<{
    x: number; y: number; row: number; col: number; openUp: boolean;
    source?: 'cell' | 'colHeader' | 'rowHeader';
  } | null>(null);

  /* ── 툴바 드롭다운 ── */
  const [showPicker, setShowPicker] = useState<'textColor' | 'bgColor' | 'border' | null>(null);
  const [currentTextColor, setCurrentTextColor] = useState('#000000');
  const [currentBgColor, setCurrentBgColor] = useState('#FFFF00');

  /* ── 커스텀 컬러피커 모달 ── */
  const [colorPickerModal, setColorPickerModal] = useState<{
    target: 'textColor' | 'bgColor';
    initialColor: string;
  } | null>(null);
  const [cpHue, setCpHue] = useState(0);
  const [cpSat, setCpSat] = useState(1);
  const [cpVal, setCpVal] = useState(1);
  const cpSvRef = useRef<HTMLDivElement>(null);
  const cpHueRef = useRef<HTMLDivElement>(null);
  const cpDraggingSvRef = useRef(false);
  const cpDraggingHueRef = useRef(false);

  /** 내부 클립보드 (이미지·스타일·병합·수식 보존) */
  const internalClipboardRef = useRef<InternalClipboard | null>(null);

  /* ── 컬러피커 모달 핸들러 ── */
  const cpRgb = useMemo(() => hsvToRgb(cpHue, cpSat, cpVal), [cpHue, cpSat, cpVal]);
  const cpHex = useMemo(() => rgbToHex(cpRgb[0], cpRgb[1], cpRgb[2]), [cpRgb]);

  const openColorPickerModal = useCallback((target: 'textColor' | 'bgColor') => {
    const hex = target === 'textColor' ? currentTextColor : currentBgColor;
    const [r, g, b] = hexToRgb(hex);
    const [h, s, v] = rgbToHsv(r, g, b);
    setCpHue(h);
    setCpSat(s);
    setCpVal(v);
    setColorPickerModal({ target, initialColor: hex });
    setShowPicker(null);
  }, [currentTextColor, currentBgColor]);

  const handleCpSvMove = useCallback((clientX: number, clientY: number) => {
    const el = cpSvRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));
    setCpSat(x);
    setCpVal(1 - y);
  }, []);

  const handleCpHueMove = useCallback((clientX: number) => {
    const el = cpHueRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    setCpHue(x * 360);
  }, []);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (cpDraggingSvRef.current) handleCpSvMove(e.clientX, e.clientY);
      if (cpDraggingHueRef.current) handleCpHueMove(e.clientX);
    };
    const onUp = () => {
      cpDraggingSvRef.current = false;
      cpDraggingHueRef.current = false;
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [handleCpSvMove, handleCpHueMove]);

  const handleCpRgbChange = useCallback((channel: 'r' | 'g' | 'b', val: string) => {
    const num = Math.max(0, Math.min(255, parseInt(val) || 0));
    const [r, g, b] = cpRgb;
    const newR = channel === 'r' ? num : r;
    const newG = channel === 'g' ? num : g;
    const newB = channel === 'b' ? num : b;
    const [h, s, v] = rgbToHsv(newR, newG, newB);
    setCpHue(h);
    setCpSat(s);
    setCpVal(v);
  }, [cpRgb]);

  const handleCpHexChange = useCallback((hex: string) => {
    let h = hex.startsWith('#') ? hex : '#' + hex;
    if (h.length === 7) {
      const [r, g, b] = hexToRgb(h);
      const [hue, s, v] = rgbToHsv(r, g, b);
      setCpHue(hue);
      setCpSat(s);
      setCpVal(v);
    }
  }, []);

  const handleCpCancel = useCallback(() => {
    setColorPickerModal(null);
  }, []);

  /* ── 파일 로드 ── */
  useEffect(() => {
    let cancelled = false;
    onLoadStart?.();

    const reader = new FileReader();
    reader.onload = (e) => {
      if (cancelled) return;
      try {
        const arrayBuffer = e.target?.result as ArrayBuffer;
        if (!arrayBuffer) throw new Error('파일을 읽을 수 없습니다.');

        setFileBuffer(arrayBuffer);

        const wb = XLSX.read(arrayBuffer, {
          type: 'array',
          cellStyles: true,
          cellFormula: true,
          cellHTML: false,
          cellNF: true,
        });
        if (wb.SheetNames.length === 0) throw new Error('시트가 없습니다.');

        // ZIP에서 borders 파싱 (wb.Styles.Borders가 비어있는 문제 보완)
        parseBordersFromZip(arrayBuffer).then((borders) => {
          setZipBorders(borders);
        });

        setWorkbook(wb);
        setSheetNames([...wb.SheetNames]);
        setActiveSheet(wb.SheetNames[0]);

        // 원본 시트 이름 → 인덱스 매핑 저장
        const origMap = new Map<string, number>();
        wb.SheetNames.forEach((name: string, idx: number) => {
          origMap.set(name, idx);
        });
        sheetOrigIndexRef.current = origMap;
        isDirtyRef.current = false;

        onLoadComplete?.();
      } catch (err) {
        console.error('[ExcelRenderer] Parse error:', err);
        onError?.('엑셀 파일을 파싱할 수 없습니다.');
      }
    };
    reader.onerror = () => {
      if (cancelled) return;
      onError?.('파일 읽기에 실패했습니다.');
    };
    reader.readAsArrayBuffer(file);

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  /* ── 언마운트 시 메모리 정리 ── */
  useEffect(() => {
    return () => {
      // 무거운 상태들을 정리하여 GC가 수거할 수 있게 함
      setWorkbook(null);
      setFileBuffer(null);
      setZipBorders(null);
      setData([]);
      setCellStyles(new Map());
      setMergeMap(new Map());
      setFormulaMap(new Map());
      setSheetImages([]);
      setColWidths([]);
      setRowHeights([]);
      undoStackRef.current = [];
      redoStackRef.current = [];
      beforeSnapshotRef.current = null;
      internalClipboardRef.current = null;
    };
  }, []);

  /* ── 외부 복사 감지: 앱 밖에서 copy 시 내부 클립보드 초기화 ── */
  useEffect(() => {
    const handleGlobalCopy = (e: ClipboardEvent) => {
      // 우리 writeClipboard fallback (execCommand)에서 발생한 copy → 무시
      if (isSelfCopyRef.current) return;
      // copy 이벤트가 우리 Container 내부에서 발생한 경우 무시 (내부 복사)
      if (containerRef.current && containerRef.current.contains(e.target as Node)) return;
      // 외부에서 복사 발생 → 내부 클립보드 무효화
      internalClipboardRef.current = null;
    };
    window.addEventListener('copy', handleGlobalCopy, true);
    return () => window.removeEventListener('copy', handleGlobalCopy, true);
  }, []);

  /* ── 시트 변경 시 데이터, 스타일, 병합, 이미지, 수식 파싱 ── */
  useEffect(() => {
    if (!workbook || !activeSheet) return;

    const sheet = workbook.Sheets[activeSheet];
    if (!sheet) return;

    // 1. 범위 결정
    const ref = sheet['!ref'];
    let maxRow = 0;
    let maxCol = 0;
    if (ref) {
      const range = XLSX.utils.decode_range(ref);
      maxRow = range.e.r + 1;
      maxCol = range.e.c + 1;
    }
    maxCol = Math.max(maxCol, 26);
    maxRow = Math.max(maxRow, 50);

    // 2. 데이터 + 수식 파싱 (셀 단위)
    const rows: string[][] = [];
    const formulas = new Map<string, string>();

    for (let r = 0; r < maxRow; r++) {
      const row: string[] = [];
      for (let c = 0; c < maxCol; c++) {
        const addr = XLSX.utils.encode_cell({ r, c });
        const cell = sheet[addr];
        if (cell) {
          row.push(cell.w ?? (cell.v != null ? String(cell.v) : ''));
          if (cell.f) {
            formulas.set(`${r}:${c}`, cell.f);
          }
        } else {
          row.push('');
        }
      }
      rows.push(row);
    }

    // 3. 병합
    const mMap = buildMergeMap(sheet['!merges']);

    // 4. 열 너비 — Excel character width → pixel 변환
    // Excel width(문자 단위) × MDW(Calibri 11pt ≈ 7.5px) + 패딩(5px)
    const sheetCols = sheet['!cols'] || [];
    const widths: number[] = [];
    for (let c = 0; c < maxCol; c++) {
      const colInfo = sheetCols[c];
      if (colInfo && (colInfo as any).width) {
        // raw XML width 값(문자 단위) 기반 계산 — 가장 정확
        const charWidth = (colInfo as any).width as number;
        const px = Math.round(charWidth * 7.5) + 5;
        widths.push(Math.min(Math.max(px, MIN_COL_WIDTH), MAX_COL_WIDTH));
      } else if (colInfo && colInfo.wpx) {
        widths.push(Math.min(Math.max(colInfo.wpx, MIN_COL_WIDTH), MAX_COL_WIDTH));
      } else if (colInfo && colInfo.wch) {
        widths.push(Math.min(Math.max(Math.round(colInfo.wch * 8) + 5, MIN_COL_WIDTH), MAX_COL_WIDTH));
      } else {
        widths.push(DEFAULT_COL_WIDTH);
      }
    }

    // 5. 행 높이 — Excel ht(포인트) → 픽셀 변환 (96DPI: 1pt = 1.333px)
    const sheetRows = sheet['!rows'] || [];
    const heights: number[] = [];
    for (let r = 0; r < maxRow; r++) {
      const rowInfo = sheetRows[r];
      if (rowInfo && rowInfo.hpt) {
        // hpt(포인트) → 픽셀 (96DPI 기준 pt × 4/3)
        heights.push(Math.max(Math.round(rowInfo.hpt * 1.333), MIN_ROW_HEIGHT));
      } else if (rowInfo && rowInfo.hpx) {
        heights.push(Math.max(rowInfo.hpx, MIN_ROW_HEIGHT));
      } else {
        heights.push(DEFAULT_ROW_HEIGHT);
      }
    }

    setData(rows);
    setColCount(maxCol);
    setMergeMap(mMap);
    setFormulaMap(formulas);
    setColWidths(widths);
    setRowHeights(heights);
    clearSelection();

    // 6. 스타일 파싱 (하이브리드: wb.Styles + ZIP borders + sheet XML indices)
    const merges = sheet['!merges'] || [];
    const wbStyles = (workbook as any).Styles;
    const bordersArr = zipBorders || [];

    if (fileBuffer) {
      const sheetIdx = workbook.SheetNames.indexOf(activeSheet);
      parseCellStyleIndices(fileBuffer, sheetIdx).then((indexMap) => {
        const styles = new Map<string, ParsedCellStyle>();
        indexMap.forEach((styleIdx, key) => {
          const parsed = resolveStyleHybrid(styleIdx, wbStyles, bordersArr);
          if (Object.keys(parsed).length > 0) {
            styles.set(key, parsed);
          }
        });

        // 병합 셀의 가장자리 테두리 합성
        // 병합 원점 셀에 우측/하단 가장자리 셀의 테두리를 합성
        for (const m of merges) {
          const sr = m.s.r, sc = m.s.c, er = m.e.r, ec = m.e.c;
          const originKey = `${sr}:${sc}`;
          const origin = styles.get(originKey) || {};

          // 우측 테두리: 병합 영역의 우측 열 셀에서 가져오기
          if (ec > sc) {
            const topRightKey = `${sr}:${ec}`;
            const topRightStyle = styles.get(topRightKey);
            if (topRightStyle?.borderRight) origin.borderRight = topRightStyle.borderRight;
          }

          // 하단 테두리: 병합 영역의 하단 행 셀에서 가져오기
          if (er > sr) {
            const bottomLeftKey = `${er}:${sc}`;
            const bottomLeftStyle = styles.get(bottomLeftKey);
            if (bottomLeftStyle?.borderBottom) origin.borderBottom = bottomLeftStyle.borderBottom;
          }

          // 우하단 모서리 (right + bottom 둘 다 없는 경우)
          if (ec > sc && er > sr) {
            const bottomRightKey = `${er}:${ec}`;
            const bottomRightStyle = styles.get(bottomRightKey);
            if (!origin.borderRight && bottomRightStyle?.borderRight) {
              origin.borderRight = bottomRightStyle.borderRight;
            }
            if (!origin.borderBottom && bottomRightStyle?.borderBottom) {
              origin.borderBottom = bottomRightStyle.borderBottom;
            }
          }

          if (Object.keys(origin).length > 0) {
            styles.set(originKey, origin);
          }
        }

        setCellStyles(styles);
      });
    } else {
      // 폴백: cell.s 직접 파싱 (fileBuffer 없는 경우)
      const styles = new Map<string, ParsedCellStyle>();
      for (let r = 0; r < maxRow; r++) {
        for (let c = 0; c < maxCol; c++) {
          const addr = XLSX.utils.encode_cell({ r, c });
          const cell = sheet[addr];
          if (cell && cell.s != null) {
            const parsed = parseCellStyleDirect(cell.s);
            if (Object.keys(parsed).length > 0) {
              styles.set(`${r}:${c}`, parsed);
            }
          }
        }
      }
      setCellStyles(styles);
    }

    // 7. 이미지 추출 (비동기)
    if (fileBuffer) {
      const sheetIdx = workbook.SheetNames.indexOf(activeSheet);
      extractImagesFromXlsx(fileBuffer, sheetIdx).then((imgs) => {
        setSheetImages(imgs);
      });
    }
  }, [workbook, activeSheet, fileBuffer, zipBorders]);

  /* ── 선택 초기화 ── */
  const clearSelection = useCallback(() => {
    setSelection(null);
    setSelectedRows(new Set());
    setSelectedCols(new Set());
    setIsAllSelected(false);
    setEditingCell(null);
    setEditValue('');
  }, []);

  /* ── Delta 기반 Undo/Redo 히스토리 (메모리 최적화) ── */

  /** Delta 계산: before/after 비교하여 변경분만 추출 */
  const computeDelta = useCallback((
    beforeData: string[][],
    afterData: string[][],
    beforeStyles: Map<string, ParsedCellStyle>,
    afterStyles: Map<string, ParsedCellStyle>,
    beforeMerge: Map<string, MergeInfo>,
    afterMerge: Map<string, MergeInfo>,
  ): { cellDeltas: CellDelta[]; mergeDeltas: MergeDelta[] } => {
    const cellDeltas: CellDelta[] = [];
    const mergeDeltas: MergeDelta[] = [];

    // data 변경 + style 변경
    const maxRows = Math.max(beforeData.length, afterData.length);
    const maxCols = Math.max(
      beforeData[0]?.length ?? 0,
      afterData[0]?.length ?? 0,
    );
    for (let r = 0; r < maxRows; r++) {
      const br = beforeData[r];
      const ar = afterData[r];
      for (let c = 0; c < maxCols; c++) {
        const key = `${r}:${c}`;
        const oldVal = br?.[c] ?? '';
        const newVal = ar?.[c] ?? '';
        const oldStyle = beforeStyles.get(key);
        const newStyle = afterStyles.get(key);
        const valChanged = oldVal !== newVal;
        const styleChanged = oldStyle !== newStyle && JSON.stringify(oldStyle) !== JSON.stringify(newStyle);
        if (valChanged || styleChanged) {
          cellDeltas.push({
            key,
            ...(valChanged ? { oldVal, newVal } : {}),
            ...(styleChanged ? { oldStyle, newStyle } : {}),
          });
        }
      }
    }

    // mergeMap 변경
    const allMergeKeys = new Set([...beforeMerge.keys(), ...afterMerge.keys()]);
    for (const key of allMergeKeys) {
      const oldInfo = beforeMerge.get(key);
      const newInfo = afterMerge.get(key);
      if (JSON.stringify(oldInfo) !== JSON.stringify(newInfo)) {
        mergeDeltas.push({ key, oldInfo, newInfo });
      }
    }

    return { cellDeltas, mergeDeltas };
  }, []);

  /** before 상태를 임시 보관하는 ref */
  const beforeSnapshotRef = useRef<{
    data: string[][];
    cellStyles: Map<string, ParsedCellStyle>;
    mergeMap: Map<string, MergeInfo>;
    colCount: number;
    isStructural: boolean;
  } | null>(null);

  /** pushHistory: 변경 전 상태 캡처 (셀 값/스타일 변경용) */
  const pushHistory = useCallback(() => {
    // 이전에 캡처한 before가 있으면 delta 계산 후 스택에 push
    if (beforeSnapshotRef.current) {
      const bs = beforeSnapshotRef.current;
      if (bs.isStructural || bs.data.length !== data.length || bs.colCount !== colCount) {
        // 구조적 변경 (행/열 수 변경)
        const entry: StructuralHistoryEntry = {
          type: 'structural',
          before: { data: bs.data, cellStyles: bs.cellStyles, mergeMap: bs.mergeMap, colCount: bs.colCount },
          after: { data: data.map(r => [...r]), cellStyles: new Map(cellStyles), mergeMap: new Map(mergeMap), colCount },
        };
        undoStackRef.current.push(entry);
      } else {
        const { cellDeltas, mergeDeltas } = computeDelta(bs.data, data, bs.cellStyles, cellStyles, bs.mergeMap, mergeMap);
        if (cellDeltas.length > 0 || mergeDeltas.length > 0) {
          undoStackRef.current.push({ type: 'delta', cellDeltas, mergeDeltas });
        }
      }
      if (undoStackRef.current.length > MAX_HISTORY) undoStackRef.current.shift();
    }

    // 현재 상태를 before로 캡처
    beforeSnapshotRef.current = {
      data: data.map(r => [...r]),
      cellStyles: new Map(cellStyles),
      mergeMap: new Map(mergeMap),
      colCount,
      isStructural: false,
    };
    redoStackRef.current = [];
    setUndoLen(undoStackRef.current.length);
    setRedoLen(0);
  }, [data, cellStyles, mergeMap, colCount, computeDelta]);

  // before 스냅샷이 있으면 상태 변경 후 자동으로 delta를 확정
  useEffect(() => {
    if (beforeSnapshotRef.current) {
      // state가 변경된 직후 → delta 확정
      const bs = beforeSnapshotRef.current;
      // before와 현재 상태가 동일하면 (아직 변경 안됨) skip
      if (bs.data === data && bs.cellStyles === cellStyles && bs.mergeMap === mergeMap && bs.colCount === colCount) return;

      if (bs.isStructural || bs.data.length !== data.length || bs.colCount !== colCount) {
        const entry: StructuralHistoryEntry = {
          type: 'structural',
          before: { data: bs.data, cellStyles: bs.cellStyles, mergeMap: bs.mergeMap, colCount: bs.colCount },
          after: { data: data.map(r => [...r]), cellStyles: new Map(cellStyles), mergeMap: new Map(mergeMap), colCount },
        };
        undoStackRef.current.push(entry);
      } else {
        const { cellDeltas, mergeDeltas } = computeDelta(bs.data, data, bs.cellStyles, cellStyles, bs.mergeMap, mergeMap);
        if (cellDeltas.length > 0 || mergeDeltas.length > 0) {
          undoStackRef.current.push({ type: 'delta', cellDeltas, mergeDeltas });
        }
      }
      if (undoStackRef.current.length > MAX_HISTORY) undoStackRef.current.shift();
      beforeSnapshotRef.current = null;
      setUndoLen(undoStackRef.current.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, cellStyles, mergeMap, colCount]);

  /** Delta 적용 (undo: 역방향, redo: 정방향) */
  const applyDelta = useCallback((entry: DeltaHistoryEntry, reverse: boolean) => {
    setData(prev => {
      const next = prev.map(r => [...r]);
      for (const d of entry.cellDeltas) {
        const val = reverse ? d.oldVal : d.newVal;
        if (val !== undefined) {
          const [rs, cs] = d.key.split(':').map(Number);
          if (next[rs]) next[rs][cs] = val;
        }
      }
      return next;
    });
    setCellStyles(prev => {
      const next = new Map(prev);
      for (const d of entry.cellDeltas) {
        const style = reverse ? d.oldStyle : d.newStyle;
        if (style !== undefined) {
          if (style) next.set(d.key, style);
          else next.delete(d.key);
        }
      }
      return next;
    });
    setMergeMap(prev => {
      const next = new Map(prev);
      for (const d of entry.mergeDeltas) {
        const info = reverse ? d.oldInfo : d.newInfo;
        if (info) next.set(d.key, info);
        else next.delete(d.key);
      }
      return next;
    });
  }, []);

  /** Structural 스냅샷 적용 */
  const applySnapshot = useCallback((snapshot: StructuralSnapshot) => {
    setData(snapshot.data.map(r => [...r]));
    setCellStyles(new Map(snapshot.cellStyles));
    setMergeMap(new Map(snapshot.mergeMap));
    setColCount(snapshot.colCount);
  }, []);

  /** 현재 상태를 HistoryEntry로 캡처 (redo/undo 전 현재 상태 보존용) */

  const handleUndo = useCallback(() => {
    if (undoStackRef.current.length === 0) return;
    // 혹시 미확정 before 스냅샷이 있으면 확정
    beforeSnapshotRef.current = null;

    const entry = undoStackRef.current.pop()!;

    // 현재 상태를 redo에 역방향으로 저장
    if (entry.type === 'delta') {
      // 역방향 delta를 redo에 push
      redoStackRef.current.push({
        type: 'delta',
        cellDeltas: entry.cellDeltas.map(d => ({
          key: d.key,
          ...(d.oldVal !== undefined || d.newVal !== undefined ? { oldVal: d.newVal, newVal: d.oldVal } : {}),
          ...(d.oldStyle !== undefined || d.newStyle !== undefined ? { oldStyle: d.newStyle, newStyle: d.oldStyle } : {}),
        })),
        mergeDeltas: entry.mergeDeltas.map(d => ({
          key: d.key,
          oldInfo: d.newInfo,
          newInfo: d.oldInfo,
        })),
      });
      applyDelta(entry, true); // 역방향 적용
    } else {
      redoStackRef.current.push({
        type: 'structural',
        before: { data: data.map(r => [...r]), cellStyles: new Map(cellStyles), mergeMap: new Map(mergeMap), colCount },
        after: entry.before,
      });
      applySnapshot(entry.before);
    }

    setUndoLen(undoStackRef.current.length);
    setRedoLen(redoStackRef.current.length);
    isDirtyRef.current = true;
  }, [data, cellStyles, mergeMap, colCount, applyDelta, applySnapshot]);

  const handleRedo = useCallback(() => {
    if (redoStackRef.current.length === 0) return;
    beforeSnapshotRef.current = null;

    const entry = redoStackRef.current.pop()!;

    if (entry.type === 'delta') {
      // 역방향 delta를 undo에 push
      undoStackRef.current.push({
        type: 'delta',
        cellDeltas: entry.cellDeltas.map(d => ({
          key: d.key,
          ...(d.oldVal !== undefined || d.newVal !== undefined ? { oldVal: d.newVal, newVal: d.oldVal } : {}),
          ...(d.oldStyle !== undefined || d.newStyle !== undefined ? { oldStyle: d.newStyle, newStyle: d.oldStyle } : {}),
        })),
        mergeDeltas: entry.mergeDeltas.map(d => ({
          key: d.key,
          oldInfo: d.newInfo,
          newInfo: d.oldInfo,
        })),
      });
      applyDelta(entry, true); // redo 엔트리도 "역방향 delta"로 저장했으므로 reverse=true
    } else {
      undoStackRef.current.push({
        type: 'structural',
        before: { data: data.map(r => [...r]), cellStyles: new Map(cellStyles), mergeMap: new Map(mergeMap), colCount },
        after: entry.before,
      });
      applySnapshot(entry.before);
    }

    setUndoLen(undoStackRef.current.length);
    setRedoLen(redoStackRef.current.length);
    isDirtyRef.current = true;
  }, [data, cellStyles, mergeMap, colCount, applyDelta, applySnapshot]);

  /* ── 선택 범위에 스타일 적용 유틸 ── */
  const applyStyleToSelection = useCallback(
    (updater: (prev: ParsedCellStyle | undefined) => ParsedCellStyle) => {
      if (!selection) return;
      pushHistory();
      isDirtyRef.current = true;
      const n = normalizeRange(selection);
      setCellStyles(prev => {
        const next = new Map(prev);
        for (let r = n.start.row; r <= n.end.row; r++) {
          for (let c = n.start.col; c <= n.end.col; c++) {
            const key = `${r}:${c}`;
            next.set(key, updater(prev.get(key)));
          }
        }
        return next;
      });
    },
    [selection, pushHistory],
  );

  /* ── Bold / Italic / Underline 토글 ── */
  const toggleBold = useCallback(() => {
    if (!selection) return;
    const n = normalizeRange(selection);
    const key = `${n.start.row}:${n.start.col}`;
    const current = cellStyles.get(key);
    const isBold = current?.fontWeight === 'bold';
    applyStyleToSelection(prev => ({
      ...prev,
      fontWeight: isBold ? undefined : 'bold',
    }));
  }, [selection, cellStyles, applyStyleToSelection]);

  const toggleItalic = useCallback(() => {
    if (!selection) return;
    const n = normalizeRange(selection);
    const key = `${n.start.row}:${n.start.col}`;
    const current = cellStyles.get(key);
    const isItalic = current?.fontStyle === 'italic';
    applyStyleToSelection(prev => ({
      ...prev,
      fontStyle: isItalic ? undefined : 'italic',
    }));
  }, [selection, cellStyles, applyStyleToSelection]);

  const toggleUnderline = useCallback(() => {
    if (!selection) return;
    const n = normalizeRange(selection);
    const key = `${n.start.row}:${n.start.col}`;
    const current = cellStyles.get(key);
    const isUnderline = current?.textDecoration === 'underline';
    applyStyleToSelection(prev => ({
      ...prev,
      textDecoration: isUnderline ? undefined : 'underline',
    }));
  }, [selection, cellStyles, applyStyleToSelection]);

  /* ── 숫자 서식: 퍼센트, 소수점 자릿수 ── */
  const togglePercentFormat = useCallback(() => {
    if (!selection) return;
    const n = normalizeRange(selection);
    const key = `${n.start.row}:${n.start.col}`;
    const current = cellStyles.get(key);
    const isPercent = current?._numberFormat === 'percent';
    applyStyleToSelection(prev => ({
      ...prev,
      _numberFormat: isPercent ? undefined : 'percent',
    }));
  }, [selection, cellStyles, applyStyleToSelection]);

  const adjustDecimalPlaces = useCallback((delta: number) => {
    if (!selection) return;
    const n = normalizeRange(selection);
    const key = `${n.start.row}:${n.start.col}`;
    const current = cellStyles.get(key);
    const curDp = current?._decimalPlaces ?? 0;
    const newDp = Math.max(0, Math.min(10, curDp + delta));
    applyStyleToSelection(prev => ({
      ...prev,
      _decimalPlaces: newDp === 0 ? undefined : newDp,
    }));
  }, [selection, cellStyles, applyStyleToSelection]);

  /* ── 텍스트 정렬 ── */
  const setTextAlign = useCallback((align: 'left' | 'center' | 'right') => {
    if (!selection) return;
    const n = normalizeRange(selection);
    const key = `${n.start.row}:${n.start.col}`;
    const current = cellStyles.get(key);
    const isActive = current?.textAlign === align;
    applyStyleToSelection(prev => ({
      ...prev,
      textAlign: isActive ? undefined : align,
    }));
  }, [selection, cellStyles, applyStyleToSelection]);

  /* ── 폰트 사이즈 조절 ── */
  const [fontSizeInput, setFontSizeInput] = useState('11');

  /** 현재 선택 셀의 폰트 사이즈를 추적 */
  useEffect(() => {
    if (!selection) return;
    const n = normalizeRange(selection);
    const key = `${n.start.row}:${n.start.col}`;
    const style = cellStyles.get(key);
    if (style?.fontSize) {
      const num = parseFloat(style.fontSize);
      if (!isNaN(num)) {
        setFontSizeInput(String(num));
        return;
      }
    }
    setFontSizeInput('11');
  }, [selection, cellStyles]);

  const applyFontSize = useCallback((size: number) => {
    if (!selection || size < 6 || size > 72) return;
    applyStyleToSelection(prev => ({
      ...prev,
      fontSize: `${size}pt`,
    }));
    setFontSizeInput(String(size));
  }, [selection, applyStyleToSelection]);

  const handleFontSizeInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFontSizeInput(e.target.value);
  }, []);

  const handleFontSizeInputCommit = useCallback(() => {
    const num = parseFloat(fontSizeInput);
    if (!isNaN(num) && num >= 6 && num <= 72) {
      applyFontSize(num);
    } else {
      // 유효하지 않으면 현재 값 복원
      if (selection) {
        const n = normalizeRange(selection);
        const key = `${n.start.row}:${n.start.col}`;
        const style = cellStyles.get(key);
        const cur = style?.fontSize ? parseFloat(style.fontSize) : 11;
        setFontSizeInput(String(cur));
      }
    }
  }, [fontSizeInput, applyFontSize, selection, cellStyles]);

  const handleFontSizeInputKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleFontSizeInputCommit();
      (e.target as HTMLInputElement).blur();
    } else if (e.key === 'Escape') {
      (e.target as HTMLInputElement).blur();
    }
    e.stopPropagation(); // 셀 키보드 이벤트 전파 방지
  }, [handleFontSizeInputCommit]);

  /* ── 텍스트 색상 / 배경색 적용 ── */
  const applyTextColor = useCallback((color: string) => {
    setCurrentTextColor(color);
    applyStyleToSelection(prev => ({ ...prev, color }));
    setShowPicker(null);
  }, [applyStyleToSelection]);

  const applyBgColor = useCallback((color: string) => {
    setCurrentBgColor(color);
    applyStyleToSelection(prev => ({ ...prev, backgroundColor: color }));
    setShowPicker(null);
  }, [applyStyleToSelection]);

  /* ── 컬러피커 모달 확인 (applyTextColor/applyBgColor 사용) ── */
  const handleCpConfirm = useCallback(() => {
    if (!colorPickerModal) return;
    if (colorPickerModal.target === 'textColor') {
      applyTextColor(cpHex);
    } else {
      applyBgColor(cpHex);
    }
    setColorPickerModal(null);
  }, [colorPickerModal, cpHex, applyTextColor, applyBgColor]);

  /* ── 테두리 적용 ── */
  const applyBorder = useCallback((option: BorderOption) => {
    if (!selection) return;
    pushHistory();
    isDirtyRef.current = true;
    const n = normalizeRange(selection);
    const thin = '1px solid #000000';
    const thick = '2px solid #000000';

    setCellStyles(prev => {
      const next = new Map(prev);
      for (let r = n.start.row; r <= n.end.row; r++) {
        for (let c = n.start.col; c <= n.end.col; c++) {
          const key = `${r}:${c}`;
          const s = { ...(prev.get(key) || {}) } as ParsedCellStyle;
          const isTop = r === n.start.row;
          const isBottom = r === n.end.row;
          const isLeft = c === n.start.col;
          const isRight = c === n.end.col;

          // 명시적 테두리 적용 플래그 — 디폴트 회색 테두리 대신 적용한 테두리만 표시
          s._hasExplicitBorder = true;

          switch (option) {
            case 'none':
              s.borderTop = undefined; s.borderBottom = undefined;
              s.borderLeft = undefined; s.borderRight = undefined;
              break;
            case 'all':
              s.borderTop = thin; s.borderBottom = thin;
              s.borderLeft = thin; s.borderRight = thin;
              break;
            case 'outer':
              s.borderTop = isTop ? thin : undefined;
              s.borderBottom = isBottom ? thin : undefined;
              s.borderLeft = isLeft ? thin : undefined;
              s.borderRight = isRight ? thin : undefined;
              break;
            case 'thick-outer':
              s.borderTop = isTop ? thick : undefined;
              s.borderBottom = isBottom ? thick : undefined;
              s.borderLeft = isLeft ? thick : undefined;
              s.borderRight = isRight ? thick : undefined;
              break;
            case 'top':
              s.borderTop = isTop ? thin : undefined;
              s.borderBottom = undefined;
              s.borderLeft = undefined;
              s.borderRight = undefined;
              break;
            case 'bottom':
              s.borderTop = undefined;
              s.borderBottom = isBottom ? thin : undefined;
              s.borderLeft = undefined;
              s.borderRight = undefined;
              break;
            case 'left':
              s.borderTop = undefined;
              s.borderBottom = undefined;
              s.borderLeft = isLeft ? thin : undefined;
              s.borderRight = undefined;
              break;
            case 'right':
              s.borderTop = undefined;
              s.borderBottom = undefined;
              s.borderLeft = undefined;
              s.borderRight = isRight ? thin : undefined;
              break;
            case 'top-bottom':
              s.borderTop = isTop ? thin : undefined;
              s.borderBottom = isBottom ? thin : undefined;
              s.borderLeft = undefined;
              s.borderRight = undefined;
              break;
            case 'inner-h':
              s.borderTop = !isTop ? thin : undefined;
              s.borderBottom = !isBottom ? thin : undefined;
              s.borderLeft = undefined;
              s.borderRight = undefined;
              break;
            case 'inner-v':
              s.borderTop = undefined;
              s.borderBottom = undefined;
              s.borderLeft = !isLeft ? thin : undefined;
              s.borderRight = !isRight ? thin : undefined;
              break;
            case 'inner-all':
              s.borderTop = !isTop ? thin : undefined;
              s.borderBottom = !isBottom ? thin : undefined;
              s.borderLeft = !isLeft ? thin : undefined;
              s.borderRight = !isRight ? thin : undefined;
              break;
          }
          next.set(key, s);
        }
      }
      return next;
    });
    setShowPicker(null);
  }, [selection, pushHistory]);

  /* ── 셀 병합 / 해제 ── */
  const handleMergeCells = useCallback(() => {
    if (!selection) return;

    // 열 헤더 / 행 헤더 다중 선택 시 범위를 올바르게 계산
    let n = normalizeRange(selection);

    if (selectedCols.size > 1) {
      // 열 헤더 선택: 각 행마다 선택된 열들을 병합 (가장 왼쪽 열 값 유지)
      const cols = Array.from(selectedCols).sort((a, b) => a - b);
      const minC = cols[0];
      const maxC = cols[cols.length - 1];
      const colSpan = maxC - minC + 1;

      // 이미 첫 번째 행의 병합이 있으면 모든 행 해제
      const firstOriginKey = `${0}:${minC}`;
      const existingFirst = mergeMap.get(firstOriginKey);
      if (existingFirst?.isOrigin && existingFirst.colSpan === colSpan) {
        pushHistory();
        isDirtyRef.current = true;
        setMergeMap(prev => {
          const next = new Map(prev);
          for (let r = 0; r < data.length; r++) {
            for (let c = minC; c <= maxC; c++) {
              next.delete(`${r}:${c}`);
            }
          }
          return next;
        });
        if (workbook && activeSheet) {
          const sheet = workbook.Sheets[activeSheet];
          if (sheet && sheet['!merges']) {
            sheet['!merges'] = sheet['!merges'].filter(
              (m: any) => !(m.s.c === minC && m.e.c === maxC)
            );
          }
        }
        return;
      }

      // 각 행마다 병합 수행
      pushHistory();
      isDirtyRef.current = true;

      // 각 행의 왼쪽 열 값을 유지하고, 나머지 열 값은 비우기
      setData(prev => {
        const next = prev.map(r => [...r]);
        for (let r = 0; r < next.length; r++) {
          // 왼쪽 열(minC) 값 유지, 나머지 비우기
          for (let c = minC + 1; c <= maxC && c < colCount; c++) {
            next[r][c] = '';
          }
        }
        return next;
      });

      setMergeMap(prev => {
        const next = new Map(prev);
        for (let r = 0; r < data.length; r++) {
          next.set(`${r}:${minC}`, { isOrigin: true, hidden: false, rowSpan: 1, colSpan });
          for (let c = minC + 1; c <= maxC; c++) {
            next.set(`${r}:${c}`, { isOrigin: false, hidden: true, rowSpan: 1, colSpan: 1 });
          }
        }
        return next;
      });

      if (workbook && activeSheet) {
        const sheet = workbook.Sheets[activeSheet];
        if (sheet) {
          if (!sheet['!merges']) sheet['!merges'] = [];
          for (let r = 0; r < data.length; r++) {
            sheet['!merges'].push({
              s: { r, c: minC },
              e: { r, c: maxC },
            });
          }
        }
      }
      return;
    }

    if (selectedRows.size > 1) {
      // 행 헤더 선택: 각 열마다 선택된 행들을 병합 (가장 위 행 값 유지)
      const rows = Array.from(selectedRows).sort((a, b) => a - b);
      const minR = rows[0];
      const maxR = rows[rows.length - 1];
      const rowSpan = maxR - minR + 1;

      // 이미 첫 번째 열의 병합이 있으면 모든 열 해제
      const firstOriginKey = `${minR}:${0}`;
      const existingFirst = mergeMap.get(firstOriginKey);
      if (existingFirst?.isOrigin && existingFirst.rowSpan === rowSpan) {
        pushHistory();
        isDirtyRef.current = true;
        setMergeMap(prev => {
          const next = new Map(prev);
          for (let r = minR; r <= maxR; r++) {
            for (let c = 0; c < colCount; c++) {
              next.delete(`${r}:${c}`);
            }
          }
          return next;
        });
        if (workbook && activeSheet) {
          const sheet = workbook.Sheets[activeSheet];
          if (sheet && sheet['!merges']) {
            sheet['!merges'] = sheet['!merges'].filter(
              (m: any) => !(m.s.r === minR && m.e.r === maxR)
            );
          }
        }
        return;
      }

      // 각 열마다 병합 수행
      pushHistory();
      isDirtyRef.current = true;

      // 각 열의 위쪽 행 값을 유지하고, 나머지 행 값은 비우기
      setData(prev => {
        const next = prev.map(r => [...r]);
        for (let c = 0; c < colCount; c++) {
          // 위쪽 행(minR) 값 유지, 나머지 비우기
          for (let r = minR + 1; r <= maxR && r < next.length; r++) {
            next[r][c] = '';
          }
        }
        return next;
      });

      setMergeMap(prev => {
        const next = new Map(prev);
        for (let c = 0; c < colCount; c++) {
          next.set(`${minR}:${c}`, { isOrigin: true, hidden: false, rowSpan, colSpan: 1 });
          for (let r = minR + 1; r <= maxR; r++) {
            next.set(`${r}:${c}`, { isOrigin: false, hidden: true, rowSpan: 1, colSpan: 1 });
          }
        }
        return next;
      });

      if (workbook && activeSheet) {
        const sheet = workbook.Sheets[activeSheet];
        if (sheet) {
          if (!sheet['!merges']) sheet['!merges'] = [];
          for (let c = 0; c < colCount; c++) {
            sheet['!merges'].push({
              s: { r: minR, c },
              e: { r: maxR, c },
            });
          }
        }
      }
      return;
    }

    // 일반 셀 범위 선택 시 병합
    if (n.start.row === n.end.row && n.start.col === n.end.col) return;

    // 이미 같은 병합이 있으면 해제
    const originKey = `${n.start.row}:${n.start.col}`;
    const existing = mergeMap.get(originKey);
    if (existing?.isOrigin) {
      // 해제
      pushHistory();
      isDirtyRef.current = true;
      setMergeMap(prev => {
        const next = new Map(prev);
        for (let r = n.start.row; r <= n.start.row + existing.rowSpan - 1; r++) {
          for (let c = n.start.col; c <= n.start.col + existing.colSpan - 1; c++) {
            next.delete(`${r}:${c}`);
          }
        }
        return next;
      });
      // workbook에서도 제거
      if (workbook && activeSheet) {
        const sheet = workbook.Sheets[activeSheet];
        if (sheet && sheet['!merges']) {
          sheet['!merges'] = sheet['!merges'].filter(
            (m: any) => !(m.s.r === n.start.row && m.s.c === n.start.col)
          );
        }
      }
      return;
    }

    // 병합 — 왼쪽 상단 셀 값 유지, 나머지 비우기
    pushHistory();
    isDirtyRef.current = true;
    const rowSpan = n.end.row - n.start.row + 1;
    const colSpan = n.end.col - n.start.col + 1;

    setData(prev => {
      const next = prev.map(r => [...r]);
      for (let r = n.start.row; r <= n.end.row; r++) {
        for (let c = n.start.col; c <= n.end.col; c++) {
          if (r === n.start.row && c === n.start.col) continue;
          next[r][c] = '';
        }
      }
      return next;
    });

    setMergeMap(prev => {
      const next = new Map(prev);
      next.set(originKey, { isOrigin: true, hidden: false, rowSpan, colSpan });
      for (let r = n.start.row; r <= n.end.row; r++) {
        for (let c = n.start.col; c <= n.end.col; c++) {
          if (r === n.start.row && c === n.start.col) continue;
          next.set(`${r}:${c}`, { isOrigin: false, hidden: true, rowSpan: 1, colSpan: 1 });
        }
      }
      return next;
    });

    // workbook에 반영
    if (workbook && activeSheet) {
      const sheet = workbook.Sheets[activeSheet];
      if (sheet) {
        if (!sheet['!merges']) sheet['!merges'] = [];
        sheet['!merges'].push({
          s: { r: n.start.row, c: n.start.col },
          e: { r: n.end.row, c: n.end.col },
        });
      }
    }
  }, [selection, selectedCols, selectedRows, data.length, colCount, mergeMap, pushHistory, workbook, activeSheet]);

  /* ── 복사 / 붙여넣기 ── */

  /** 선택 범위의 min/max 행열 결정 */
  const getCopyRange = useCallback((): { minR: number; maxR: number; minC: number; maxC: number } | null => {
    if (isAllSelected) return { minR: 0, maxR: data.length - 1, minC: 0, maxC: colCount - 1 };
    if (selectedCols.size > 0) {
      const cols = Array.from(selectedCols).sort((a, b) => a - b);
      return { minR: 0, maxR: data.length - 1, minC: cols[0], maxC: cols[cols.length - 1] };
    }
    if (selectedRows.size > 0) {
      const rows = Array.from(selectedRows).sort((a, b) => a - b);
      return { minR: rows[0], maxR: rows[rows.length - 1], minC: 0, maxC: colCount - 1 };
    }
    if (selection) {
      const n = normalizeRange(selection);
      return { minR: n.start.row, maxR: n.end.row, minC: n.start.col, maxC: n.end.col };
    }
    return null;
  }, [isAllSelected, selectedCols, selectedRows, selection, data.length, colCount]);

  /** 선택된 셀 범위의 텍스트를 탭/줄바꿈 형태로 생성 */
  const buildCopyText = useCallback((): string | null => {
    const range = getCopyRange();
    if (!range) return null;
    const { minR, maxR, minC, maxC } = range;
    const lines: string[] = [];
    for (let r = minR; r <= maxR; r++) {
      const cols: string[] = [];
      for (let c = minC; c <= maxC; c++) {
        cols.push(data[r]?.[c] ?? '');
      }
      lines.push(cols.join('\t'));
    }
    return lines.join('\n');
  }, [getCopyRange, data]);

  /** 내부 클립보드에 셀 데이터(값+스타일+이미지+수식+병합) 저장 */
  const saveToInternalClipboard = useCallback((plainText: string) => {
    const range = getCopyRange();
    if (!range) return;
    const { minR, maxR, minC, maxC } = range;
    const rowCount = maxR - minR + 1;
    const cc = maxC - minC + 1;
    const cells: InternalClipboardCell[][] = [];

    for (let r = 0; r < rowCount; r++) {
      const row: InternalClipboardCell[] = [];
      for (let c = 0; c < cc; c++) {
        const absR = minR + r;
        const absC = minC + c;
        const key = `${absR}:${absC}`;
        const cell: InternalClipboardCell = {
          value: data[absR]?.[absC] ?? '',
        };
        const style = cellStyles.get(key);
        if (style) cell.style = { ...style };
        const img = sheetImages.find(i => i.fromRow === absR && i.fromCol === absC);
        if (img) cell.image = { ...img, fromRow: r, fromCol: c };
        const formula = formulaMap.get(key);
        if (formula) cell.formula = formula;
        const merge = mergeMap.get(key);
        if (merge) cell.merge = { ...merge };
        row.push(cell);
      }
      cells.push(row);
    }

    internalClipboardRef.current = { plainText, cells, rowCount, colCount: cc };
  }, [getCopyRange, data, cellStyles, sheetImages, formulaMap, mergeMap]);

  const handleCopy = useCallback(async () => {
    const text = buildCopyText();
    if (text === null) return;
    saveToInternalClipboard(text);
    // 시스템 클립보드에도 쓰기 시도 (실패해도 내부 클립보드는 저장됨)
    await writeClipboard(text);
    addToast({ type: 'success', title: '복사 완료', description: '선택한 셀이 클립보드에 복사되었습니다.', duration: 2000 });
    setCellContextMenu(null);
  }, [buildCopyText, saveToInternalClipboard, writeClipboard, addToast]);

  /** 브라우저 네이티브 copy 이벤트 (Ctrl+C 시 발행) */
  const handleNativeCopy = useCallback((e: React.ClipboardEvent) => {
    if (editingCell) return; // 편집 중이면 기본 동작 유지
    const text = buildCopyText();
    if (text === null) return;
    e.preventDefault();
    e.clipboardData.setData('text/plain', text);
    saveToInternalClipboard(text);
    addToast({ type: 'success', title: '복사 완료', description: '선택한 셀이 클립보드에 복사되었습니다.', duration: 2000 });
  }, [editingCell, buildCopyText, saveToInternalClipboard, addToast]);

  /** 붙여넣기 시작 위치 결정 헬퍼 */
  const getPasteOrigin = useCallback((): { startRow: number; startCol: number } | null => {
    if (selectedCols.size > 0) {
      const cols = Array.from(selectedCols).sort((a, b) => a - b);
      return { startRow: 0, startCol: cols[0] };
    }
    if (selectedRows.size > 0) {
      const rows = Array.from(selectedRows).sort((a, b) => a - b);
      return { startRow: rows[0], startCol: 0 };
    }
    if (selection) {
      const n = normalizeRange(selection);
      return { startRow: n.start.row, startCol: n.start.col };
    }
    return null;
  }, [selectedCols, selectedRows, selection]);

  /** 내부 클립보드로 붙여넣기 (스타일·병합·수식·이미지 포함) */
  const doInternalPaste = useCallback((clip: InternalClipboard) => {
    const origin = getPasteOrigin();
    if (!origin) return;
    const { startRow, startCol } = origin;

    pushHistory();
    isDirtyRef.current = true;

    // 값 붙여넣기
    setData(prev => {
      const next = prev.map(r => [...r]);
      for (let r = 0; r < clip.rowCount; r++) {
        const targetR = startRow + r;
        if (targetR >= next.length) {
          while (next.length <= targetR) next.push(Array(colCount).fill(''));
        }
        next[targetR] = [...next[targetR]];
        for (let c = 0; c < clip.colCount; c++) {
          const targetC = startCol + c;
          if (targetC < colCount) {
            next[targetR][targetC] = clip.cells[r][c].value;
          }
        }
      }
      return next;
    });

    // 스타일 붙여넣기
    setCellStyles(prev => {
      const next = new Map(prev);
      for (let r = 0; r < clip.rowCount; r++) {
        for (let c = 0; c < clip.colCount; c++) {
          const targetR = startRow + r;
          const targetC = startCol + c;
          if (targetC >= colCount) continue;
          const key = `${targetR}:${targetC}`;
          const srcCell = clip.cells[r][c];
          if (srcCell.style) {
            next.set(key, { ...srcCell.style });
          } else {
            next.delete(key);
          }
        }
      }
      return next;
    });

    // 수식 붙여넣기
    setFormulaMap(prev => {
      const next = new Map(prev);
      for (let r = 0; r < clip.rowCount; r++) {
        for (let c = 0; c < clip.colCount; c++) {
          const targetR = startRow + r;
          const targetC = startCol + c;
          if (targetC >= colCount) continue;
          const key = `${targetR}:${targetC}`;
          const srcCell = clip.cells[r][c];
          if (srcCell.formula) {
            next.set(key, srcCell.formula);
          } else {
            next.delete(key);
          }
        }
      }
      return next;
    });

    // 병합 정보 붙여넣기 — 대상 영역의 기존 병합 제거 후 새로 적용
    setMergeMap(prev => {
      const next = new Map(prev);
      // 대상 영역 기존 병합 제거
      for (let r = startRow; r < startRow + clip.rowCount; r++) {
        for (let c = startCol; c < startCol + clip.colCount && c < colCount; c++) {
          next.delete(`${r}:${c}`);
        }
      }
      // 새 병합 적용
      for (let r = 0; r < clip.rowCount; r++) {
        for (let c = 0; c < clip.colCount; c++) {
          const srcCell = clip.cells[r][c];
          if (srcCell.merge) {
            const targetR = startRow + r;
            const targetC = startCol + c;
            if (targetC >= colCount) continue;
            next.set(`${targetR}:${targetC}`, { ...srcCell.merge });
          }
        }
      }
      return next;
    });

    // 이미지 붙여넣기
    const newImages: SheetImage[] = [];
    for (let r = 0; r < clip.rowCount; r++) {
      for (let c = 0; c < clip.colCount; c++) {
        const srcCell = clip.cells[r][c];
        if (srcCell.image) {
          newImages.push({
            ...srcCell.image,
            fromRow: startRow + r,
            fromCol: startCol + c,
            toRow: srcCell.image.toRow !== undefined ? startRow + r + ((srcCell.image.toRow ?? 0) - srcCell.image.fromRow) : undefined,
            toCol: srcCell.image.toCol !== undefined ? startCol + c + ((srcCell.image.toCol ?? 0) - srcCell.image.fromCol) : undefined,
          });
        }
      }
    }
    if (newImages.length > 0) {
      setSheetImages(prev => [...prev, ...newImages]);
    }

    addToast({ type: 'success', title: '붙여넣기 완료', description: '스타일·수식·이미지를 포함하여 붙여넣기되었습니다.', duration: 2000 });
    setCellContextMenu(null);
  }, [getPasteOrigin, pushHistory, colCount, addToast]);

  /** 텍스트만 붙여넣기 (외부 소스) */
  const doPastePlainText = useCallback((text: string) => {
    const origin = getPasteOrigin();
    if (!origin) return;
    const { startRow, startCol } = origin;

    pushHistory();
    isDirtyRef.current = true;
    const rows = text.split('\n').filter((_, i, a) => !(i === a.length - 1 && a[a.length - 1] === '')).map(line => line.split('\t'));
    setData(prev => {
      const next = prev.map(r => [...r]);
      for (let r = 0; r < rows.length; r++) {
        const targetR = startRow + r;
        if (targetR >= next.length) {
          while (next.length <= targetR) next.push(Array(colCount).fill(''));
        }
        next[targetR] = [...next[targetR]];
        for (let c = 0; c < rows[r].length; c++) {
          const targetC = startCol + c;
          if (targetC < colCount) {
            next[targetR][targetC] = rows[r][c];
          }
        }
      }
      return next;
    });
    addToast({ type: 'success', title: '붙여넣기 완료', description: '클립보드 내용이 붙여넣기되었습니다.', duration: 2000 });
    setCellContextMenu(null);
  }, [getPasteOrigin, pushHistory, colCount, addToast]);

  /** 우클릭 메뉴 붙여넣기 — 내부 클립보드 우선, 없으면 시스템 클립보드 */
  const handlePaste = useCallback(async () => {
    // 1) 내부 클립보드가 있으면 시스템 클립보드 접근 없이 바로 사용
    const clip = internalClipboardRef.current;
    if (clip) {
      doInternalPaste(clip);
      return;
    }
    // 2) 내부 클립보드 없음 → 시스템 클립보드에서 읽기
    const text = await readClipboard();
    if (!text) {
      addToast({ type: 'error', title: '붙여넣기 실패', description: '클립보드에 접근할 수 없습니다. 브라우저 권한을 확인하세요.', duration: 3000 });
      setCellContextMenu(null);
      return;
    }
    doPastePlainText(text);
    setCellContextMenu(null);
  }, [doInternalPaste, readClipboard, addToast, doPastePlainText]);

  /** onPaste 이벤트 핸들러 (Ctrl+V 시 브라우저가 발행) */
  const handleNativePaste = useCallback((e: React.ClipboardEvent) => {
    if (editingCell) return; // 편집 중이면 무시 (input에서 처리)
    e.preventDefault();
    // 1) 내부 클립보드 우선
    const clip = internalClipboardRef.current;
    if (clip) {
      doInternalPaste(clip);
      return;
    }
    // 2) 시스템 클립보드 텍스트 사용
    const text = e.clipboardData.getData('text/plain');
    if (!text) return;
    doPastePlainText(text);
  }, [editingCell, doInternalPaste, doPastePlainText]);

  /* ── 행/열 삽입/삭제 ── */
  const getSelectedRange = useCallback((): { minRow: number; maxRow: number; minCol: number; maxCol: number } | null => {
    if (!selection) return null;
    const n = normalizeRange(selection);
    return { minRow: n.start.row, maxRow: n.end.row, minCol: n.start.col, maxCol: n.end.col };
  }, [selection]);

  const handleInsertRowAbove = useCallback(() => {
    const range = getSelectedRange();
    if (!range) return;
    pushHistory();
    isDirtyRef.current = true;
    const count = range.maxRow - range.minRow + 1;
    setData(prev => {
      const next = [...prev];
      const newRows = Array.from({ length: count }, () => Array(colCount).fill(''));
      next.splice(range.minRow, 0, ...newRows);
      return next;
    });
    setCellContextMenu(null);
  }, [getSelectedRange, pushHistory, colCount]);

  const handleInsertRowBelow = useCallback(() => {
    const range = getSelectedRange();
    if (!range) return;
    pushHistory();
    isDirtyRef.current = true;
    const count = range.maxRow - range.minRow + 1;
    setData(prev => {
      const next = [...prev];
      const newRows = Array.from({ length: count }, () => Array(colCount).fill(''));
      next.splice(range.maxRow + 1, 0, ...newRows);
      return next;
    });
    setCellContextMenu(null);
  }, [getSelectedRange, pushHistory, colCount]);

  const handleInsertColLeft = useCallback(() => {
    const range = getSelectedRange();
    if (!range) return;
    pushHistory();
    isDirtyRef.current = true;
    const count = range.maxCol - range.minCol + 1;
    setData(prev => prev.map(row => {
      const next = [...row];
      next.splice(range.minCol, 0, ...Array(count).fill(''));
      return next;
    }));
    setColCount(prev => prev + count);
    setCellContextMenu(null);
  }, [getSelectedRange, pushHistory]);

  const handleInsertColRight = useCallback(() => {
    const range = getSelectedRange();
    if (!range) return;
    pushHistory();
    isDirtyRef.current = true;
    const count = range.maxCol - range.minCol + 1;
    setData(prev => prev.map(row => {
      const next = [...row];
      next.splice(range.maxCol + 1, 0, ...Array(count).fill(''));
      return next;
    }));
    setColCount(prev => prev + count);
    setCellContextMenu(null);
  }, [getSelectedRange, pushHistory]);

  const handleDeleteRows = useCallback(() => {
    const range = getSelectedRange();
    if (!range) return;
    pushHistory();
    isDirtyRef.current = true;
    setData(prev => prev.filter((_, i) => i < range.minRow || i > range.maxRow));
    clearSelection();
    setCellContextMenu(null);
  }, [getSelectedRange, pushHistory, clearSelection]);

  const handleDeleteCols = useCallback(() => {
    const range = getSelectedRange();
    if (!range) return;
    pushHistory();
    isDirtyRef.current = true;
    setData(prev => prev.map(row =>
      row.filter((_, i) => i < range.minCol || i > range.maxCol)
    ));
    setColCount(prev => Math.max(1, prev - (range.maxCol - range.minCol + 1)));
    clearSelection();
    setCellContextMenu(null);
  }, [getSelectedRange, pushHistory, clearSelection]);

  /* ── 셀 우클릭 컨텍스트 메뉴 ── */
  const handleCellContextMenu = useCallback(
    (e: React.MouseEvent, row: number, col: number) => {
      e.preventDefault();
      e.stopPropagation();
      // 셀이 이미 선택 범위 안에 있으면 기존 선택 유지
      const inRange = isCellInRange(row, col, selection);
      const inRowSel = selectedRows.has(row);
      const inColSel = selectedCols.has(col);
      if (!inRange && !inRowSel && !inColSel && !isAllSelected) {
        setSelection({ start: { row, col }, end: { row, col } });
        setSelectedRows(new Set());
        setSelectedCols(new Set());
        setIsAllSelected(false);
      }
      if (!ENABLE_CELL_CONTEXT_MENU) {
        setCellContextMenu(null);
        return;
      }
      const viewportH = window.innerHeight;
      const viewportW = window.innerWidth;
      const menuH = 280;
      const openUp = e.clientY + menuH > viewportH;
      let x = e.clientX;
      if (x + CELL_CTX_MENU_WIDTH > viewportW) x = viewportW - CELL_CTX_MENU_WIDTH - 8;
      setCellContextMenu({ x, y: e.clientY, row, col, openUp, source: 'cell' });
    },
    [selection, selectedRows, selectedCols, isAllSelected],
  );

  /* ── 헤더(열/행) 우클릭 컨텍스트 메뉴 ── */
  const handleColHeaderContextMenu = useCallback(
    (e: React.MouseEvent, col: number) => {
      e.preventDefault();
      e.stopPropagation();
      // 해당 열이 선택 안 되어 있으면 선택
      if (!selectedCols.has(col)) {
        setSelectedCols(new Set([col]));
        setSelectedRows(new Set());
        setIsAllSelected(false);
        setSelection({ start: { row: 0, col }, end: { row: data.length - 1, col } });
        lastColAnchorRef.current = col;
      }
      if (!ENABLE_HEADER_CONTEXT_MENU) {
        setCellContextMenu(null);
        return;
      }
      const viewportH = window.innerHeight;
      const viewportW = window.innerWidth;
      const menuH = 200;
      const openUp = e.clientY + menuH > viewportH;
      let x = e.clientX;
      if (x + CELL_CTX_MENU_WIDTH > viewportW) x = viewportW - CELL_CTX_MENU_WIDTH - 8;
      setCellContextMenu({ x, y: e.clientY, row: 0, col, openUp, source: 'colHeader' });
    },
    [selectedCols, data.length],
  );

  const handleRowHeaderContextMenu = useCallback(
    (e: React.MouseEvent, row: number) => {
      e.preventDefault();
      e.stopPropagation();
      // 해당 행이 선택 안 되어 있으면 선택
      if (!selectedRows.has(row)) {
        setSelectedRows(new Set([row]));
        setSelectedCols(new Set());
        setIsAllSelected(false);
        setSelection({ start: { row, col: 0 }, end: { row, col: colCount - 1 } });
        lastRowAnchorRef.current = row;
      }
      if (!ENABLE_HEADER_CONTEXT_MENU) {
        setCellContextMenu(null);
        return;
      }
      const viewportH = window.innerHeight;
      const viewportW = window.innerWidth;
      const menuH = 200;
      const openUp = e.clientY + menuH > viewportH;
      let x = e.clientX;
      if (x + CELL_CTX_MENU_WIDTH > viewportW) x = viewportW - CELL_CTX_MENU_WIDTH - 8;
      setCellContextMenu({ x, y: e.clientY, row, col: 0, openUp, source: 'rowHeader' });
    },
    [selectedRows, colCount],
  );

  const closeCellContextMenu = useCallback(() => {
    setCellContextMenu(null);
  }, []);

  /* ── 셀 값 관련 ── */
  const getCellValue = useCallback(
    (row: number, col: number): string => {
      if (row >= data.length || col >= colCount) return '';
      return data[row]?.[col] ?? '';
    },
    [data, colCount],
  );

  /** 수식이 포함된 모든 셀 재계산 */
  const recalcAllFormulas = useCallback(
    (grid: string[][], fMap: Map<string, string>): string[][] => {
      const next = grid.map(r => [...r]);
      fMap.forEach((formula, key) => {
        const [rStr, cStr] = key.split(':');
        const r = parseInt(rStr, 10);
        const c = parseInt(cStr, 10);
        const result = evaluateFormula(formula, next, new Set([key]));
        if (next[r]) next[r][c] = result;
      });
      return next;
    },
    [],
  );

  const setCellValue = useCallback(
    (row: number, col: number, value: string) => {
      isDirtyRef.current = true;
      const key = `${row}:${col}`;

      if (value.startsWith('=') && value.length > 1) {
        // 수식 입력
        const formula = value.slice(1);
        setFormulaMap(prev => {
          const next = new Map(prev);
          next.set(key, formula);
          return next;
        });
        // 결과값 계산해서 data에 저장 + 의존 셀들 연쇄 재계산
        setData(prev => {
          const next = prev.map(r => [...r]);
          if (!next[row]) next[row] = Array(colCount).fill('');
          next[row] = [...next[row]];
          // 먼저 현재 셀에 수식 결과 저장
          const result = evaluateFormula(formula, next, new Set([key]));
          next[row][col] = result;
          // 다른 수식 셀들도 재계산
          const updatedMap = new Map(formulaMap);
          updatedMap.set(key, formula);
          return recalcAllFormulas(next, updatedMap);
        });
      } else {
        // 일반 값 입력 — 기존 수식 제거
        setFormulaMap(prev => {
          if (!prev.has(key)) return prev;
          const next = new Map(prev);
          next.delete(key);
          return next;
        });
        setData(prev => {
          const next = prev.map(r => [...r]);
          if (!next[row]) next[row] = Array(colCount).fill('');
          next[row] = [...next[row]];
          next[row][col] = value;
          // 이 셀을 참조하는 수식 셀들 재계산
          if (formulaMap.size > 0) {
            return recalcAllFormulas(next, formulaMap);
          }
          return next;
        });
      }
    },
    [colCount, formulaMap, recalcAllFormulas],
  );

  /* ── 셀 참조 라벨 ── */
  const activeCellRef = useMemo(() => {
    if (!selection) return '';
    const { start, end } = normalizeRange(selection);
    const startRef = `${colIndexToLetter(start.col)}${start.row + 1}`;
    if (start.row === end.row && start.col === end.col) return startRef;
    const endRef = `${colIndexToLetter(end.col)}${end.row + 1}`;
    return `${startRef}:${endRef}`;
  }, [selection]);

  const activeCellValue = useMemo(() => {
    if (!selection) return '';
    const key = `${selection.start.row}:${selection.start.col}`;
    const formula = formulaMap.get(key);
    if (formula) return `=${formula}`;
    return getCellValue(selection.start.row, selection.start.col);
  }, [selection, getCellValue, formulaMap]);

  /* ── 편집 ── */
  const startEditing = useCallback(
    (row: number, col: number) => {
      setEditingCell({ row, col });
      // 수식이 있으면 =수식 표시, 없으면 셀 값 표시
      const key = `${row}:${col}`;
      const formula = formulaMap.get(key);
      setEditValue(formula ? `=${formula}` : getCellValue(row, col));
    },
    [getCellValue, formulaMap],
  );

  const finishEditing = useCallback(() => {
    if (editingCell) {
      setCellValue(editingCell.row, editingCell.col, editValue);
      setEditingCell(null);
      setEditValue('');
    }
  }, [editingCell, editValue, setCellValue]);

  const cancelEditing = useCallback(() => {
    setEditingCell(null);
    setEditValue('');
  }, []);

  useEffect(() => {
    if (editingCell && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingCell]);

  /* ── 마우스 이벤트 ── */
  const handleCellMouseDown = useCallback(
    (row: number, col: number, e: React.MouseEvent) => {
      // 우클릭은 contextMenu에서 처리 — 기존 선택 유지
      if (e.button === 2) return;
      if (editingCell) finishEditing();
      setIsSelecting(true);
      setSelectedRows(new Set());
      setSelectedCols(new Set());
      setIsAllSelected(false);
      setSelection({ start: { row, col }, end: { row, col } });
      containerRef.current?.focus();
    },
    [editingCell, finishEditing],
  );

  const handleCellMouseEnter = useCallback(
    (row: number, col: number) => {
      if (isSelecting && selection) {
        setSelection((prev) => (prev ? { start: prev.start, end: { row, col } } : null));
      }
    },
    [isSelecting, selection],
  );

  const handleMouseUp = useCallback(() => {
    setIsSelecting(false);
    setHeaderDragMode(null);
  }, []);

  /* document 레벨 mouseup — 헤더 드래그/셀 드래그가 Container 밖에서 끝나도 확실히 해제 */
  useEffect(() => {
    if (!isSelecting && !headerDragMode && !isFillDragging) return;
    const onDocMouseUp = () => {
      if (isFillDragging) {
        // 채우기 드래그 종료 — executeFill 이후 상태 초기화
        // 실제 fill은 handleFillMouseUp에서 처리
      }
      setIsSelecting(false);
      setHeaderDragMode(null);
    };
    document.addEventListener('mouseup', onDocMouseUp);
    return () => document.removeEventListener('mouseup', onDocMouseUp);
  }, [isSelecting, headerDragMode, isFillDragging]);

  /* ── 채우기 핸들 (Fill Handle) ── */

  /** 채우기 핸들 위치 (TableArea 기준 absolute px) */
  const fillHandlePos = useMemo(() => {
    if (!selection || isAllSelected || selectedRows.size > 0 || selectedCols.size > 0 || editingCell) return null;
    const n = normalizeRange(selection);
    const endRow = n.end.row;
    const endCol = n.end.col;

    // 헤더 열 너비(HEADER_COL_WIDTH) + 열 합 + thead 높이(26)
    const theadH = 26;
    let x = HEADER_COL_WIDTH;
    for (let c = 0; c <= endCol; c++) x += colWidths[c] || DEFAULT_COL_WIDTH;
    let y = theadH;
    for (let r = 0; r <= endRow; r++) y += rowHeights[r] || DEFAULT_ROW_HEIGHT;

    return { x, y };
  }, [selection, isAllSelected, selectedRows.size, selectedCols.size, editingCell, colWidths, rowHeights]);

  /** 채우기 드래그 중 오버레이 범위 (TableArea 기준 absolute px) */
  const fillOverlayRect = useMemo(() => {
    if (!isFillDragging || fillEndRow === null || !selection) return null;
    const n = normalizeRange(selection);
    const srcEndRow = n.end.row;
    // 아래쪽으로만 채우기 (위쪽은 무시)
    if (fillEndRow <= srcEndRow) return null;

    const theadH = 26;
    let top = theadH;
    for (let r = 0; r <= srcEndRow; r++) top += rowHeights[r] || DEFAULT_ROW_HEIGHT;
    let bottom = theadH;
    for (let r = 0; r <= fillEndRow; r++) bottom += rowHeights[r] || DEFAULT_ROW_HEIGHT;
    let left = HEADER_COL_WIDTH;
    for (let c = 0; c < n.start.col; c++) left += colWidths[c] || DEFAULT_COL_WIDTH;
    let right = HEADER_COL_WIDTH;
    for (let c = 0; c <= n.end.col; c++) right += colWidths[c] || DEFAULT_COL_WIDTH;

    return { top, left, width: right - left, height: bottom - top };
  }, [isFillDragging, fillEndRow, selection, rowHeights, colWidths]);

  /** 채우기 핸들 mousedown — 드래그 시작 */
  const handleFillHandleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!selection) return;
    setIsFillDragging(true);
    setFillEndRow(normalizeRange(selection).end.row);
  }, [selection]);

  /** 채우기 드래그 중 mousemove — fillEndRow 갱신 (TableArea 기준 Y좌표 → 행 인덱스) */
  useEffect(() => {
    if (!isFillDragging) return;
    const handleMove = (e: MouseEvent) => {
      if (!tableRef.current) return;
      const rect = tableRef.current.getBoundingClientRect();
      const y = e.clientY - rect.top + tableRef.current.scrollTop;
      const theadH = 26;
      // y 좌표 → 행 인덱스
      let accum = theadH;
      for (let r = 0; r < data.length; r++) {
        accum += rowHeights[r] || DEFAULT_ROW_HEIGHT;
        if (accum >= y) {
          setFillEndRow(r);
          return;
        }
      }
      setFillEndRow(data.length - 1);
    };
    const handleUp = () => {
      setIsFillDragging(false);
    };
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    };
  }, [isFillDragging, data.length, rowHeights]);

  /** 채우기 완료 — 드래그 종료 시 값/스타일/수식/병합 채우기 */
  useEffect(() => {
    // isFillDragging이 true→false로 바뀌고 fillEndRow가 유효할 때 실행
    if (isFillDragging || fillEndRow === null || !selection) return;
    const n = normalizeRange(selection);
    const srcStartRow = n.start.row;
    const srcEndRow = n.end.row;
    const srcStartCol = n.start.col;
    const srcEndCol = n.end.col;
    const targetEndRow = fillEndRow;

    // 아래쪽으로만 채우기
    if (targetEndRow <= srcEndRow) {
      setFillEndRow(null);
      return;
    }

    pushHistory();
    isDirtyRef.current = true;

    const srcRowCount = srcEndRow - srcStartRow + 1;

    // 값 채우기
    setData(prev => {
      const next = prev.map(r => [...r]);
      for (let r = srcEndRow + 1; r <= targetEndRow; r++) {
        if (r >= next.length) {
          while (next.length <= r) next.push(Array(colCount).fill(''));
        }
        const srcRow = srcStartRow + ((r - srcEndRow - 1) % srcRowCount);
        for (let c = srcStartCol; c <= srcEndCol; c++) {
          next[r][c] = next[srcRow]?.[c] ?? '';
        }
      }
      return next;
    });

    // 스타일 채우기
    setCellStyles(prev => {
      const next = new Map(prev);
      for (let r = srcEndRow + 1; r <= targetEndRow; r++) {
        const srcRow = srcStartRow + ((r - srcEndRow - 1) % srcRowCount);
        for (let c = srcStartCol; c <= srcEndCol; c++) {
          const srcKey = `${srcRow}:${c}`;
          const tgtKey = `${r}:${c}`;
          const srcStyle = prev.get(srcKey);
          if (srcStyle) next.set(tgtKey, { ...srcStyle });
          else next.delete(tgtKey);
        }
      }
      return next;
    });

    // 수식 채우기 (셀 참조 자동 조정)
    setFormulaMap(prev => {
      const next = new Map(prev);
      for (let r = srcEndRow + 1; r <= targetEndRow; r++) {
        const srcRow = srcStartRow + ((r - srcEndRow - 1) % srcRowCount);
        const rowDelta = r - srcRow;
        for (let c = srcStartCol; c <= srcEndCol; c++) {
          const srcKey = `${srcRow}:${c}`;
          const tgtKey = `${r}:${c}`;
          const srcFormula = prev.get(srcKey);
          if (srcFormula) {
            // 셀 참조 행 번호 조정 (예: A1 → A3)
            const adjusted = srcFormula.replace(/([A-Z]+)(\d+)/g, (_match, col, row) => {
              const newRow = parseInt(row, 10) + rowDelta;
              return `${col}${newRow}`;
            });
            next.set(tgtKey, adjusted);
          } else {
            next.delete(tgtKey);
          }
        }
      }
      return next;
    });

    // 병합 정보 채우기
    setMergeMap(prev => {
      const next = new Map(prev);
      for (let r = srcEndRow + 1; r <= targetEndRow; r++) {
        const srcRow = srcStartRow + ((r - srcEndRow - 1) % srcRowCount);
        for (let c = srcStartCol; c <= srcEndCol; c++) {
          const srcKey = `${srcRow}:${c}`;
          const tgtKey = `${r}:${c}`;
          const srcMerge = prev.get(srcKey);
          if (srcMerge) next.set(tgtKey, { ...srcMerge });
          else next.delete(tgtKey);
        }
      }
      return next;
    });

    // 선택 범위 확장
    setSelection({
      start: { row: srcStartRow, col: srcStartCol },
      end: { row: targetEndRow, col: srcEndCol },
    });
    setFillEndRow(null);
  }, [isFillDragging, fillEndRow, selection, pushHistory, colCount, data.length]);

  const handleCellDoubleClick = useCallback(
    (row: number, col: number) => {
      startEditing(row, col);
    },
    [startEditing],
  );

  /* ── 행/열 헤더 마우스 이벤트 (클릭 + 드래그 + Shift 다중 선택) ── */

  /** 행 범위를 selectedRows + selection 에 반영하는 유틸 */
  const applyRowRange = useCallback(
    (minR: number, maxR: number) => {
      const rowSet = new Set<number>();
      for (let r = minR; r <= maxR; r++) rowSet.add(r);
      setSelectedRows(rowSet);
      setSelection({ start: { row: minR, col: 0 }, end: { row: maxR, col: colCount - 1 } });
    },
    [colCount],
  );

  /** 열 범위를 selectedCols + selection 에 반영하는 유틸 */
  const applyColRange = useCallback(
    (minC: number, maxC: number) => {
      const colSet = new Set<number>();
      for (let c = minC; c <= maxC; c++) colSet.add(c);
      setSelectedCols(colSet);
      setSelection({ start: { row: 0, col: minC }, end: { row: data.length - 1, col: maxC } });
    },
    [data.length],
  );

  const handleRowHeaderMouseDown = useCallback(
    (row: number, e: React.MouseEvent) => {
      // 우클릭은 컨텍스트 메뉴에서 처리 — 기존 선택 유지
      if (e.button === 2) return;
      e.preventDefault();
      if (editingCell) finishEditing();
      setSelectedCols(new Set());
      setIsAllSelected(false);
      setIsSelecting(false);

      if (e.shiftKey && lastRowAnchorRef.current >= 0) {
        // Shift+클릭: 앵커 ~ 현재 행 범위 선택
        const anchor = lastRowAnchorRef.current;
        applyRowRange(Math.min(anchor, row), Math.max(anchor, row));
      } else if (e.ctrlKey || e.metaKey) {
        // Ctrl/Cmd+클릭: 토글
        setSelectedRows((prev) => {
          const next = new Set(prev);
          if (next.has(row)) next.delete(row);
          else next.add(row);
          return next;
        });
        setSelection({ start: { row, col: 0 }, end: { row, col: colCount - 1 } });
        lastRowAnchorRef.current = row;
      } else {
        // 일반 클릭
        setSelectedRows(new Set([row]));
        setSelection({ start: { row, col: 0 }, end: { row, col: colCount - 1 } });
        lastRowAnchorRef.current = row;
      }
      setHeaderDragMode('row');
      headerDragStartRef.current = row;
      // 포커스를 Container로 유지 (키보드 단축키 동작을 위해)
      containerRef.current?.focus();
    },
    [colCount, editingCell, finishEditing, applyRowRange],
  );

  const handleRowHeaderMouseEnter = useCallback(
    (row: number) => {
      if (headerDragMode !== 'row') return;
      const startRow = headerDragStartRef.current;
      applyRowRange(Math.min(startRow, row), Math.max(startRow, row));
    },
    [headerDragMode, applyRowRange],
  );

  const handleColHeaderMouseDown = useCallback(
    (col: number, e: React.MouseEvent) => {
      // 우클릭은 컨텍스트 메뉴에서 처리 — 기존 선택 유지
      if (e.button === 2) return;
      e.preventDefault();
      if (editingCell) finishEditing();
      setSelectedRows(new Set());
      setIsAllSelected(false);
      setIsSelecting(false);

      if (e.shiftKey && lastColAnchorRef.current >= 0) {
        // Shift+클릭: 앵커 ~ 현재 열 범위 선택
        const anchor = lastColAnchorRef.current;
        applyColRange(Math.min(anchor, col), Math.max(anchor, col));
      } else if (e.ctrlKey || e.metaKey) {
        // Ctrl/Cmd+클릭: 토글
        setSelectedCols((prev) => {
          const next = new Set(prev);
          if (next.has(col)) next.delete(col);
          else next.add(col);
          return next;
        });
        setSelection({ start: { row: 0, col }, end: { row: data.length - 1, col } });
        lastColAnchorRef.current = col;
      } else {
        // 일반 클릭
        setSelectedCols(new Set([col]));
        setSelection({ start: { row: 0, col }, end: { row: data.length - 1, col } });
        lastColAnchorRef.current = col;
      }
      setHeaderDragMode('col');
      headerDragStartRef.current = col;
      // 포커스를 Container로 유지 (키보드 단축키 동작을 위해)
      containerRef.current?.focus();
    },
    [data.length, editingCell, finishEditing, applyColRange],
  );

  const handleColHeaderMouseEnter = useCallback(
    (col: number) => {
      if (headerDragMode !== 'col') return;
      const startCol = headerDragStartRef.current;
      applyColRange(Math.min(startCol, col), Math.max(startCol, col));
    },
    [headerDragMode, applyColRange],
  );

  const handleCornerClick = useCallback(() => {
    if (editingCell) finishEditing();
    const next = !isAllSelected;
    setIsAllSelected(next);
    if (next) {
      setSelection({
        start: { row: 0, col: 0 },
        end: { row: data.length - 1, col: colCount - 1 },
      });
      setSelectedRows(new Set());
      setSelectedCols(new Set());
    } else {
      clearSelection();
    }
    containerRef.current?.focus();
  }, [isAllSelected, data.length, colCount, editingCell, finishEditing, clearSelection]);

  /* ── 키보드 이벤트 ── */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (editingCell) {
        if (e.key === 'Enter') {
          e.preventDefault();
          finishEditing();
          if (editingCell.row < data.length - 1) {
            const nextRow = editingCell.row + 1;
            setSelection({
              start: { row: nextRow, col: editingCell.col },
              end: { row: nextRow, col: editingCell.col },
            });
          }
        } else if (e.key === 'Escape') {
          cancelEditing();
        } else if (e.key === 'Tab') {
          e.preventDefault();
          finishEditing();
          if (editingCell.col < colCount - 1) {
            const nextCol = editingCell.col + 1;
            setSelection({
              start: { row: editingCell.row, col: nextCol },
              end: { row: editingCell.row, col: nextCol },
            });
          }
        }
        return;
      }

      /* Ctrl/Cmd 단축키 (선택 여부와 무관) */
      if (e.ctrlKey || e.metaKey) {
        switch (e.key.toLowerCase()) {
          case 'z': e.preventDefault(); if (e.shiftKey) handleRedo(); else handleUndo(); return;
          case 'y': e.preventDefault(); handleRedo(); return;
          case 'b': e.preventDefault(); toggleBold(); return;
          case 'i': e.preventDefault(); toggleItalic(); return;
          case 'u': e.preventDefault(); toggleUnderline(); return;
          case 'c':
            if (selection || selectedCols.size > 0 || selectedRows.size > 0 || isAllSelected) {
              e.preventDefault();
              handleCopy();
            }
            return;
          case 'v':
            // Ctrl+V: 브라우저의 onPaste 이벤트로 처리 (preventDefault 하지 않음)
            return;
        }
      }

      /* Delete/Backspace — 헤더 선택도 포함 */
      if (e.key === 'Delete' || e.key === 'Backspace') {
        let minR: number | null = null, maxR: number | null = null, minC: number | null = null, maxC: number | null = null;
        if (isAllSelected) {
          minR = 0; maxR = data.length - 1; minC = 0; maxC = colCount - 1;
        } else if (selectedCols.size > 0) {
          const cols = Array.from(selectedCols).sort((a, b) => a - b);
          minR = 0; maxR = data.length - 1; minC = cols[0]; maxC = cols[cols.length - 1];
        } else if (selectedRows.size > 0) {
          const rows = Array.from(selectedRows).sort((a, b) => a - b);
          minR = rows[0]; maxR = rows[rows.length - 1]; minC = 0; maxC = colCount - 1;
        } else if (selection) {
          const norm = normalizeRange(selection);
          minR = norm.start.row; maxR = norm.end.row; minC = norm.start.col; maxC = norm.end.col;
        }
        if (minR !== null && maxR !== null && minC !== null && maxC !== null) {
          e.preventDefault();
          pushHistory();
          isDirtyRef.current = true;
          const fMinR = minR, fMaxR = maxR, fMinC = minC, fMaxC = maxC;
          setData((prev) => {
            const next = prev.map(r => [...r]);
            for (let r = fMinR; r <= fMaxR; r++) {
              if (!next[r]) continue;
              for (let c = fMinC; c <= fMaxC; c++) {
                next[r][c] = '';
              }
            }
            return next;
          });
          addToast({ type: 'info', title: '삭제 완료', description: '선택한 셀의 내용이 삭제되었습니다.', duration: 2000 });
          return;
        }
      }

      if (!selection) return;
      const { start } = selection;

      switch (e.key) {
        case 'Enter':
          e.preventDefault();
          startEditing(start.row, start.col);
          break;
        case 'Escape':
          clearSelection();
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (start.row > 0) {
            setSelection({
              start: { row: start.row - 1, col: start.col },
              end: { row: start.row - 1, col: start.col },
            });
          }
          break;
        case 'ArrowDown':
          e.preventDefault();
          if (start.row < data.length - 1) {
            setSelection({
              start: { row: start.row + 1, col: start.col },
              end: { row: start.row + 1, col: start.col },
            });
          }
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (start.col > 0) {
            setSelection({
              start: { row: start.row, col: start.col - 1 },
              end: { row: start.row, col: start.col - 1 },
            });
          }
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (start.col < colCount - 1) {
            setSelection({
              start: { row: start.row, col: start.col + 1 },
              end: { row: start.row, col: start.col + 1 },
            });
          }
          break;
        case 'Tab':
          e.preventDefault();
          if (start.col < colCount - 1) {
            setSelection({
              start: { row: start.row, col: start.col + 1 },
              end: { row: start.row, col: start.col + 1 },
            });
          }
          break;
        default:
          if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
            startEditing(start.row, start.col);
            setEditValue(e.key);
            e.preventDefault();
          }
          break;
      }
    },
    [editingCell, selection, data.length, colCount, startEditing, finishEditing, cancelEditing, clearSelection, handleUndo, handleRedo, toggleBold, toggleItalic, toggleUnderline, handleCopy, handlePaste, pushHistory, selectedCols, selectedRows, isAllSelected, addToast],
  );

  /* ── 수식 바 입력 ── */
  const handleFormulaChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!selection) return;
      const val = e.target.value;
      setCellValue(selection.start.row, selection.start.col, val);
    },
    [selection, setCellValue],
  );

  /* ── 행/열 리사이즈 ── */
  const handleColResizeStart = useCallback(
    (e: React.MouseEvent, colIdx: number) => {
      e.preventDefault();
      e.stopPropagation();
      isDirtyRef.current = true;
      setResizing({
        type: 'col',
        index: colIdx,
        startPos: e.clientX,
        startSize: colWidths[colIdx] || DEFAULT_COL_WIDTH,
      });
    },
    [colWidths],
  );

  const handleRowResizeStart = useCallback(
    (e: React.MouseEvent, rowIdx: number) => {
      e.preventDefault();
      e.stopPropagation();
      isDirtyRef.current = true;
      setResizing({
        type: 'row',
        index: rowIdx,
        startPos: e.clientY,
        startSize: rowHeights[rowIdx] || DEFAULT_ROW_HEIGHT,
      });
    },
    [rowHeights],
  );

  useEffect(() => {
    if (!resizing) return;

    const handleMove = (e: MouseEvent) => {
      if (resizing.type === 'col') {
        const delta = e.clientX - resizing.startPos;
        const newWidth = Math.max(MIN_COL_WIDTH, resizing.startSize + delta);
        setColWidths((prev) => {
          const next = [...prev];
          next[resizing.index] = newWidth;
          return next;
        });
      } else {
        const delta = e.clientY - resizing.startPos;
        const newHeight = Math.max(MIN_ROW_HEIGHT, resizing.startSize + delta);
        setRowHeights((prev) => {
          const next = [...prev];
          next[resizing.index] = newHeight;
          return next;
        });
      }
    };

    const handleUp = () => {
      setResizing(null);
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    };
  }, [resizing]);

  /* ── 현재 시트 데이터를 workbook에 반영 ── */
  const syncCurrentSheetToWorkbook = useCallback(() => {
    if (!workbook || !activeSheet) return;
    const sheet = workbook.Sheets[activeSheet];
    if (!sheet) return;

    for (let r = 0; r < data.length; r++) {
      for (let c = 0; c < colCount; c++) {
        const addr = XLSX.utils.encode_cell({ r, c });
        const newVal = data[r]?.[c] ?? '';
        const cell = sheet[addr];
        const fKey = `${r}:${c}`;
        const uiFormula = formulaMap.get(fKey);

        if (cell) {
          // ── 수식 셀 처리 ──
          if (uiFormula !== undefined) {
            // UI에 수식이 존재 → cell.f 갱신
            cell.f = uiFormula;
            // 계산값 타입 보존: 숫자면 number, 아니면 string
            const num = Number(newVal);
            if (newVal !== '' && !isNaN(num) && isFinite(num)) {
              cell.v = num;
              cell.t = 'n';
            } else {
              cell.v = newVal;
              cell.t = 's';
            }
            delete cell.w; // 라이브러리가 재계산하도록
            continue;
          }

          // 원래 수식이 있었지만 UI에서 제거된 경우
          if (cell.f && uiFormula === undefined) {
            delete cell.f;
          }

          // ── 일반 셀: 변경 여부 판별 후 업데이트 ──
          const origDisplay = cell.w ?? (cell.v != null ? String(cell.v) : '');
          if (newVal === origDisplay) continue; // 변경 없음 → 원본 유지

          // 값 변경됨 → 올바른 타입으로 설정
          const num = Number(newVal);
          if (newVal !== '' && !isNaN(num) && isFinite(num)) {
            cell.v = num;
            cell.t = 'n';
          } else {
            cell.v = newVal;
            cell.t = 's';
          }
          delete cell.w; // 라이브러리가 재계산하도록
        } else if (newVal !== '') {
          // 새 셀 생성
          const num = Number(newVal);
          const newCell: any = (!isNaN(num) && isFinite(num) && newVal !== '')
            ? { v: num, t: 'n' }
            : { v: newVal, t: 's' };
          if (uiFormula) {
            newCell.f = uiFormula;
          }
          sheet[addr] = newCell;
        }
      }
    }
  }, [workbook, activeSheet, data, colCount, formulaMap]);

  /* ── 다운로드 (원본 서식 유지) ── */
  const handleDownload = useCallback(async () => {
    if (!workbook) return;

    // ── 편집이 없으면 원본 파일 그대로 다운로드 ──
    if (!isDirtyRef.current && fileBuffer) {
      const ext = file.name.split('.').pop()?.toLowerCase();
      const mimeMap: Record<string, string> = {
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        xls: 'application/vnd.ms-excel',
        xlsm: 'application/vnd.ms-excel.sheet.macroEnabled.12',
        xlsb: 'application/vnd.ms-excel.sheet.binary.macroEnabled.12',
        csv: 'text/csv',
      };
      const mime = mimeMap[ext || 'xlsx'] || mimeMap.xlsx;
      const blob = new Blob([fileBuffer], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      return;
    }

    // ── 편집된 경우: 현재 시트 데이터 동기화 ──
    syncCurrentSheetToWorkbook();

    // ── 이전에 sync된 시트의 셀 타입 복구 ──
    // (이전 syncCurrentSheetToWorkbook이 문자열로 덮어쓴 경우 보완)
    for (const sn of workbook.SheetNames) {
      const ws = workbook.Sheets[sn];
      if (!ws || !ws['!ref']) continue;
      const range = XLSX.utils.decode_range(ws['!ref']);
      for (let r = range.s.r; r <= range.e.r; r++) {
        for (let c = range.s.c; c <= range.e.c; c++) {
          const addr = XLSX.utils.encode_cell({ r, c });
          const cell = ws[addr];
          if (!cell) continue;
          // 타입이 'n'인데 v가 문자열인 경우 → 숫자로 변환 시도
          if (typeof cell.v === 'string' && cell.v !== '') {
            const num = Number(cell.v);
            if (!isNaN(num) && isFinite(num) && (cell.t === 'n' || cell.z)) {
              cell.v = num;
              cell.t = 'n';
              delete cell.w;
            }
          }
        }
      }
    }

    // 열 너비 / 행 높이 반영
    const sheet = workbook.Sheets[activeSheet];
    if (sheet) {
      if (!sheet['!cols']) sheet['!cols'] = [];
      for (let c = 0; c < colWidths.length; c++) {
        if (!sheet['!cols'][c]) sheet['!cols'][c] = {};
        sheet['!cols'][c].wpx = colWidths[c];
      }
      if (!sheet['!rows']) sheet['!rows'] = [];
      for (let r = 0; r < rowHeights.length; r++) {
        if (!sheet['!rows'][r]) sheet['!rows'][r] = {};
        sheet['!rows'][r].hpx = rowHeights[r];
      }
    }

    // ── 모든 시트의 셀 스타일 재구축 (xlsx-js-style 호환) ──
    const wbStyles = (workbook as any).Styles;
    if (fileBuffer && wbStyles && zipBorders) {
      try {
        await rebuildAllCellStyles(
          workbook,
          fileBuffer,
          wbStyles,
          zipBorders,
          sheetOrigIndexRef.current,
        );
      } catch (err) {
        console.warn('[ExcelRenderer] Style rebuild failed, writing with partial styles:', err);
      }
    }

    // ── XLSX.write로 버퍼 생성 후 원본 이미지/드로잉 병합 ──
    const ext = file.name.split('.').pop()?.toLowerCase() || 'xlsx';
    const bookType = (
      ext === 'xlsm' ? 'xlsm' :
      ext === 'xlsb' ? 'xlsb' :
      ext === 'csv' ? 'csv' :
      ext === 'xls' ? 'biff8' as any :
      'xlsx'
    );
    const outputBuffer: ArrayBuffer = XLSX.write(workbook, { type: 'array', bookType });

    let finalBlob: Blob;
    // ZIP 기반 포맷(xlsx, xlsm)만 미디어 병합 수행
    const isZipFormat = ['xlsx', 'xlsm'].includes(ext);
    if (isZipFormat && fileBuffer) {
      try {
        finalBlob = await mergeMediaFromOriginal(
          outputBuffer,
          fileBuffer,
          sheetOrigIndexRef.current,
          workbook.SheetNames,
        );
      } catch (err) {
        console.warn('[ExcelRenderer] Media merge failed, using output as-is:', err);
        const mimeMap: Record<string, string> = {
          xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          xlsm: 'application/vnd.ms-excel.sheet.macroEnabled.12',
        };
        finalBlob = new Blob([outputBuffer], { type: mimeMap[ext] || mimeMap.xlsx });
      }
    } else {
      const mimeMap: Record<string, string> = {
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        xls: 'application/vnd.ms-excel',
        xlsm: 'application/vnd.ms-excel.sheet.macroEnabled.12',
        xlsb: 'application/vnd.ms-excel.sheet.binary.macroEnabled.12',
        csv: 'text/csv',
      };
      finalBlob = new Blob([outputBuffer], { type: mimeMap[ext] || mimeMap.xlsx });
    }

    const url = URL.createObjectURL(finalBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [workbook, fileBuffer, zipBorders, activeSheet, colWidths, rowHeights, file.name, sheetNames, syncCurrentSheetToWorkbook]);

  /* ── 시트 추가 ── */
  const handleAddSheet = useCallback(() => {
    if (!workbook) return;
    isDirtyRef.current = true;
    syncCurrentSheetToWorkbook();

    let idx = sheetNames.length + 1;
    let newName = `Sheet${idx}`;
    while (sheetNames.includes(newName)) {
      idx++;
      newName = `Sheet${idx}`;
    }

    const ws = XLSX.utils.aoa_to_sheet([]);
    workbook.Sheets[newName] = ws;
    const updatedNames = [...sheetNames, newName];
    workbook.SheetNames = updatedNames;
    setSheetNames(updatedNames);
    clearSelection();
    setActiveSheet(newName);
  }, [workbook, sheetNames, syncCurrentSheetToWorkbook, clearSelection]);

  /* ── 시트 탭 우클릭 ── */
  const handleSheetTabContextMenu = useCallback(
    (e: React.MouseEvent, sheetName: string) => {
      e.preventDefault();
      e.stopPropagation();

      const viewportH = window.innerHeight;
      const viewportW = window.innerWidth;
      const clickY = e.clientY;
      let clickX = e.clientX;

      const openUp = clickY + SHEET_CTX_MENU_HEIGHT > viewportH;
      if (clickX + SHEET_CTX_MENU_WIDTH > viewportW) {
        clickX = viewportW - SHEET_CTX_MENU_WIDTH - 8;
      }

      setSheetContextMenu({ x: clickX, y: clickY, sheetName, openUp });
    },
    [],
  );

  const closeSheetContextMenu = useCallback(() => {
    setSheetContextMenu(null);
  }, []);

  /* ── 시트 삽입 (현재 시트 뒤에) ── */
  const handleInsertSheet = useCallback(() => {
    if (!workbook || !sheetContextMenu) return;
    isDirtyRef.current = true;
    syncCurrentSheetToWorkbook();

    const targetName = sheetContextMenu.sheetName;
    const targetIdx = sheetNames.indexOf(targetName);

    let idx = sheetNames.length + 1;
    let newName = `Sheet${idx}`;
    while (sheetNames.includes(newName)) {
      idx++;
      newName = `Sheet${idx}`;
    }

    const ws = XLSX.utils.aoa_to_sheet([]);
    workbook.Sheets[newName] = ws;

    const updatedNames = [...sheetNames];
    updatedNames.splice(targetIdx + 1, 0, newName);
    workbook.SheetNames = updatedNames;
    setSheetNames(updatedNames);

    clearSelection();
    setActiveSheet(newName);
    setSheetContextMenu(null);
  }, [workbook, sheetContextMenu, sheetNames, syncCurrentSheetToWorkbook, clearSelection]);

  /* ── 시트 삭제 ── */
  const handleDeleteSheet = useCallback(() => {
    if (!workbook || !sheetContextMenu) return;
    isDirtyRef.current = true;
    const targetName = sheetContextMenu.sheetName;

    if (sheetNames.length <= 1) {
      setSheetContextMenu(null);
      return;
    }

    const targetIdx = sheetNames.indexOf(targetName);
    delete workbook.Sheets[targetName];
    const updatedNames = sheetNames.filter((n) => n !== targetName);
    workbook.SheetNames = updatedNames;
    setSheetNames(updatedNames);

    if (activeSheet === targetName) {
      const nextIdx = Math.min(targetIdx, updatedNames.length - 1);
      clearSelection();
      setActiveSheet(updatedNames[nextIdx]);
    }

    setSheetContextMenu(null);
  }, [workbook, sheetContextMenu, sheetNames, activeSheet, clearSelection]);

  /* ── 시트 이름 변경 모달 열기 ── */
  const handleOpenRenameModal = useCallback(() => {
    if (!sheetContextMenu) return;
    const name = sheetContextMenu.sheetName;
    setRenameModal({ open: true, sheetName: name, value: name, error: '' });
    setSheetContextMenu(null);
  }, [sheetContextMenu]);

  /* ── 시트 이름 변경 확정 ── */
  const handleRenameConfirm = useCallback(() => {
    if (!workbook) return;
    const { sheetName, value } = renameModal;
    const trimmed = value.trim();

    if (!trimmed) {
      setRenameModal((prev) => ({ ...prev, error: '시트 이름을 입력해주세요.' }));
      return;
    }
    if (trimmed.length > 31) {
      setRenameModal((prev) => ({ ...prev, error: '시트 이름은 31자 이하여야 합니다.' }));
      return;
    }
    if (trimmed !== sheetName && sheetNames.includes(trimmed)) {
      setRenameModal((prev) => ({ ...prev, error: '이미 존재하는 시트 이름입니다.' }));
      return;
    }

    if (trimmed !== sheetName) {
      isDirtyRef.current = true;
      workbook.Sheets[trimmed] = workbook.Sheets[sheetName];
      delete workbook.Sheets[sheetName];
      const updatedNames = sheetNames.map((n) => (n === sheetName ? trimmed : n));
      workbook.SheetNames = updatedNames;
      setSheetNames(updatedNames);

      // 원본 인덱스 매핑 갱신
      const origIdx = sheetOrigIndexRef.current.get(sheetName);
      if (origIdx !== undefined) {
        sheetOrigIndexRef.current.delete(sheetName);
        sheetOrigIndexRef.current.set(trimmed, origIdx);
      }

      if (activeSheet === sheetName) {
        setActiveSheet(trimmed);
      }
    }

    setRenameModal({ open: false, sheetName: '', value: '', error: '' });
  }, [workbook, renameModal, sheetNames, activeSheet]);

  /* ── 시트 복사 (서식 복사) ── */
  const handleCopySheet = useCallback(() => {
    if (!workbook || !sheetContextMenu) return;
    isDirtyRef.current = true;
    syncCurrentSheetToWorkbook();

    const sourceName = sheetContextMenu.sheetName;
    const sourceSheet = workbook.Sheets[sourceName];
    if (!sourceSheet) {
      setSheetContextMenu(null);
      return;
    }

    let copyIdx = 2;
    let copyName = `${sourceName} (2)`;
    while (sheetNames.includes(copyName)) {
      copyIdx++;
      copyName = `${sourceName} (${copyIdx})`;
    }

    const copiedSheet = JSON.parse(JSON.stringify(sourceSheet));
    workbook.Sheets[copyName] = copiedSheet;

    const sourceIdx = sheetNames.indexOf(sourceName);
    const updatedNames = [...sheetNames];
    updatedNames.splice(sourceIdx + 1, 0, copyName);
    workbook.SheetNames = updatedNames;
    setSheetNames(updatedNames);

    clearSelection();
    setActiveSheet(copyName);
    setSheetContextMenu(null);
  }, [workbook, sheetContextMenu, sheetNames, syncCurrentSheetToWorkbook, clearSelection]);

  /* ── 모달 닫기 ── */
  const handleCloseRenameModal = useCallback(() => {
    setRenameModal({ open: false, sheetName: '', value: '', error: '' });
  }, []);

  /* ── 모달 열릴 때 input에 포커스 ── */
  useEffect(() => {
    if (renameModal.open && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renameModal.open]);

  /* ── 시트 탭 드래그 앤 드롭 ── */
  const handleSheetDragStart = useCallback(
    (e: React.DragEvent, name: string) => {
      e.stopPropagation();
      setDragSheetName(name);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', name);
      if (e.currentTarget instanceof HTMLElement) {
        e.dataTransfer.setDragImage(e.currentTarget, e.nativeEvent.offsetX, e.nativeEvent.offsetY);
      }
    },
    [],
  );

  const handleSheetDragOver = useCallback(
    (e: React.DragEvent, name: string) => {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'move';
      if (!dragSheetName || dragSheetName === name) {
        setDragOverSheet(null);
        return;
      }
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      const midX = rect.left + rect.width / 2;
      const side = e.clientX < midX ? 'left' : 'right';
      setDragOverSheet({ name, side });
    },
    [dragSheetName],
  );

  const handleSheetDragLeave = useCallback((e: React.DragEvent) => {
    e.stopPropagation();
    setDragOverSheet(null);
  }, []);

  const handleSheetDrop = useCallback(
    (e: React.DragEvent, targetName: string) => {
      e.preventDefault();
      e.stopPropagation();
      if (!workbook || !dragSheetName || dragSheetName === targetName) {
        setDragSheetName(null);
        setDragOverSheet(null);
        return;
      }

      const fromIdx = sheetNames.indexOf(dragSheetName);
      if (fromIdx === -1) return;

      isDirtyRef.current = true;

      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      const midX = rect.left + rect.width / 2;
      const dropSide = e.clientX < midX ? 'left' : 'right';

      const without = sheetNames.filter((n) => n !== dragSheetName);
      const targetIdx = without.indexOf(targetName);
      const insertIdx = dropSide === 'left' ? targetIdx : targetIdx + 1;

      without.splice(insertIdx, 0, dragSheetName);

      workbook.SheetNames = without;
      setSheetNames(without);
      setDragSheetName(null);
      setDragOverSheet(null);
    },
    [workbook, dragSheetName, sheetNames],
  );

  const handleSheetDragEnd = useCallback((e: React.DragEvent) => {
    e.stopPropagation();
    setDragSheetName(null);
    setDragOverSheet(null);
  }, []);

  /** SheetTabs 컨테이너에서 내부 드래그 이벤트 전파 차단 */
  const handleSheetTabsDragEnter = useCallback((e: React.DragEvent) => {
    if (dragSheetName) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, [dragSheetName]);

  const handleSheetTabsDragOver = useCallback((e: React.DragEvent) => {
    if (dragSheetName) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, [dragSheetName]);

  /* ── 선택 관련 계산 ── */
  const isRowSelected = useCallback(
    (row: number) => {
      if (isAllSelected) return true;
      if (selectedRows.has(row)) return true;
      if (selection) {
        const n = normalizeRange(selection);
        return (
          row >= n.start.row &&
          row <= n.end.row &&
          n.start.col === 0 &&
          n.end.col === colCount - 1
        );
      }
      return false;
    },
    [isAllSelected, selectedRows, selection, colCount],
  );

  const isColSelected = useCallback(
    (col: number) => {
      if (isAllSelected) return true;
      if (selectedCols.has(col)) return true;
      if (selection) {
        const n = normalizeRange(selection);
        return (
          col >= n.start.col &&
          col <= n.end.col &&
          n.start.row === 0 &&
          n.end.row === data.length - 1
        );
      }
      return false;
    },
    [isAllSelected, selectedCols, selection, data.length],
  );

  /* ── 선택 영역 통계 ── */
  const selectionStats = useMemo(() => {
    if (!selection) return null;
    const n = normalizeRange(selection);
    if (n.start.row === n.end.row && n.start.col === n.end.col) return null;

    let sum = 0;
    let count = 0;
    let numCount = 0;

    for (let r = n.start.row; r <= n.end.row; r++) {
      for (let c = n.start.col; c <= n.end.col; c++) {
        const val = getCellValue(r, c);
        if (val !== '') {
          count++;
          const num = Number(val);
          if (!isNaN(num)) {
            sum += num;
            numCount++;
          }
        }
      }
    }

    if (numCount === 0) return { count };
    const avg = sum / numCount;
    return { count, sum: Math.round(sum * 1000) / 1000, avg: Math.round(avg * 1000) / 1000 };
  }, [selection, getCellValue]);

  /* ── 이미지 맵 (fromRow:fromCol → image) ── */
  const imageMap = useMemo(() => {
    const map = new Map<string, SheetImage>();
    for (const img of sheetImages) {
      map.set(`${img.fromRow}:${img.fromCol}`, img);
    }
    return map;
  }, [sheetImages]);

  /* ── colgroup 메모 ── */
  const colGroupCols = useMemo(() => {
    const cols: React.ReactNode[] = [
      <col key="header" style={{ width: HEADER_COL_WIDTH, minWidth: HEADER_COL_WIDTH }} />,
    ];
    for (let c = 0; c < colCount; c++) {
      const w = colWidths[c] || DEFAULT_COL_WIDTH;
      cols.push(<col key={c} style={{ width: w, minWidth: MIN_COL_WIDTH }} />);
    }
    return cols;
  }, [colCount, colWidths]);

  /* ── 테이블 총 너비 (table-layout: fixed 에서 필수) ── */
  const tableWidth = useMemo(() => {
    let total = HEADER_COL_WIDTH;
    for (let c = 0; c < colCount; c++) {
      total += colWidths[c] || DEFAULT_COL_WIDTH;
    }
    return total;
  }, [colCount, colWidths]);

  /* ── Virtual Scrolling: 행 높이 prefix sum ── */
  const rowPrefixSums = useMemo(() => {
    const sums = [0];
    for (let i = 0; i < data.length; i++) {
      sums.push(sums[i] + (rowHeights[i] || DEFAULT_ROW_HEIGHT));
    }
    return sums;
  }, [data.length, rowHeights]);

  /* ── Virtual Scrolling: 병합 셀 행 범위 사전 계산 (startRow 오름차순 정렬) ── */
  const mergeRowRanges = useMemo(() => {
    const ranges: { start: number; end: number }[] = [];
    for (const [key, info] of mergeMap.entries()) {
      if (!info.isOrigin || info.rowSpan <= 1) continue;
      const r = parseInt(key.split(':')[0], 10);
      ranges.push({ start: r, end: r + info.rowSpan - 1 });
    }
    ranges.sort((a, b) => a.start - b.start);
    return ranges;
  }, [mergeMap]);

  /* ── Virtual Scrolling: 가시 행 범위 계산 ── */
  const visibleRowRange = useMemo(() => {
    if (data.length === 0) return { startRow: 0, endRow: -1 };

    const headerH = 26; // thead 높이
    const adjustedScrollTop = Math.max(0, scrollTop - headerH);

    let startRow = Math.max(
      0,
      binarySearchRow(rowPrefixSums, adjustedScrollTop) - 1 - OVERSCAN_ROWS,
    );
    let endRow = Math.min(
      data.length - 1,
      binarySearchRow(rowPrefixSums, adjustedScrollTop + viewportHeight) + OVERSCAN_ROWS,
    );

    // 사전 계산된 병합 행 범위로 확장 (정렬됨 → 범위 벗어나면 early break)
    for (const mr of mergeRowRanges) {
      if (mr.start > endRow) break; // 이후 병합은 가시 범위 아래
      if (mr.start < startRow && mr.end >= startRow) startRow = mr.start;
      if (mr.start <= endRow && mr.end > endRow) endRow = mr.end;
    }

    return {
      startRow: Math.max(0, startRow),
      endRow: Math.min(data.length - 1, endRow),
    };
  }, [scrollTop, viewportHeight, rowPrefixSums, data.length, mergeRowRanges]);

  const topSpacerHeight = rowPrefixSums[visibleRowRange.startRow] || 0;
  const bottomSpacerHeight = Math.max(
    0,
    (rowPrefixSums[data.length] || 0) - (rowPrefixSums[visibleRowRange.endRow + 1] || 0),
  );

  /* ── Virtual Scrolling: rAF 스로틀링 스크롤 핸들러 ── */
  const handleTableScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    scrollTopRef.current = e.currentTarget.scrollTop;
    if (!rafIdRef.current) {
      rafIdRef.current = requestAnimationFrame(() => {
        setScrollTop(scrollTopRef.current);
        rafIdRef.current = 0;
      });
    }
  }, []);

  /* rAF 클린업 */
  useEffect(() => {
    return () => {
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
    };
  }, []);

  /* ── Virtual Scrolling: 뷰포트 크기 관찰 ── */
  useEffect(() => {
    const el = tableRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setViewportHeight(entry.contentRect.height);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  /* ── Virtual Scrolling: 선택 셀 자동 스크롤 ── */
  useEffect(() => {
    if (!selection || !tableRef.current) return;
    const { start } = selection;
    if (start.row < 0 || start.row >= rowPrefixSums.length - 1) return;

    const cellTop = rowPrefixSums[start.row] || 0;
    const cellBottom = rowPrefixSums[start.row + 1] || cellTop;
    const el = tableRef.current;
    const currentScrollTop = el.scrollTop;
    const headerH = 26;
    const visibleTop = currentScrollTop + headerH;
    const visibleBottom = currentScrollTop + el.clientHeight;

    if (cellTop < visibleTop) {
      el.scrollTop = Math.max(0, cellTop - headerH);
    } else if (cellBottom > visibleBottom) {
      el.scrollTop = cellBottom - el.clientHeight;
    }
  }, [selection, rowPrefixSums]);

  /* ── 렌더링 ── */
  if (!workbook) return null;

  if (data.length === 0) {
    return (
      <Container>
        <EmptyState>시트에 데이터가 없습니다.</EmptyState>
      </Container>
    );
  }

  return (
    <Container
      ref={containerRef}
      onMouseUp={handleMouseUp}
      onKeyDown={handleKeyDown}
      onCopy={handleNativeCopy}
      onPaste={handleNativePaste}
      tabIndex={0}
    >
      {/* 툴바 — 기능 유지, UI만 숨김 */}
      <Toolbar style={{ display: 'none' }}>
        {/* Undo / Redo */}
        <ToolbarGroup>
          <Tooltip label="실행 취소" position="bottom" align="start">
            <ToolbarBtn onClick={handleUndo} disabled={undoLen === 0}>
              <Undo2 />
            </ToolbarBtn>
          </Tooltip>
          <Tooltip label="다시 실행" position="bottom">
            <ToolbarBtn onClick={handleRedo} disabled={redoLen === 0}>
              <Redo2 />
            </ToolbarBtn>
          </Tooltip>
        </ToolbarGroup>

        {/* Bold / Italic / Underline */}
        <ToolbarGroup>
          <Tooltip label="굵게" position="bottom">
            <ToolbarBtn
              onClick={toggleBold}
              disabled={!selection}
              $active={!!selection && cellStyles.get(`${normalizeRange(selection).start.row}:${normalizeRange(selection).start.col}`)?.fontWeight === 'bold'}
            >
              <Bold />
            </ToolbarBtn>
          </Tooltip>
          <Tooltip label="기울임" position="bottom">
            <ToolbarBtn
              onClick={toggleItalic}
              disabled={!selection}
              $active={!!selection && cellStyles.get(`${normalizeRange(selection).start.row}:${normalizeRange(selection).start.col}`)?.fontStyle === 'italic'}
            >
              <Italic />
            </ToolbarBtn>
          </Tooltip>
          <Tooltip label="밑줄" position="bottom">
            <ToolbarBtn
              onClick={toggleUnderline}
              disabled={!selection}
              $active={!!selection && cellStyles.get(`${normalizeRange(selection).start.row}:${normalizeRange(selection).start.col}`)?.textDecoration === 'underline'}
            >
              <Underline />
            </ToolbarBtn>
          </Tooltip>
        </ToolbarGroup>

        {/* 숫자 서식: 퍼센트 / 소수점 */}
        <ToolbarGroup>
          <Tooltip label="퍼센트 형식" position="bottom">
            <ToolbarBtn
              onClick={togglePercentFormat}
              disabled={!selection}
              $active={!!selection && cellStyles.get(`${normalizeRange(selection).start.row}:${normalizeRange(selection).start.col}`)?._numberFormat === 'percent'}
            >
              <Percent />
            </ToolbarBtn>
          </Tooltip>
          <Tooltip label="소수점 이하 자릿수 감소" position="bottom">
            <ToolbarBtn
              onClick={() => adjustDecimalPlaces(-1)}
              disabled={!selection}
            >
              <DecimalsArrowLeft />
            </ToolbarBtn>
          </Tooltip>
          <Tooltip label="소수점 이하 자릿수 증가" position="bottom">
            <ToolbarBtn
              onClick={() => adjustDecimalPlaces(1)}
              disabled={!selection}
            >
              <DecimalsArrowRight />
            </ToolbarBtn>
          </Tooltip>
        </ToolbarGroup>

        {/* 텍스트 정렬 */}
        <ToolbarGroup>
          <Tooltip label="왼쪽 정렬" position="bottom">
            <ToolbarBtn
              onClick={() => setTextAlign('left')}
              disabled={!selection}
              $active={!!selection && cellStyles.get(`${normalizeRange(selection).start.row}:${normalizeRange(selection).start.col}`)?.textAlign === 'left'}
            >
              <AlignLeft />
            </ToolbarBtn>
          </Tooltip>
          <Tooltip label="가운데 정렬" position="bottom">
            <ToolbarBtn
              onClick={() => setTextAlign('center')}
              disabled={!selection}
              $active={!!selection && cellStyles.get(`${normalizeRange(selection).start.row}:${normalizeRange(selection).start.col}`)?.textAlign === 'center'}
            >
              <AlignCenter />
            </ToolbarBtn>
          </Tooltip>
          <Tooltip label="오른쪽 정렬" position="bottom">
            <ToolbarBtn
              onClick={() => setTextAlign('right')}
              disabled={!selection}
              $active={!!selection && cellStyles.get(`${normalizeRange(selection).start.row}:${normalizeRange(selection).start.col}`)?.textAlign === 'right'}
            >
              <AlignRight />
            </ToolbarBtn>
          </Tooltip>
        </ToolbarGroup>

        {/* 폰트 사이즈 */}
        <ToolbarGroup>
          <Tooltip label="글꼴 크기 줄이기" position="bottom">
            <ToolbarBtn
              onClick={() => { const cur = parseFloat(fontSizeInput) || 11; applyFontSize(Math.max(6, cur - 1)); }}
              disabled={!selection}
            >
              <Minus />
            </ToolbarBtn>
          </Tooltip>
          <FontSizeInput
            type="text"
            value={fontSizeInput}
            onChange={handleFontSizeInputChange}
            onBlur={handleFontSizeInputCommit}
            onKeyDown={handleFontSizeInputKeyDown}
            onMouseDown={e => e.stopPropagation()}
            disabled={!selection}
          />
          <Tooltip label="글꼴 크기 늘리기" position="bottom">
            <ToolbarBtn
              onClick={() => { const cur = parseFloat(fontSizeInput) || 11; applyFontSize(Math.min(72, cur + 1)); }}
              disabled={!selection}
            >
              <Plus />
            </ToolbarBtn>
          </Tooltip>
        </ToolbarGroup>

        {/* Text Color / Bg Color / Borders */}
        <ToolbarGroup>
          {/* 텍스트 색상 — Google Sheets 스타일 "A" 아이콘 */}
          <ToolbarDropdownWrap>
            <Tooltip label="글꼴 색상" position="bottom">
              <SplitBtnIcon
                as="button"
                onClick={() => selection && applyTextColor(currentTextColor)}
                style={{ position: 'relative', cursor: selection ? 'pointer' : 'not-allowed', opacity: selection ? 1 : 0.3 }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <text x="12" y="17" textAnchor="middle" fontFamily="Arial, sans-serif" fontSize="16" fontWeight="bold" fill="currentColor">A</text>
                </svg>
                <ColorIndicator $color={currentTextColor} />
              </SplitBtnIcon>
            </Tooltip>
            <SplitBtnArrow
              as="button"
              onClick={() => setShowPicker(prev => prev === 'textColor' ? null : 'textColor')}
              style={{ cursor: 'pointer' }}
            >
              <ChevronDown />
            </SplitBtnArrow>
            {showPicker === 'textColor' && (
              <DropdownPanel $width={222}>
                <div style={{ fontSize: '0.6875rem', fontWeight: 600, marginBottom: 6, color: 'inherit' }}>글꼴 색상</div>
                <ColorGrid>
                  {COLOR_PALETTE.flat().map(c => (
                    <ColorSwatch key={c} $color={c} $active={currentTextColor === c} onClick={() => applyTextColor(c)} title={c} />
                  ))}
                </ColorGrid>
                <CustomColorRow>
                  <CustomColorBtn onClick={() => openColorPickerModal('textColor')}>
                    <CustomColorPreview $color={currentTextColor} />
                    맞춤 색상
                  </CustomColorBtn>
                </CustomColorRow>
              </DropdownPanel>
            )}
          </ToolbarDropdownWrap>

          {/* 배경 색상 — Google Sheets 스타일 페인트 버킷 아이콘 */}
          <ToolbarDropdownWrap>
            <Tooltip label="셀 배경색" position="bottom">
              <SplitBtnIcon
                as="button"
                onClick={() => selection && applyBgColor(currentBgColor)}
                style={{ position: 'relative', cursor: selection ? 'pointer' : 'not-allowed', opacity: selection ? 1 : 0.3 }}
              >
                <PaintBucket />
                <ColorIndicator $color={currentBgColor} />
              </SplitBtnIcon>
            </Tooltip>
            <SplitBtnArrow
              as="button"
              onClick={() => setShowPicker(prev => prev === 'bgColor' ? null : 'bgColor')}
              style={{ cursor: 'pointer' }}
            >
              <ChevronDown />
            </SplitBtnArrow>
            {showPicker === 'bgColor' && (
              <DropdownPanel $width={222}>
                <div style={{ fontSize: '0.6875rem', fontWeight: 600, marginBottom: 6, color: 'inherit' }}>셀 배경색</div>
                <ColorGrid>
                  {COLOR_PALETTE.flat().map(c => (
                    <ColorSwatch key={c} $color={c} $active={currentBgColor === c} onClick={() => applyBgColor(c)} title={c} />
                  ))}
                </ColorGrid>
                <CustomColorRow>
                  <CustomColorBtn onClick={() => openColorPickerModal('bgColor')}>
                    <CustomColorPreview $color={currentBgColor} />
                    맞춤 색상
                  </CustomColorBtn>
                </CustomColorRow>
              </DropdownPanel>
            )}
          </ToolbarDropdownWrap>

          {/* 셀 테두리 */}
          <ToolbarDropdownWrap>
            <Tooltip label="셀 테두리" position="bottom">
              <SplitBtnIcon
                as="button"
                onClick={() => setShowPicker(prev => prev === 'border' ? null : 'border')}
                style={{ cursor: 'pointer' }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="1" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="12" y1="3" x2="12" y2="21" />
                </svg>
              </SplitBtnIcon>
            </Tooltip>
            <SplitBtnArrow
              as="button"
              onClick={() => setShowPicker(prev => prev === 'border' ? null : 'border')}
              style={{ cursor: 'pointer' }}
            >
              <ChevronDown />
            </SplitBtnArrow>
            {showPicker === 'border' && (
              <DropdownPanel $width={160}>
                <div style={{ fontSize: '0.6875rem', fontWeight: 600, marginBottom: 6, color: 'inherit' }}>셀 테두리</div>
                <BorderGrid>
                  {BORDER_OPTIONS.flat().map(opt => (
                    <Tooltip key={opt.key} label={opt.label}>
                      <BorderOptionBtn onClick={() => applyBorder(opt.key)}>
                        <BorderIcon type={opt.key} />
                      </BorderOptionBtn>
                    </Tooltip>
                  ))}
                </BorderGrid>
              </DropdownPanel>
            )}
          </ToolbarDropdownWrap>
        </ToolbarGroup>

        {/* 셀 병합 */}
        <ToolbarGroup>
          <Tooltip label="셀 병합/해제" position="bottom">
            <ToolbarBtn onClick={handleMergeCells} disabled={!selection}>
              <Merge />
            </ToolbarBtn>
          </Tooltip>
        </ToolbarGroup>

        {/* 다운로드 */}
        <ToolbarGroup>
          <Tooltip label="다운로드" position="bottom">
            <ToolbarBtn onClick={handleDownload}>
              <Download />
            </ToolbarBtn>
          </Tooltip>
        </ToolbarGroup>
      </Toolbar>

      {/* 피커 오버레이 (클릭 시 닫기) */}
      {showPicker && <PickerOverlay onClick={() => setShowPicker(null)} />}

      {/* 수식 입력 바 */}
      <FormulaBar>
        <CellRefLabel>{activeCellRef || '—'}</CellRefLabel>
        <FormulaInput
          value={
            editingCell
              ? editValue
              : activeCellValue
          }
          onChange={
            editingCell
              ? (e) => setEditValue(e.target.value)
              : handleFormulaChange
          }
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (editingCell) {
                finishEditing();
              }
            } else if (e.key === 'Escape') {
              if (editingCell) cancelEditing();
            }
          }}
          onFocus={() => {
            // 수식 바 클릭 시 편집 모드 진입
            if (selection && !editingCell) {
              startEditing(selection.start.row, selection.start.col);
            }
          }}
          placeholder="셀을 선택하세요"
          readOnly={!selection && !editingCell}
        />
      </FormulaBar>

      {/* 테이블 (Virtual Scrolling 적용) */}
      <TableArea ref={tableRef} onScroll={handleTableScroll}>
        <Table style={{ width: tableWidth, minWidth: tableWidth }}>
          <colgroup>{colGroupCols}</colgroup>
          <Thead>
            <tr>
              <CornerCell
                $selected={isAllSelected}
                onClick={handleCornerClick}
                title="전체 선택"
              />
              {Array.from({ length: colCount }, (_, ci) => (
                <ColHeader
                  key={ci}
                  $selected={isColSelected(ci)}
                  onMouseDown={(e) => handleColHeaderMouseDown(ci, e)}
                  onMouseEnter={() => handleColHeaderMouseEnter(ci)}
                  onContextMenu={(e) => handleColHeaderContextMenu(e, ci)}
                  title={colIndexToLetter(ci)}
                  style={{ position: 'sticky', top: 0 }}
                >
                  {colIndexToLetter(ci)}
                  <ColResizeHandle
                    onMouseDown={(e) => handleColResizeStart(e, ci)}
                  />
                </ColHeader>
              ))}
            </tr>
          </Thead>
          <tbody>
            {/* Virtual Scrolling: 상단 스페이서 (열 구조 유지) */}
            {topSpacerHeight > 0 && (
              <tr key="vs-top" aria-hidden="true" style={{ height: topSpacerHeight }}>
                <td style={{ padding: 0, border: 'none' }} />
                {Array.from({ length: colCount }, (_, i) => (
                  <td key={i} style={{ padding: 0, border: 'none' }} />
                ))}
              </tr>
            )}
            {/* Virtual Scrolling: 가시 영역 행만 렌더링 */}
            {Array.from(
              { length: Math.max(0, visibleRowRange.endRow - visibleRowRange.startRow + 1) },
              (_, idx) => {
                const ri = visibleRowRange.startRow + idx;
                const row = data[ri];
                if (!row) return null;
                const rh = rowHeights[ri] || DEFAULT_ROW_HEIGHT;
                return (
                  <tr key={ri} style={{ height: rh }}>
                    <RowHeader
                      $selected={isRowSelected(ri)}
                      onMouseDown={(e) => handleRowHeaderMouseDown(ri, e)}
                      onMouseEnter={() => handleRowHeaderMouseEnter(ri)}
                      onContextMenu={(e) => handleRowHeaderContextMenu(e, ri)}
                      style={{ position: 'sticky', left: 0, height: rh }}
                    >
                      {ri + 1}
                      <RowResizeHandle
                        onMouseDown={(e) => handleRowResizeStart(e, ri)}
                      />
                    </RowHeader>
                    {row.map((cellValue, ci) => {
                      if (ci >= colCount) return null;

                      const mergeKey = `${ri}:${ci}`;
                      const merge = mergeMap.get(mergeKey);
                      if (merge?.hidden) return null;

                      const isEditing =
                        editingCell?.row === ri && editingCell?.col === ci;
                      const isSelected =
                        isAllSelected ||
                        selectedRows.has(ri) ||
                        selectedCols.has(ci) ||
                        isCellInRange(ri, ci, selection);
                      const isActiveCell =
                        selection?.start.row === ri && selection?.start.col === ci;

                      return (
                        <MemoCell
                          key={ci}
                          ri={ri}
                          ci={ci}
                          cellValue={cellValue}
                          rh={rh}
                          isSelected={isSelected}
                          isActiveCell={!!isActiveCell}
                          isEditing={isEditing}
                          cellStyle={cellStyles.get(mergeKey)}
                          image={imageMap.get(mergeKey)}
                          merge={merge}
                          colWidth={colWidths[ci] || DEFAULT_COL_WIDTH}
                          editValue={editValue}
                          inputRef={inputRef}
                          onEditValueChange={setEditValue}
                          onFinishEditing={finishEditing}
                          onMouseDown={handleCellMouseDown}
                          onMouseEnter={handleCellMouseEnter}
                          onDoubleClick={handleCellDoubleClick}
                          onContextMenu={handleCellContextMenu}
                        />
                      );
                    })}
                  </tr>
                );
              },
            )}
            {/* Virtual Scrolling: 하단 스페이서 (열 구조 유지) */}
            {bottomSpacerHeight > 0 && (
              <tr key="vs-bottom" aria-hidden="true" style={{ height: bottomSpacerHeight }}>
                <td style={{ padding: 0, border: 'none' }} />
                {Array.from({ length: colCount }, (_, i) => (
                  <td key={i} style={{ padding: 0, border: 'none' }} />
                ))}
              </tr>
            )}
          </tbody>
        </Table>

        {/* 채우기 핸들 — 선택 범위 우하단 */}
        {fillHandlePos && !isFillDragging && (
          <FillHandle
            style={{
              left: fillHandlePos.x - 4,
              top: fillHandlePos.y - 4,
            }}
            onMouseDown={handleFillHandleMouseDown}
          />
        )}

        {/* 채우기 드래그 중 오버레이 */}
        {fillOverlayRect && (
          <FillOverlay
            style={{
              left: fillOverlayRect.left,
              top: fillOverlayRect.top,
              width: fillOverlayRect.width,
              height: fillOverlayRect.height,
            }}
          />
        )}
      </TableArea>

      {/* 시트 탭 */}
      {sheetNames.length > 0 && (
        <SheetTabs
          onDragEnter={handleSheetTabsDragEnter}
          onDragOver={handleSheetTabsDragOver}
        >
          {sheetNames.map((name) => (
            <SheetTab
              key={name}
              $active={name === activeSheet}
              $dragging={dragSheetName === name}
              $dragOver={
                dragOverSheet?.name === name ? dragOverSheet.side : null
              }
              draggable
              onClick={() => {
                if (name !== activeSheet) {
                  syncCurrentSheetToWorkbook();
                  clearSelection();
                  setActiveSheet(name);
                }
              }}
              onContextMenu={(e) => handleSheetTabContextMenu(e, name)}
              onDragStart={(e) => handleSheetDragStart(e, name)}
              onDragOver={(e) => handleSheetDragOver(e, name)}
              onDragLeave={handleSheetDragLeave}
              onDrop={(e) => handleSheetDrop(e, name)}
              onDragEnd={handleSheetDragEnd}
            >
              {name}
            </SheetTab>
          ))}
          <Tooltip label="시트 추가">
            <AddSheetBtn onClick={handleAddSheet}>
              <Plus size={14} />
            </AddSheetBtn>
          </Tooltip>
        </SheetTabs>
      )}

      {/* 셀 우클릭 컨텍스트 메뉴 */}
      {cellContextMenu && (
        <>
          <CellContextOverlay onClick={closeCellContextMenu} onContextMenu={(e) => { e.preventDefault(); closeCellContextMenu(); }} />
          <CellContextMenuBox
            $x={cellContextMenu.x}
            $y={cellContextMenu.y}
            $openUp={cellContextMenu.openUp}
            onMouseDown={(e) => e.preventDefault()}
          >
            <CellCtxItem onClick={handleCopy}>
              <Copy /> 복사
            </CellCtxItem>
            <CellCtxItem onClick={handlePaste}>
              <Clipboard /> 붙여넣기
            </CellCtxItem>
            <CellCtxDivider />
            {/* 열 헤더 우클릭: 행 삽입/삭제 숨김 */}
            {cellContextMenu.source !== 'colHeader' && (
              <>
                <CellCtxItem onClick={handleInsertRowAbove}>
                  <Plus /> 위에 행 삽입
                </CellCtxItem>
                <CellCtxItem onClick={handleInsertRowBelow}>
                  <Plus /> 아래에 행 삽입
                </CellCtxItem>
              </>
            )}
            {/* 행 헤더 우클릭: 열 삽입/삭제 숨김 */}
            {cellContextMenu.source !== 'rowHeader' && (
              <>
                <CellCtxItem onClick={handleInsertColLeft}>
                  <Plus /> 왼쪽에 열 삽입
                </CellCtxItem>
                <CellCtxItem onClick={handleInsertColRight}>
                  <Plus /> 오른쪽에 열 삽입
                </CellCtxItem>
              </>
            )}
            <CellCtxDivider />
            {cellContextMenu.source !== 'colHeader' && (
              <CellCtxItem $danger onClick={handleDeleteRows}>
                <Trash2 /> 행 삭제
              </CellCtxItem>
            )}
            {cellContextMenu.source !== 'rowHeader' && (
              <CellCtxItem $danger onClick={handleDeleteCols}>
                <Trash2 /> 열 삭제
              </CellCtxItem>
            )}
          </CellContextMenuBox>
        </>
      )}

      {/* 시트 컨텍스트 메뉴 */}
      {sheetContextMenu && (
        <>
          <SheetContextOverlay onClick={closeSheetContextMenu} />
          <SheetContextMenu $x={sheetContextMenu.x} $y={sheetContextMenu.y} $openUp={sheetContextMenu.openUp}>
            <SheetCtxItem onClick={handleInsertSheet}>
              <FilePlus2 />
              시트 삽입
            </SheetCtxItem>
            <SheetCtxItem onClick={handleCopySheet}>
              <Copy />
              시트 복사
            </SheetCtxItem>
            <SheetCtxDivider />
            <SheetCtxItem onClick={handleOpenRenameModal}>
              <Pencil />
              이름 변경
            </SheetCtxItem>
            <SheetCtxDivider />
            <SheetCtxItem
              $danger
              onClick={handleDeleteSheet}
              disabled={sheetNames.length <= 1}
            >
              <FileX2 />
              시트 삭제
            </SheetCtxItem>
          </SheetContextMenu>
        </>
      )}

      {/* 시트 이름 변경 모달 */}
      {renameModal.open && (
        <ModalOverlay onClick={handleCloseRenameModal}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>시트 이름 변경</ModalTitle>
              <ModalCloseBtn onClick={handleCloseRenameModal}>
                <X />
              </ModalCloseBtn>
            </ModalHeader>
            <ModalBody>
              <ModalLabel htmlFor="sheet-rename-input">새 시트 이름</ModalLabel>
              <ModalInput
                id="sheet-rename-input"
                ref={renameInputRef}
                value={renameModal.value}
                onChange={(e) =>
                  setRenameModal((prev) => ({ ...prev, value: e.target.value, error: '' }))
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleRenameConfirm();
                  } else if (e.key === 'Escape') {
                    handleCloseRenameModal();
                  }
                }}
                placeholder="시트 이름을 입력하세요"
                maxLength={31}
              />
              {renameModal.error && <ModalError>{renameModal.error}</ModalError>}
            </ModalBody>
            <ModalFooter>
              <ModalBtn onClick={handleCloseRenameModal}>취소</ModalBtn>
              <ModalBtn $primary onClick={handleRenameConfirm}>
                변경
              </ModalBtn>
            </ModalFooter>
          </ModalBox>
        </ModalOverlay>
      )}

      {/* 커스텀 컬러피커 모달 */}
      {colorPickerModal && (
        <ModalOverlay onClick={handleCpCancel}>
          <ModalBox onClick={(e) => e.stopPropagation()} style={{ width: 360 }}>
            <ModalHeader>
              <ModalTitle>
                {colorPickerModal.target === 'textColor' ? '글꼴 색상' : '셀 배경색'}
              </ModalTitle>
              <ModalCloseBtn onClick={handleCpCancel}><X /></ModalCloseBtn>
            </ModalHeader>
            <ModalBody style={{ padding: '16px 20px' }}>
              {/* SV 영역 */}
              <CpSatValArea
                ref={cpSvRef}
                $hue={cpHue}
                onMouseDown={(e) => {
                  cpDraggingSvRef.current = true;
                  handleCpSvMove(e.clientX, e.clientY);
                }}
              >
                <CpCursor $x={cpSat * 100} $y={(1 - cpVal) * 100} />
              </CpSatValArea>

              {/* 미리보기 + 스포이드 | 작은 원 + Hue 바 */}
              <CpMiddleRow>
                <CpPreviewSwatch $color={cpHex} />
                <CpEyedropperBtn title="스포이드">
                  <Pipette />
                </CpEyedropperBtn>
                <CpHueSection>
                  <CpHueSmallSwatch $color={cpHex} />
                  <CpHueBar
                    ref={cpHueRef}
                    onMouseDown={(e) => {
                      cpDraggingHueRef.current = true;
                      handleCpHueMove(e.clientX);
                    }}
                  >
                    <CpHueThumb $pos={(cpHue / 360) * 100} />
                  </CpHueBar>
                </CpHueSection>
              </CpMiddleRow>

              {/* 16진수 + R + G + B 가로 배치 */}
              <CpInputRow>
                <CpInputField $flex={1.4}>
                  <CpInputLabel>16진수</CpInputLabel>
                  <CpHexInputWrap>
                    <CpHexInput
                      value={cpHex}
                      onChange={(e) => handleCpHexChange(e.target.value.replace('#', ''))}
                      maxLength={7}
                    />
                  </CpHexInputWrap>
                </CpInputField>
                <CpInputField>
                  <CpInputLabel>R</CpInputLabel>
                  <CpRgbInput
                    type="number"
                    min={0}
                    max={255}
                    value={cpRgb[0]}
                    onChange={(e) => handleCpRgbChange('r', e.target.value)}
                  />
                </CpInputField>
                <CpInputField>
                  <CpInputLabel>G</CpInputLabel>
                  <CpRgbInput
                    type="number"
                    min={0}
                    max={255}
                    value={cpRgb[1]}
                    onChange={(e) => handleCpRgbChange('g', e.target.value)}
                  />
                </CpInputField>
                <CpInputField>
                  <CpInputLabel>B</CpInputLabel>
                  <CpRgbInput
                    type="number"
                    min={0}
                    max={255}
                    value={cpRgb[2]}
                    onChange={(e) => handleCpRgbChange('b', e.target.value)}
                  />
                </CpInputField>
              </CpInputRow>
            </ModalBody>
            <ModalFooter>
              <ModalBtn onClick={handleCpCancel}>취소</ModalBtn>
              <ModalBtn $primary onClick={handleCpConfirm}>확인</ModalBtn>
            </ModalFooter>
          </ModalBox>
        </ModalOverlay>
      )}

      {/* 상태 바 */}
      <StatusBar>
        <StatusGroup>
          <span>
            {data.length} 행 × {colCount} 열
          </span>
          {activeSheet && <span>시트: {activeSheet}</span>}
        </StatusGroup>
        <StatusGroup>
          {selectionStats && (
            <>
              <span>개수: {selectionStats.count}</span>
              {selectionStats.sum !== undefined && (
                <>
                  <span>합계: {selectionStats.sum}</span>
                  <span>평균: {selectionStats.avg}</span>
                </>
              )}
            </>
          )}
        </StatusGroup>
      </StatusBar>
    </Container>
  );
};
